import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from './api';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { useAuth } from './useAuth';
import ExcelJS from 'exceljs';
import { getAircraftModex } from './aircraftModex';
import { getAircraftTmsName, getTmsGroups } from './groupPolicy';
import pma273Logo from './assets/pma273-logo.png';
import navairLogo from './assets/navair-logo.png';

const DSR_COVER = {
    idiqContract: 'N61340-19-D-1006',
    taskOrder: 'N00421-24-F-0543',
    cdrlNumber: 'A001',
    contractorName: 'Amentum Services, Inc.',
    contractorEmail: 'aaron.shepard@amentum.com',
    contractName: 'T-44C/T-54A METS Aircraft CLS',
    distribution: 'METS APML, CNATRA N43, CNATRA DET, CNATRA N422',
};

const SITE_COVER_INFO = {
    NASCC: { location: 'NAS Corpus Christi', address: 'NAS Corpus Christi Hangar 58' }
};

const getCoverInfoForSite = (siteCode) => {
    const mapped = siteCode ? SITE_COVER_INFO[String(siteCode).toUpperCase()] : null;
    if (mapped) return mapped;
    return {
        location: siteCode || 'NAS Corpus Christi',
        address: siteCode || 'NAS Corpus Christi Hangar 58',
    };
};

const loadPngBuffer = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to load image: ${url}`);
    }
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
};

const addDsrCoverPage = async (workbook, { siteLocation, contractorAddress }) => {
    const coverSheet = workbook.addWorksheet('Cover Page');
    const medium = { style: 'medium', color: { argb: 'FF000000' } };
    const grayFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFBFBFBF' },
    };

    coverSheet.views = [{ showGridLines: false }];
    coverSheet.pageSetup.orientation = 'portrait';
    coverSheet.pageSetup.fitToPage = true;
    coverSheet.pageSetup.fitToWidth = 1;
    coverSheet.pageSetup.fitToHeight = 1;
    coverSheet.pageSetup.paperSize = 1;
    coverSheet.pageSetup.printArea = 'A1:D25';
    coverSheet.columns = [
        { width: 13 },
        { width: 23.57 },
        { width: 46.57 },
        { width: 20.86 },
    ];

    coverSheet.mergeCells('B1:D1');
    coverSheet.mergeCells('B2:D2');
    coverSheet.mergeCells('B20:D20');
    coverSheet.mergeCells('B21:D21');
    coverSheet.mergeCells('B22:D22');
    coverSheet.mergeCells('B23:D23');
    coverSheet.mergeCells('B24:D24');
    coverSheet.mergeCells('B25:D25');

    coverSheet.getRow(1).height = 26.25;
    coverSheet.getRow(2).height = 26.25;
    coverSheet.getRow(10).height = 49;
    coverSheet.getRow(20).height = 23.25;
    coverSheet.getRow(21).height = 50;
    coverSheet.getRow(23).height = 16;
    coverSheet.getRow(25).height = 56.25;

    const titleFont = { name: 'Calibri', size: 20, bold: true, color: { argb: 'FF000000' } };
    const labelFont = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF000000' } };
    const valueFont = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF000000' } };
    const sectionLabelFont = { name: 'Arial Narrow', size: 8, bold: false, color: { argb: 'FF000000' } };
    const statementFont = { name: 'Times New Roman', size: 10, bold: true, italic: true, color: { argb: 'FF000000' } };

    coverSheet.getCell('B1').value = 'Multi Engine Training System (METS)';
    coverSheet.getCell('B1').font = titleFont;
    coverSheet.getCell('B1').alignment = { horizontal: 'center', vertical: 'middle' };

    coverSheet.getCell('B2').value = ' T-44C/T-54A Aircraft Contractor Logistics Services (CLS)';
    coverSheet.getCell('B2').font = titleFont;
    coverSheet.getCell('B2').alignment = { horizontal: 'center', vertical: 'middle' };

    const fieldRows = [
        { row: 11, label: 'Contractor Name:', value: DSR_COVER.contractorName },
        { row: 12, label: 'Contractor Address:', value: contractorAddress },
        { row: 13, label: 'Contractor Email Address:', value: DSR_COVER.contractorEmail, email: true },
        { row: 14, label: 'Contract Number:', value: `${DSR_COVER.idiqContract}/Task Order ${DSR_COVER.taskOrder}` },
        { row: 15, label: 'Name of Contract:', value: `${DSR_COVER.contractName} ` },
        { row: 16, label: 'CDRL Number:', value: DSR_COVER.cdrlNumber },
        { row: 17, label: 'CDRL Title:', value: 'Daily Aircraft Status Report' },
        { row: 18, label: 'Site Location:', value: siteLocation },
        { row: 19, label: 'Distribution:', value: DSR_COVER.distribution },
    ];

    fieldRows.forEach(({ row, label, value, email }) => {
        const labelCell = coverSheet.getCell(row, 2);
        const valueCell = coverSheet.getCell(row, 3);
        const barCell = coverSheet.getCell(row, 4);

        labelCell.value = label;
        labelCell.font = labelFont;
        labelCell.alignment = { vertical: 'center', wrapText: true };
        labelCell.border = { top: medium, left: medium, bottom: medium, right: medium };

        if (email) {
            valueCell.value = { text: value, hyperlink: `mailto:${value}` };
            valueCell.font = { name: 'Calibri', size: 11, underline: true, color: { argb: 'FF0563C1' } };
        } else {
            valueCell.value = value;
            valueCell.font = valueFont;
        }
        valueCell.alignment = { vertical: 'center', wrapText: true };
        valueCell.border = { top: medium, bottom: medium, right: medium };

        barCell.fill = grayFill;
        barCell.border = {
            top: medium,
            left: medium,
            right: medium,
            bottom: medium,
        };
    });

    const boxedSection = (labelRow, valueRow, label, value) => {
        const labelCell = coverSheet.getCell(labelRow, 2);
        labelCell.value = label;
        labelCell.font = sectionLabelFont;
        labelCell.alignment = { vertical: 'center', wrapText: true };
        labelCell.border = { top: medium, left: medium, right: medium };

        const valueCell = coverSheet.getCell(valueRow, 2);
        valueCell.value = value;
        valueCell.font = statementFont;
        valueCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        valueCell.border = { left: medium, right: medium, bottom: medium };
    };

    boxedSection(
        20,
        21,
        'Distribution Statement:',
        'DISTRIBUTION STATEMENT C:  Distribution authorized to U.S. Government Agencies and their Contractors; Administrative or Operational use; 27 July 2023;  Other requests for this document shall be referred to Commanding Officer, Navy Undergraduate Flight Training Systems Program Office, PMA-273 22145 Arnold Circle, Bldg. 404, Patuxent River MD 20670.'
    );
    boxedSection(
        22,
        23,
        'Classification',
        'UNCLASSIFIED \u2013 FOR OFFICIAL USE ONLY INFORMATION'
    );
    boxedSection(
        24,
        25,
        'Destruction Notice',
        'DESTRUCTION NOTICE \u2013 Destroy by any method that will prevent disclosure of contents or reconstruction of the document.'
    );

    try {
        const [pmaBuf, navairBuf] = await Promise.all([
            loadPngBuffer(pma273Logo),
            loadPngBuffer(navairLogo),
        ]);
        const pmaId = workbook.addImage({ buffer: pmaBuf, extension: 'png' });
        const navairId = workbook.addImage({ buffer: navairBuf, extension: 'png' });
        coverSheet.addImage(pmaId, {
            tl: { col: 1.05, row: 2.2 },
            ext: { width: 150, height: 164 },
        });
        coverSheet.addImage(navairId, {
            tl: { col: 3.55, row: 2.2 },
            ext: { width: 155, height: 165 },
        });
    } catch (logoErr) {
        console.warn('Cover page logos could not be added:', logoErr);
    }

    return coverSheet;
};

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2a2a2a' : '#f8f9fa',
        activeTab: isDark ? '#6cb2f7' : '#007bff',
        inactiveTab: isDark ? '#444' : '#e9ecef',
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1600px', margin: '0 auto', fontFamily: 'sans-serif' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0, color: colors.text },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.text,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.text,
        },  
        card: { 
            backgroundColor: colors.cardBg, 
            border: `1px solid ${colors.border}`, 
            borderRadius: '8px', 
            padding: '20px', 
            boxShadow: `0 2px 5px ${colors.shadow}`, 
            marginBottom: '20px' 
        },
        tabContainer: {
            display: 'flex',
            borderBottom: `2px solid ${colors.border}`,
            marginBottom: '20px',
            backgroundColor: colors.cardBg,
            borderRadius: '8px 8px 0 0',
            overflowX: 'auto',
        },
        tab: {
            padding: '15px 25px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '16px',
            fontWeight: '500',
            color: colors.text,
            transition: 'all 0.3s ease',
            borderBottom: '3px solid transparent',
            whiteSpace: 'nowrap',
        },
        activeTab: {
            backgroundColor: colors.activeTab,
            color: 'white',
            borderBottom: `3px solid ${colors.activeTab}`,
        },
        tabContent: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '0 0 8px 8px',
            padding: '20px',
            minHeight: '400px',
        },
        table: { 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '12px',
        },
        tableHeader: { 
            backgroundColor: colors.headerBg, 
            borderBottom: `2px solid ${colors.border}` 
        },
        tableHeaderCell: { 
            padding: '8px 6px', 
            textAlign: 'left', 
            fontWeight: 'bold', 
            color: colors.text,
            fontSize: '11px',
            whiteSpace: 'nowrap',
            border: `1px solid ${colors.border}`,
        },
        tableRow: { 
            borderBottom: `1px solid ${colors.border}`,
            '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa' } 
        },
        tableCell: { 
            padding: '6px 4px', 
            color: colors.text, 
            fontSize: '10px',
            border: `1px solid ${colors.border}`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100px',
        },
        exportButton: {
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            marginBottom: '20px',
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
        },
        noDataContainer: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        controlsContainer: {
            display: 'flex',
            gap: '15px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        filterSelect: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
        },
        searchInput: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            minWidth: '200px',
            fontSize: '14px',
        },
        statusBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            color: 'white',
        },
        siteBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            backgroundColor: colors.link,
            color: 'white',
        },
        button: {
            padding: '8px 16px',
            borderRadius: '4px',
            border: '1px solid #007bff',
            backgroundColor: '#007bff',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.3s ease',
        },
    };
};



function convertDateToSerialNumber(date) {
    // Get the full year from the date object
    const year = date.getFullYear();
  
    // Get the last digit of the year
    const lastDigitOfYear = year.toString().slice(-1);
  
    // Concatenate the last digit with "001" and convert to a number
    const prefix = Number(lastDigitOfYear + "001");
  
    // Create a new date object for January 1st of the same year
    const startOfYear = new Date(year, 0, 1);
  
    // Calculate the difference in milliseconds and convert to days
    const dayOfYear = Math.floor((date - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
  
    // Add the day of the year to the prefix
    return prefix + dayOfYear;
  };

// --- END OF STATIC CONTENT COMPONENTS ---

function DailyAircraftStatusReport({ theme }) {
    const styles = getStyles(theme);
    // Set default tab to 'ac-status'
    const [activeTab, setActiveTab] = useState('ac-status');
    const [, setLoading] = useState(false); // setLoading used in exportToExcel, loading value not displayed yet
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [sites, setSites] = useState([]);
    const [scrRfpaCurrentDate] = useState(new Date().toISOString().split('T')[0]);
    const [scrRfpaDate, setScrRfpaDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Track SCR-RFPA date changes for Excel export
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSite, setSelectedSite] = useState('');
    const [selectedTms, setSelectedTms] = useState('');
    const [tmsList, setTmsList] = useState([]);
    
    // FLT Data tab month/year state (for Excel export sync and component state)
    const [fltDataMonth, setFltDataMonth] = useState(new Date().getMonth() + 1);
    const [fltDataYear, setFltDataYear] = useState(new Date().getFullYear());
    
    // Callback to handle month/year changes from FLT Data tab
    const handleFltDataMonthYearChange = useCallback((month, year) => {
        setFltDataMonth(month);
        setFltDataYear(year);
    }, []);
    
    // Placeholder data states (retained but unused for static tabs)
    const [aircraftStatusData, setAircraftStatusData] = useState([]);
    const [dailyACTimeData, setDailyACTimeData] = useState([]);
    const [flightHourData, setFlightHourData] = useState([]);
    // Empty API responses are valid. Track load state separately from array
    // length so an empty result does not trigger a render/fetch loop.
    const aircraftStatusLoadState = useRef('idle');
    const dailyACTimeLoadState = useRef('idle');
    const flightHourLoadState = useRef('idle');
    
    // DSR Summary data
    const [dsrSummary, setDsrSummary] = useState({
        totalAircraft: 0,
        num_assigned: 0, // Total aircraft assigned to site
        num_nmcrs: 0, // Non-Mission Capable Rate Supply (ACI + AWP + OTH + REI + AIMD + DET)
        num_mcrs: 0, // Mission Capable Rate Supply (UP + XC + PM + FCF + DN + DS )
        num_nmcm: 0, // Non-Mission Capable Maintenance (DN)
        num_pm: 0, // Planned Maintenance (PM)
        num_nmcs: 0, // Non-Mission Capable Supply (DS)
        num_pmc_fcf: 0, // Planned Maintenance Complete - Functional Check Flight (FCF)
        num_mc_minus_fcf: 0, // Mission Capable minus FCF (UP + XC + PM)
        num_xc_prepos: 0, // Cross Country / Preposition (XC)
        num_det: 0, // Detachment (DET)
        num_rfp_mob: 0, // Ready for Production / Mobile (UP)
    });

    const { isStaff, groups } = useAuth();
    
    // Filter sites based on user groups
    const filteredSites = useMemo(() => {
        if (isStaff) {
            return sites;
        }
        return sites.filter(site => {
            return groups.some(group => 
                group.toUpperCase() === site.name.toUpperCase()
            );
        });
    }, [sites, groups, isStaff]);

    // If the user can only work with one site, select it automatically so
    // site-specific editors (including SCR-RFPA) are immediately available.
    // Keep "All Sites" for users with multiple choices to avoid saving data
    // against an arbitrary location.
    useEffect(() => {
        if (selectedSite === '' && filteredSites.length === 1) {
            setSelectedSite(String(filteredSites[0].id));
        }
    }, [filteredSites, selectedSite]);

    const filteredTms = useMemo(() => {
        const tmsGroups = getTmsGroups(groups, tmsList);
        if (isStaff || tmsGroups.length === 0) return tmsList;
        return tmsList.filter((tms) =>
            tmsGroups.some((group) => group.toUpperCase() === String(tms.name || '').toUpperCase())
        );
    }, [tmsList, groups, isStaff]);
    
    // Only fetch sites/TMS catalogs on mount - other data will be fetched lazily
    useEffect(() => {
        const fetchCatalogs = async () => {
            try {
                const [sitesRes, tmsRes] = await Promise.all([
                    api.get('/sites/'),
                    api.get('/tms/'),
                ]);
                const sitesData = Array.isArray(sitesRes.data) ?
                    sitesRes.data : (sitesRes.data.results || []);
                const tmsData = Array.isArray(tmsRes.data) ?
                    tmsRes.data : (tmsRes.data.results || []);
                setSites(sitesData);
                setTmsList(tmsData);
            } catch (error) {
                setSites([]);
                setTmsList([]);
            }
        };

        fetchCatalogs();
    }, []);

    useEffect(() => {
        const tmsGroups = getTmsGroups(groups, tmsList);
        if (!isStaff && tmsGroups.length === 1 && selectedTms === '') {
            setSelectedTms(tmsGroups[0]);
        }
    }, [groups, isStaff, tmsList, selectedTms]);

    // Calculate DSR summary metrics
    const calculateDSRSummary = useCallback((aircraftData) => {
        const siteName = selectedSite
            ? sites.find(s => s.id === parseInt(selectedSite))?.name
            : null;
        const filteredData = aircraftData.filter((aircraft) => {
            if (siteName && aircraft.site !== siteName) return false;
            if (selectedTms && getAircraftTmsName(aircraft) !== selectedTms) return false;
            return true;
        });

        // Calculate counts for each status category
        const statusCounts = {
            UP: filteredData.filter(ac => ac.status === 'UP').length,
            XC: filteredData.filter(ac => ac.status === 'XC').length,
            DN: filteredData.filter(ac => ac.status === 'DN').length,
            DS: filteredData.filter(ac => ac.status === 'DS').length,
            PM: filteredData.filter(ac => ac.status === 'PM').length,
            FCF: filteredData.filter(ac => ac.status === 'FCF').length,
            AWP: filteredData.filter(ac => ac.status === 'AWP').length,
            DET: filteredData.filter(ac => ac.status === 'DET').length,
            OTH: filteredData.filter(ac => ac.status === 'OTH').length,
            ACI: filteredData.filter(ac => ac.status === 'ACI').length,
            REI: filteredData.filter(ac => ac.status === 'REI').length,
            AIMD: filteredData.filter(ac => ac.status === 'AIMD').length,
        };
        
        // Calculate DSR metrics based on aircraft status definitions
        const dsrMetrics = {
            num_assigned: filteredData.length, // Total aircraft assigned
            num_nmcrs: statusCounts.AIMD + statusCounts.AWP + statusCounts.OTH + statusCounts.ACI + statusCounts.REI + statusCounts.DET, // Non-Mission Capable Rate Supply
            num_mcrs: statusCounts.UP + statusCounts.XC + statusCounts.PM + statusCounts.FCF + statusCounts.DN + statusCounts.DS, // Mission Capable Rate Supply
            num_nmcm: statusCounts.DN, // Non-Mission Capable Maintenance
            num_pm: statusCounts.PM, // Planned Maintenance
            num_nmcs: statusCounts.DS, // Non-Mission Capable Supply (same as NMCRS)
            num_pmc_fcf: statusCounts.FCF, // Planned Maintenance Complete - FCF
            num_mc_minus_fcf: statusCounts.UP + statusCounts.XC + statusCounts.PM, // Mission Capable minus FCF
            num_xc_prepos: statusCounts.XC, // Cross Country / Preposition
            num_det: statusCounts.DET, // Detachment
            num_rfp_mob: statusCounts.UP, // Ready for Production / MOB
        };

        setDsrSummary(dsrMetrics);
    }, [selectedSite, selectedTms, sites]);

    // Lazy loading functions for each tab
    const fetchAircraftStatusData = useCallback(async () => {
        if (aircraftStatusLoadState.current === 'loaded') {
            return aircraftStatusData;
        }
        if (aircraftStatusLoadState.current === 'loading') {
            return [];
        }

        aircraftStatusLoadState.current = 'loading';
        try {
            const response = await api.get('/aircraft-status/');
            const data = Array.isArray(response.data) ?
                response.data : (response.data.results || []);
            aircraftStatusLoadState.current = 'loaded';
            setAircraftStatusData(data);
            calculateDSRSummary(data);
            return data;
        } catch (error) {
            aircraftStatusLoadState.current = 'idle';
            return [];
        }
    }, [aircraftStatusData, calculateDSRSummary]);

    const fetchDailyACTimeData = useCallback(async () => {
        if (dailyACTimeLoadState.current === 'loaded') {
            return dailyACTimeData;
        }
        if (dailyACTimeLoadState.current === 'loading') {
            return [];
        }

        dailyACTimeLoadState.current = 'loading';
        try {
            const response = await api.get('/daily-ac-time/');
            const data = Array.isArray(response.data) ?
                response.data : (response.data.results || []);
            dailyACTimeLoadState.current = 'loaded';
            setDailyACTimeData(data);
            return data;
        } catch (error) {
            dailyACTimeLoadState.current = 'idle';
            return [];
        }
    }, [dailyACTimeData]);


    const fetchFlightHourData = useCallback(async () => {
        if (flightHourLoadState.current === 'loaded') {
            return flightHourData;
        }
        if (flightHourLoadState.current === 'loading') {
            return [];
        }

        flightHourLoadState.current = 'loading';
        try {
            const response = await api.get('/flight-hour-monthly/');
            const data = Array.isArray(response.data) ?
                response.data : (response.data.results || []);
            flightHourLoadState.current = 'loaded';
            setFlightHourData(data);
            return data;
        } catch (error) {
            flightHourLoadState.current = 'idle';
            return [];
        }
    }, [flightHourData]);

    // Recalculate DSR summary when aircraft status data or selected site changes
    useEffect(() => {
        if (aircraftStatusData.length > 0) {
            calculateDSRSummary(aircraftStatusData);
        }
    }, [aircraftStatusData, selectedSite, selectedTms, sites, calculateDSRSummary]);

    // Fetch data when switching to specific tabs
    useEffect(() => {
        if (activeTab === 'ac-status' || selectedTms) {
            fetchAircraftStatusData();
        }
        if (activeTab === 'flt-data') {
            fetchDailyACTimeData();
            fetchFlightHourData();
        }
    }, [activeTab, selectedTms, fetchAircraftStatusData, fetchDailyACTimeData, fetchFlightHourData]);

    // Create filtered versions of all data based on selected site
    const getSelectedSiteName = useCallback(() => {
        if (!selectedSite) return null;
        const site = sites.find(s => s.id === parseInt(selectedSite));
        return site ? site.name : null;
    }, [selectedSite, sites]);

    const filteredAircraftStatusData = useMemo(() => {
        const siteName = getSelectedSiteName();
        return aircraftStatusData.filter((item) => {
            if (siteName && item.site !== siteName) return false;
            if (selectedTms && getAircraftTmsName(item) !== selectedTms) return false;
            return true;
        });
    }, [aircraftStatusData, getSelectedSiteName, selectedTms]);

    const tmsBunos = useMemo(() => {
        if (!selectedTms) return null;
        const siteName = getSelectedSiteName();
        return new Set(
            aircraftStatusData
                .filter((item) => {
                    if (getAircraftTmsName(item) !== selectedTms) return false;
                    if (siteName && item.site !== siteName) return false;
                    return true;
                })
                .map((item) => String(item.buno || ''))
                .filter(Boolean)
        );
    }, [aircraftStatusData, selectedTms, getSelectedSiteName]);

    const filteredDailyACTimeData = useMemo(() => {
        const siteName = getSelectedSiteName();
        return dailyACTimeData.filter((item) => {
            if (siteName && item.site_name !== siteName) return false;
            if (tmsBunos && !tmsBunos.has(String(item.t_buno || ''))) return false;
            return true;
        });
    }, [dailyACTimeData, getSelectedSiteName, tmsBunos]);

    // Phase Window computation from Daily AC Time
    // Helper function to parse date string without timezone conversion
    const parseDateLocal = (dateString) => {
        if (!dateString) return null;
        // If it's already a Date object, return it
        if (dateString instanceof Date) return dateString;
        // Parse YYYY-MM-DD format and create date in local timezone
        const parts = dateString.split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const day = parseInt(parts[2], 10);
            return new Date(year, month, day);
        }
        // Fallback to standard Date parsing
        return new Date(dateString);
    };

    const parseHours = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? null : num;
    };

    const buildPhaseRows = useCallback((rows, predicate) => {
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const fmt = (d) => {
            if (!d) return '';
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return '';
            const dd = String(dt.getDate()).padStart(2, '0');
            const mmm = monthNames[dt.getMonth()];
            const yyyy = dt.getFullYear();
            return `${dd}-${mmm}-${yyyy}`;
        };
        const list = rows
            .map((r) => {
                const hoursToPhase = parseHours(r.t_hours_to_phase);
                return {
                    buno: r.t_buno || r.buno || r.serno || '',
                    hours: hoursToPhase,
                    lfd: r.t_last_flown_date ? fmt(r.t_last_flown_date) : '',
                    ndf: r.t_next_phase ? String(r.t_next_phase) : '',
                };
            })
            .filter((r) => r.hours !== null && predicate(r.hours))
            .sort((a, b) => {
                const aa = parseInt(a.buno, 10);
                const bb = parseInt(b.buno, 10);
                if (isNaN(aa) && isNaN(bb)) return String(a.buno).localeCompare(String(b.buno));
                if (isNaN(aa)) return 1;
                if (isNaN(bb)) return -1;
                return aa - bb;
            })
            .map((r, idx) => ({
                idx: idx + 1,
                buno: r.buno,
                time: `PHASE INSPECTION ( ${r.hours.toFixed(1)} HRS )`,
                lfd: r.lfd,
                ndf: r.ndf,
            }));
        return list;
    }, []);

    const phaseLt100Rows = useMemo(() => {
        // <= 40 and >= 0 hours to phase
        return buildPhaseRows(filteredDailyACTimeData || [], (h) => h <= 40 && h >= 0);
    }, [filteredDailyACTimeData, buildPhaseRows]);

    const phaseGt100Rows = useMemo(() => {
        // less than 0 hours to phase
        return buildPhaseRows(filteredDailyACTimeData || [], (h) => h < 0);
    }, [filteredDailyACTimeData, buildPhaseRows]);


    const filteredFlightHourData = useMemo(() => {
        const siteName = getSelectedSiteName();
        if (!siteName) return flightHourData;
        return flightHourData.filter(item => item.site_name === siteName);
    }, [flightHourData, getSelectedSiteName]);

    // Build NMCRS table rows from Aircraft STATUS API (ACI + AWP + OTH + REI + AIMD)
    const nmcrsStatusCodes = useMemo(() => new Set(['ACI', 'AWP', 'OTH', 'REI', 'AIMD']), []);

    //
    const nmcrsRows = useMemo(() => {
        const rows = (filteredAircraftStatusData || [])
            .filter(item => item && nmcrsStatusCodes.has(item.status))
            .sort((a, b) => {
                const ba = parseInt(a.buno, 10);
                const bb = parseInt(b.buno, 10);
                if (isNaN(ba) && isNaN(bb)) return String(a.buno).localeCompare(String(b.buno));
                if (isNaN(ba)) return 1;
                if (isNaN(bb)) return -1;
                return ba - bb;
            })
            .map((item, idx) => {
                const edd = item.edd ? parseDateLocal(item.edd) : null;
                const ecd = item.ecd ? parseDateLocal(item.ecd) : null;
                let ndfDate = null;
                if (edd && !isNaN(edd.getTime()) && ecd && !isNaN(ecd.getTime())) {
                    ndfDate = edd <= ecd ? edd : ecd;
                } else if (edd && !isNaN(edd.getTime())) {
                    ndfDate = edd;
                } else if (ecd && !isNaN(ecd.getTime())) {
                    ndfDate = ecd;
                }
                
                let ndf;
                if (ndfDate) {
                    const day = String(ndfDate.getDate()).padStart(2, '0');
                    const month = ndfDate.toLocaleDateString('en', { month: 'short' });
                    const year = String(ndfDate.getFullYear()).slice(-2);
                    ndf = `${day}-${month}-${year}`;
                } else {
                    ndf = item.status === 'AWP' ? 'EDDIW' : '';
                }
                
                let lfd;
                // If aircraft status is ACI, leave LFD blank (aircraft won't have LFD from Daily AC Time)
                if (item.status === 'ACI') {
                    lfd = '';
                } else if (item.last_fly_date) {
                    const lfdDate = new Date(item.last_fly_date);
                    if (!isNaN(lfdDate.getTime())) {
                        const day = String(lfdDate.getDate()).padStart(2, '0');
                        const month = lfdDate.toLocaleDateString('en', { month: 'short' });
                        const year = String(lfdDate.getFullYear()).slice(-2);
                        lfd = `${day}-${month}-${year}`;
                    } else {
                        lfd = '';
                    }
                } else {
                    lfd = '';
                }
                
                return {
                    idx: idx + 1,
                    buno: item.buno,
                    status: item.status,
                    reason: item.reason || '',
                    lfd,
                    ndf,
                };
            });
        return rows;
    }, [filteredAircraftStatusData, nmcrsStatusCodes]);

    // Pair NMCRS rows so LEFT column is 1..ceil(n/2) and RIGHT continues after
    const nmcrsRowPairs = useMemo(() => {
        const n = nmcrsRows.length;
        const split = Math.ceil(n / 2);
        const leftCol = nmcrsRows.slice(0, split);  // idx 1..split
        const rightCol = nmcrsRows.slice(split);    // idx split+1..n
        const maxLen = Math.max(leftCol.length, rightCol.length);
        const pairs = [];
        for (let i = 0; i < maxLen; i += 1) {
            const left = leftCol[i];
            const right = rightCol[i];
            pairs.push([left, right]);
        }
        return pairs;
    }, [nmcrsRows]);

    // Build Section D rows for DN status aircraft
    const sectionDRows = useMemo(() => {
        const rows = (filteredAircraftStatusData || [])
            .filter(item => item.status === 'DN')
            .sort((a, b) => {
                const ba = parseInt(String(a.buno).replace(/\D/g, ''), 10);
                const bb = parseInt(String(b.buno).replace(/\D/g, ''), 10);
                if (isNaN(ba) && isNaN(bb)) return String(a.buno).localeCompare(String(b.buno));
                if (isNaN(ba)) return 1;
                if (isNaN(bb)) return -1;
                return ba - bb;
            })
            .map((item, idx) => {
                const edd = item.edd ? parseDateLocal(item.edd) : null;
                const ecd = item.ecd ? parseDateLocal(item.ecd) : null;
                let ndfDate = null;
                if (edd && !isNaN(edd.getTime()) && ecd && !isNaN(ecd.getTime())) {
                    ndfDate = edd <= ecd ? edd : ecd;
                } else if (edd && !isNaN(edd.getTime())) {
                    ndfDate = edd;
                } else if (ecd && !isNaN(ecd.getTime())) {
                    ndfDate = ecd;
                }
                
                let ndf;
                if (ndfDate) {
                    const day = String(ndfDate.getDate()).padStart(2, '0');
                    const month = ndfDate.toLocaleDateString('en', { month: 'short' });
                    const year = String(ndfDate.getFullYear()).slice(-2);
                    ndf = `${day}-${month}-${year}`;
                } else {
                    ndf = item.status === 'AWP' ? 'EDDIW' : '';
                }
                
                let lfd;
                // Check if aircraft exists in Daily AC Time data - if not found, likely at ACI, leave LFD blank
                const foundInDailyACTime = filteredDailyACTimeData?.some(acTime => 
                    acTime.t_buno === item.buno || acTime.t_buno?.slice(-3) === item.buno?.slice(-3)
                );
                
                // If aircraft status is ACI or not found in Daily AC Time, leave LFD blank
                if (item.status === 'ACI' || !foundInDailyACTime) {
                    lfd = '';
                } else if (item.last_fly_date) {
                    const lfdDate = new Date(item.last_fly_date);
                    if (!isNaN(lfdDate.getTime())) {
                        const day = String(lfdDate.getDate()).padStart(2, '0');
                        const month = lfdDate.toLocaleDateString('en', { month: 'short' });
                        const year = String(lfdDate.getFullYear()).slice(-2);
                        lfd = `${day}-${month}-${year}`;
                    } else {
                        lfd = '';
                    }
                } else {
                    lfd = '';
                }
                
                return {
                    idx: idx + 1,
                    buno: item.buno,
                    status: item.status,
                    reason: item.reason || '',
                    lfd,
                    ndf,
                };
            });
        return rows;
    }, [filteredAircraftStatusData, filteredDailyACTimeData]);

    // Pair Section D rows so LEFT column is 1..ceil(n/2) and RIGHT continues after
    const sectionDRowPairs = useMemo(() => {
        const n = sectionDRows.length;
        const split = Math.ceil(n / 2);
        const leftCol = sectionDRows.slice(0, split);  // idx 1..split
        const rightCol = sectionDRows.slice(split);    // idx split+1..n
        const maxLen = Math.max(leftCol.length, rightCol.length);
        const pairs = [];
        for (let i = 0; i < maxLen; i += 1) {
            const left = leftCol[i];
            const right = rightCol[i];
            pairs.push([left, right]);
        }
        return pairs;
    }, [sectionDRows]);

    // Build Section E rows for PM status aircraft
    const sectionERows = useMemo(() => {
        const rows = (filteredAircraftStatusData || [])
            .filter(item => item.status === 'PM')
            .sort((a, b) => {
                const ba = parseInt(String(a.buno).replace(/\D/g, ''), 10);
                const bb = parseInt(String(b.buno).replace(/\D/g, ''), 10);
                if (isNaN(ba) && isNaN(bb)) return String(a.buno).localeCompare(String(b.buno));
                if (isNaN(ba)) return 1;
                if (isNaN(bb)) return -1;
                return ba - bb;
            })
            .map((item, idx) => {
                const edd = item.edd ? parseDateLocal(item.edd) : null;
                const ecd = item.ecd ? parseDateLocal(item.ecd) : null;
                let ndfDate = null;
                if (edd && !isNaN(edd.getTime()) && ecd && !isNaN(ecd.getTime())) {
                    ndfDate = edd <= ecd ? edd : ecd;
                } else if (edd && !isNaN(edd.getTime())) {
                    ndfDate = edd;
                } else if (ecd && !isNaN(ecd.getTime())) {
                    ndfDate = ecd;
                }
                
                let ndf;
                if (ndfDate) {
                    const day = String(ndfDate.getDate()).padStart(2, '0');
                    const month = ndfDate.toLocaleDateString('en', { month: 'short' });
                    const year = String(ndfDate.getFullYear()).slice(-2);
                    ndf = `${day}-${month}-${year}`;
                } else {
                    ndf = item.status === 'AWP' ? 'EDDIW' : '';
                }
                
                let lfd;
                // Check if aircraft exists in Daily AC Time data - if not found, likely at ACI, leave LFD blank
                const foundInDailyACTime = filteredDailyACTimeData?.some(acTime => 
                    acTime.t_buno === item.buno || acTime.t_buno?.slice(-3) === item.buno?.slice(-3)
                );
                
                // If aircraft status is ACI or not found in Daily AC Time, leave LFD blank
                if (item.status === 'ACI' || !foundInDailyACTime) {
                    lfd = '';
                } else if (item.last_fly_date) {
                    const lfdDate = new Date(item.last_fly_date);
                    if (!isNaN(lfdDate.getTime())) {
                        const day = String(lfdDate.getDate()).padStart(2, '0');
                        const month = lfdDate.toLocaleDateString('en', { month: 'short' });
                        const year = String(lfdDate.getFullYear()).slice(-2);
                        lfd = `${day}-${month}-${year}`;
                    } else {
                        lfd = '';
                    }
                } else {
                    lfd = '';
                }
                
                return {
                    idx: idx + 1,
                    buno: item.buno,
                    status: item.status,
                    reason: item.reason || '',
                    lfd,
                    ndf,
                };
            });
        return rows;
    }, [filteredAircraftStatusData, filteredDailyACTimeData]);

    // Build Section F rows for DS status aircraft
    const sectionFRows = useMemo(() => {
        const rows = (filteredAircraftStatusData || [])
            .filter(item => item.status === 'DS')
            .sort((a, b) => {
                const ba = parseInt(String(a.buno).replace(/\D/g, ''), 10);
                const bb = parseInt(String(b.buno).replace(/\D/g, ''), 10);
                if (isNaN(ba) && isNaN(bb)) return String(a.buno).localeCompare(String(b.buno));
                if (isNaN(ba)) return 1;
                if (isNaN(bb)) return -1;
                return ba - bb;
            })
            .map((item, idx) => {
                const edd = item.edd ? parseDateLocal(item.edd) : null;
                const ecd = item.ecd ? parseDateLocal(item.ecd) : null;
                let ndfDate = null;
                if (edd && !isNaN(edd.getTime()) && ecd && !isNaN(ecd.getTime())) {
                    ndfDate = edd <= ecd ? edd : ecd;
                } else if (edd && !isNaN(edd.getTime())) {
                    ndfDate = edd;
                } else if (ecd && !isNaN(ecd.getTime())) {
                    ndfDate = ecd;
                }
                
                let ndf;
                if (ndfDate) {
                    const day = String(ndfDate.getDate()).padStart(2, '0');
                    const month = ndfDate.toLocaleDateString('en', { month: 'short' });
                    const year = String(ndfDate.getFullYear()).slice(-2);
                    ndf = `${day}-${month}-${year}`;
                } else {
                    ndf = item.status === 'AWP' ? 'EDDIW' : '';
                }
                
                let lfd;
                // Check if aircraft exists in Daily AC Time data - if not found, likely at ACI, leave LFD blank
                const foundInDailyACTime = filteredDailyACTimeData?.some(acTime => 
                    acTime.t_buno === item.buno || acTime.t_buno?.slice(-3) === item.buno?.slice(-3)
                );
                
                // If aircraft status is ACI or not found in Daily AC Time, leave LFD blank
                if (item.status === 'ACI' || !foundInDailyACTime) {
                    lfd = '';
                } else if (item.last_fly_date) {
                    const lfdDate = new Date(item.last_fly_date);
                    if (!isNaN(lfdDate.getTime())) {
                        const day = String(lfdDate.getDate()).padStart(2, '0');
                        const month = lfdDate.toLocaleDateString('en', { month: 'short' });
                        const year = String(lfdDate.getFullYear()).slice(-2);
                        lfd = `${day}-${month}-${year}`;
                    } else {
                        lfd = '';
                    }
                } else {
                    lfd = '';
                }
                
                return {
                    idx: idx + 1,
                    buno: item.buno,
                    status: item.status,
                    reason: item.reason || '',
                    lfd,
                    ndf,
                };
            });
        return rows;
    }, [filteredAircraftStatusData, filteredDailyACTimeData]);

    // Build Section G rows for FCF status aircraft
    const sectionGRows = useMemo(() => {
        const rows = (filteredAircraftStatusData || [])
            .filter(item => item.status === 'FCF')
            .sort((a, b) => {
                const ba = parseInt(String(a.buno).replace(/\D/g, ''), 10);
                const bb = parseInt(String(b.buno).replace(/\D/g, ''), 10);
                if (isNaN(ba) && isNaN(bb)) return String(a.buno).localeCompare(String(b.buno));
                if (isNaN(ba)) return 1;
                if (isNaN(bb)) return -1;
                return ba - bb;
            })
            .map((item, idx) => {
                const edd = item.edd ? parseDateLocal(item.edd) : null;
                const ecd = item.ecd ? parseDateLocal(item.ecd) : null;
                let ndfDate = null;
                if (edd && !isNaN(edd.getTime()) && ecd && !isNaN(ecd.getTime())) {
                    ndfDate = edd <= ecd ? edd : ecd;
                } else if (edd && !isNaN(edd.getTime())) {
                    ndfDate = edd;
                } else if (ecd && !isNaN(ecd.getTime())) {
                    ndfDate = ecd;
                }
                
                let ndf;
                if (ndfDate) {
                    const day = String(ndfDate.getDate()).padStart(2, '0');
                    const month = ndfDate.toLocaleDateString('en', { month: 'short' });
                    const year = String(ndfDate.getFullYear()).slice(-2);
                    ndf = `${day}-${month}-${year}`;
                } else {
                    ndf = item.status === 'AWP' ? 'EDDIW' : '';
                }
                
                let lfd;
                // Check if aircraft exists in Daily AC Time data - if not found, likely at ACI, leave LFD blank
                const foundInDailyACTime = filteredDailyACTimeData?.some(acTime => 
                    acTime.t_buno === item.buno || acTime.t_buno?.slice(-3) === item.buno?.slice(-3)
                );
                
                // If aircraft status is ACI or not found in Daily AC Time, leave LFD blank
                if (item.status === 'ACI' || !foundInDailyACTime) {
                    lfd = '';
                } else if (item.last_fly_date) {
                    const lfdDate = new Date(item.last_fly_date);
                    if (!isNaN(lfdDate.getTime())) {
                        const day = String(lfdDate.getDate()).padStart(2, '0');
                        const month = lfdDate.toLocaleDateString('en', { month: 'short' });
                        const year = String(lfdDate.getFullYear()).slice(-2);
                        lfd = `${day}-${month}-${year}`;
                    } else {
                        lfd = '';
                    }
                } else {
                    lfd = '';
                }
                
                return {
                    idx: idx + 1,
                    buno: item.buno,
                    status: item.status,
                    reason: item.reason || '',
                    lfd,
                    ndf,
                };
            });
        return rows;
    }, [filteredAircraftStatusData, filteredDailyACTimeData]);

    // Build Section H rows for DET and XC status aircraft
    const sectionHRows = useMemo(() => {
        const rows = (filteredAircraftStatusData || [])
            .filter(item => item.status === 'DET' || item.status === 'XC')
            .sort((a, b) => {
                const ba = parseInt(String(a.buno).replace(/\D/g, ''), 10);
                const bb = parseInt(String(b.buno).replace(/\D/g, ''), 10);
                if (isNaN(ba) && isNaN(bb)) return String(a.buno).localeCompare(String(b.buno));
                if (isNaN(ba)) return 1;
                if (isNaN(bb)) return -1;
                return ba - bb;
            })
            .map((item, idx) => {
                const edd = item.edd ? parseDateLocal(item.edd) : null;
                const ecd = item.ecd ? parseDateLocal(item.ecd) : null;
                let ndfDate = null;
                if (edd && !isNaN(edd.getTime()) && ecd && !isNaN(ecd.getTime())) {
                    ndfDate = edd <= ecd ? edd : ecd;
                } else if (edd && !isNaN(edd.getTime())) {
                    ndfDate = edd;
                } else if (ecd && !isNaN(ecd.getTime())) {
                    ndfDate = ecd;
                }
                
                let ndf;
                if (ndfDate) {
                    const day = String(ndfDate.getDate()).padStart(2, '0');
                    const month = ndfDate.toLocaleDateString('en', { month: 'short' });
                    const year = String(ndfDate.getFullYear()).slice(-2);
                    ndf = `${day}-${month}-${year}`;
                } else {
                    ndf = item.status === 'AWP' ? 'EDDIW' : '';
                }
                
                let lfd;
                // Check if aircraft exists in Daily AC Time data - if not found, likely at ACI, leave LFD blank
                const foundInDailyACTime = filteredDailyACTimeData?.some(acTime => 
                    acTime.t_buno === item.buno || acTime.t_buno?.slice(-3) === item.buno?.slice(-3)
                );
                
                // If aircraft status is ACI or not found in Daily AC Time, leave LFD blank
                if (item.status === 'ACI' || !foundInDailyACTime) {
                    lfd = '';
                } else if (item.last_fly_date) {
                    const lfdDate = new Date(item.last_fly_date);
                    if (!isNaN(lfdDate.getTime())) {
                        const day = String(lfdDate.getDate()).padStart(2, '0');
                        const month = lfdDate.toLocaleDateString('en', { month: 'short' });
                        const year = String(lfdDate.getFullYear()).slice(-2);
                        lfd = `${day}-${month}-${year}`;
                    } else {
                        lfd = '';
                    }
                } else {
                    lfd = '';
                }
                
                return {
                    idx: idx + 1,
                    buno: item.buno,
                    status: item.status,
                    reason: item.reason || '',
                    lfd,
                    ndf,
                };
            });
        return rows;
    }, [filteredAircraftStatusData, filteredDailyACTimeData]);

    // Pair rows for each section
    const sectionERowPairs = useMemo(() => {
        const n = sectionERows.length;
        const split = Math.ceil(n / 2);
        const leftCol = sectionERows.slice(0, split);
        const rightCol = sectionERows.slice(split);
        const maxLen = Math.max(leftCol.length, rightCol.length);
        const pairs = [];
        for (let i = 0; i < maxLen; i += 1) {
            const left = leftCol[i];
            const right = rightCol[i];
            pairs.push([left, right]);
        }
        return pairs;
    }, [sectionERows]);

    const sectionFRowPairs = useMemo(() => {
        const n = sectionFRows.length;
        const split = Math.ceil(n / 2);
        const leftCol = sectionFRows.slice(0, split);
        const rightCol = sectionFRows.slice(split);
        const maxLen = Math.max(leftCol.length, rightCol.length);
        const pairs = [];
        for (let i = 0; i < maxLen; i += 1) {
            const left = leftCol[i];
            const right = rightCol[i];
            pairs.push([left, right]);
        }
        return pairs;
    }, [sectionFRows]);

    const sectionGRowPairs = useMemo(() => {
        const n = sectionGRows.length;
        const split = Math.ceil(n / 2);
        const leftCol = sectionGRows.slice(0, split);
        const rightCol = sectionGRows.slice(split);
        const maxLen = Math.max(leftCol.length, rightCol.length);
        const pairs = [];
        for (let i = 0; i < maxLen; i += 1) {
            const left = leftCol[i];
            const right = rightCol[i];
            pairs.push([left, right]);
        }
        return pairs;
    }, [sectionGRows]);

    const sectionHRowPairs = useMemo(() => {
        const n = sectionHRows.length;
        const split = Math.ceil(n / 2);
        const leftCol = sectionHRows.slice(0, split);
        const rightCol = sectionHRows.slice(split);
        const maxLen = Math.max(leftCol.length, rightCol.length);
        const pairs = [];
        for (let i = 0; i < maxLen; i += 1) {
            const left = leftCol[i];
            const right = rightCol[i];
            pairs.push([left, right]);
        }
        return pairs;
    }, [sectionHRows]);



    // Status functions for Excel export
    const getStatus1 = (status) => {
        if (['UP', 'XC', 'FCF', 'PMC'].includes(status)) return 'FMC';
        if (status === 'DET') return 'DET';
        if (status === 'PM') return 'PM';
        if (status === 'DN') return 'NMCM';
        if (status === 'DS') return 'NMCS';
        if (['AWP', 'OTH', '107', 'REI/TEI', 'AIMD', 'ACI'].includes(status)) return 'ACO';
        return status;
    };

    // Function to get site full name from site code
    const getSiteFullName = (siteCode) => siteCode || '';

    const userSiteName = useMemo(() => {
        if (!selectedSite) return '';
        const site = sites.find(s => s.id === parseInt(selectedSite));
        return site ? getSiteFullName(site.name) : '';
    }, [selectedSite, sites]);

    const aircraftType = useMemo(() => {
        if (selectedTms) return selectedTms;
        const siteAircraft = (Array.isArray(aircraftStatusData) ? aircraftStatusData : []).find((row) => {
            if (!selectedSite) return Boolean(getAircraftTmsName(row));
            const siteName = sites.find((s) => s.id === parseInt(selectedSite, 10))?.name;
            const rowSite = row.site || row.aircraft?.site_name;
            return siteName && rowSite === siteName && getAircraftTmsName(row);
        });
        return getAircraftTmsName(siteAircraft);
    }, [selectedSite, selectedTms, sites, aircraftStatusData]);

    // DSR Data object for Excel export
    const DSR_DATA = useMemo(() => ({
        date_code: new Date().toLocaleDateString('en-US', { 
            weekday: 'short', 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        }),
        date: new Date().toLocaleDateString(),
        contract_number: DSR_COVER.taskOrder,
        cdrl_number: DSR_COVER.cdrlNumber,
        location: userSiteName,
        aircraft: aircraftType,
        num_assigned: dsrSummary.num_assigned,
        num_mcrs: dsrSummary.num_mcrs,
        num_nmcrs: dsrSummary.num_nmcrs,
        num_nmcm: dsrSummary.num_nmcm,
        num_pm: dsrSummary.num_pm,
        num_nmcs: dsrSummary.num_nmcs,
        num_pmc_fcf: dsrSummary.num_pmc_fcf,
        num_xc: dsrSummary.num_xc_prepos,
        num_det: dsrSummary.num_det,
        num_lt100: phaseLt100Rows.length,
        num_gt100: phaseGt100Rows.length,
        phase_lt100: phaseLt100Rows,
        phase_gt100: phaseGt100Rows,
    }), [dsrSummary, phaseLt100Rows, phaseGt100Rows, userSiteName, aircraftType]);

    // NDF calculation function
    const getNDF = (edd, ecd, status) => {
        const eddDate = edd ? parseDateLocal(edd) : null;
        const ecdDate = ecd ? parseDateLocal(ecd) : null;
        let ndfDate = null;
        
        if (eddDate && !isNaN(eddDate.getTime()) && ecdDate && !isNaN(ecdDate.getTime())) {
            ndfDate = eddDate <= ecdDate ? eddDate : ecdDate;
        } else if (eddDate && !isNaN(eddDate.getTime())) {
            ndfDate = eddDate;
        } else if (ecdDate && !isNaN(ecdDate.getTime())) {
            ndfDate = ecdDate;
        }
        
        if (ndfDate) {
            const day = String(ndfDate.getDate()).padStart(2, '0');
            const month = ndfDate.toLocaleDateString('en', { month: 'short' });
            const year = String(ndfDate.getFullYear()).slice(-2);
            return `${day}-${month}-${year}`;
        }
        
        return status === 'AWP' ? 'EDDIW' : '';
    };

    // DSR Data preparation for Excel export
    const prepareDSRData = (customPhaseLt100 = null, customPhaseGt100 = null) => {
        const dsrData = [];

        // Use custom phase data if provided, otherwise use memoized state
        const phaseLt100 = customPhaseLt100 !== null ? customPhaseLt100 : DSR_DATA.phase_lt100;
        const phaseGt100 = customPhaseGt100 !== null ? customPhaseGt100 : DSR_DATA.phase_gt100;

        // Add NMCRS data rows
        dsrData.push(['SECTION "C" - AIRCRAFT NOT MISSION CAPABLE REPORTING STATUS (NMCRS) DATA']);
        dsrData.push(['','BUNO','STATUS CODE','LFD *','NDF','','BUNO','STATUS CODE','LFD *','NDF']);
        if (nmcrsRowPairs && nmcrsRowPairs.length > 0) {
                nmcrsRowPairs.forEach((pair) => {
                    const left = pair[0];
                    const right = pair[1];
                    dsrData.push([
                        left?.idx || '',
                        left?.buno || '',
                        left?.reason || '',
                        left?.lfd || '',
                        left?.ndf || '',
                        right ? right.idx : '',
                        right ? right.buno : '',
                        right ? right.reason : '',
                        right ? right.lfd : '',
                        right ? right.ndf : ''
                ]);
            });
            } else {
                dsrData.push(['', '', '', '', '', '', '', '', '', '']);
            }

            dsrData.push(['']);
            dsrData.push(['SECTION "D" - AIRCRAFT "NOT MISSION CAPABLE MAINTENANCE" (NMCM) SUMMARY']);
            dsrData.push(['','BUNO','STATUS CODE','LFD *','NDF','','BUNO','STATUS CODE','LFD *','NDF']);
            
            if(sectionDRowPairs && sectionDRowPairs.length > 0) {
                sectionDRowPairs.forEach((pair) => {
                const left = pair[0];
                const right = pair[1];
                    dsrData.push([
                        left?.idx || '',
                        left?.buno || '',
                        left?.reason || '',
                        left?.lfd || '',
                        left?.ndf || '',
                        right ? right.idx : '',
                        right ? right.buno : '',
                        right ? right.reason : '',
                        right ? right.lfd : '',
                        right ? right.ndf : ''
                ]);
            });
            } else {
                dsrData.push(['', '', '', '', '', '', '', '', '', '']);
            }

            dsrData.push(['']);
            dsrData.push(['SECTION "E" - AIRCRAFT "PLANNED MAINTENANCE" (PM) SUMMARY']);
            dsrData.push(['','BUNO','STATUS CODE','LFD *','NDF','','BUNO','STATUS CODE','LFD *','NDF']);
            //Insert PM data here
            if(sectionERowPairs && sectionERowPairs.length > 0) {
                sectionERowPairs.forEach((pair) => {
                const left = pair[0];
                const right = pair[1];
                    dsrData.push([
                        left?.idx || '',
                        left?.buno || '',
                        left?.reason || '',
                        left?.lfd || '',
                        left?.ndf || '',
                        right ? right.idx : '',
                        right ? right.buno : '',
                        right ? right.reason : '',
                        right ? right.lfd : '',
                        right ? right.ndf : ''
                ]);
            });
            } else {
                dsrData.push(['', '', '', '', '', '', '', '', '', '']);
            }

            dsrData.push(['']);
            dsrData.push(['SECTION "F" - AIRCRAFT "NOT MISSION CAPABLE SUPPLY" (NMCS) DATA']);
            dsrData.push(['','BUNO','STATUS CODE','LFD *','NDF','','BUNO','STATUS CODE','LFD *','NDF']);
            //Insert NMCS data here
            if(sectionFRowPairs && sectionFRowPairs.length > 0) {
                sectionFRowPairs.forEach((pair) => {
                const left = pair[0];
                const right = pair[1];
                    dsrData.push([
                        left?.idx || '',
                        left?.buno || '',
                        left?.reason || '',
                        left?.lfd || '',
                        left?.ndf || '',
                        right ? right.idx : '',
                        right ? right.buno : '',
                        right ? right.reason : '',
                        right ? right.lfd : '',
                        right ? right.ndf : ''
                ]);
            });
            } else {
                dsrData.push(['', '', '', '', '', '', '', '', '', '']);
            }
            dsrData.push(['']);
            dsrData.push(['SECTION "G" - AIRCRAFT AWAITING FUNCTIONAL CHECK FLIGHT (FCF) DATA']);
            dsrData.push(['','BUNO','STATUS CODE','LFD *','NDF','','BUNO','STATUS CODE','LFD *','NDF']);
            //Insert FCF data here
            if(sectionGRowPairs && sectionGRowPairs.length > 0) {
                sectionGRowPairs.forEach((pair) => {
                const left = pair[0];
                const right = pair[1];
                    dsrData.push([
                        left?.idx || '',
                        left?.buno || '',
                        left?.reason || '',
                        left?.lfd || '',
                        left?.ndf || '',
                        right ? right.idx : '',
                        right ? right.buno : '',
                        right ? right.reason : '',
                        right ? right.lfd : '',
                        right ? right.ndf : ''
                ]);
            });
            } else {
                dsrData.push(['', '', '', '', '', '', '', '', '', '']);
            }
            dsrData.push(['']);
            dsrData.push(['SECTION "H" - AIRCRAFT CROSS COUNTRY / PREPOS / DETACHMENT (OUT OF MOB AREA) DATA']);
            dsrData.push(['','BUNO','STATUS CODE','LFD *','NDF','','BUNO','STATUS CODE','LFD *','NDF']);
            //Insert XC/DET data here
            if(sectionHRowPairs && sectionHRowPairs.length > 0) {
                sectionHRowPairs.forEach((pair) => {
                const left = pair[0];
                const right = pair[1];
                    dsrData.push([
                        left?.idx || '',
                        left?.buno || '',
                        left?.reason || '',
                        left?.lfd || '',
                        left?.ndf || '',
                        right ? right.idx : '',
                        right ? right.buno : '',
                        right ? right.reason : '',
                        right ? right.lfd : '',
                        right ? right.ndf : ''
                    ]);
                });
            } else {
                dsrData.push(['', '', '', '', '', '', '', '', '', '']);
            }
            dsrData.push(['']);
            dsrData.push(['SECTION "I" - AIRCRAFT IN PHASE WINDOW']);
            //sectionIRow.getCell(1).font = { name: 'Times New Roman', size: 12, bold: true, underline: true};
            dsrData.push([
                '',
                'BUNO',
                'TIME TO PHASE < 100%',
                'LFD *',
                'NDF',
                '',
                'BUNO',
                'TIME TO PHASE > 100%',
                'LFD *',
                'NDF'
            ]);
            
            // Add Phase Window data
            if ((phaseLt100 && phaseLt100.length > 0) || (phaseGt100 && phaseGt100.length > 0)) {
                // Helper function to get NDF from aircraft status data
                const getNDFFromAircraftStatus = (buno) => {
                    if (!buno || !filteredAircraftStatusData) return '';
                    
                    const aircraft = filteredAircraftStatusData.find(item => 
                        item.buno === buno || item.modex === buno || item.effective_modex === buno || item.buno?.slice(-3) === buno
                    );
                    
                    if (!aircraft) return '';
                    
                    // Use the same NDF calculation logic as in the web interface
                    const getNDF = (edd, ecd, status) => {
                        const eddDate = edd ? parseDateLocal(edd) : null;
                        const ecdDate = ecd ? parseDateLocal(ecd) : null;
                        let ndfDate = null;
                        if (eddDate && !isNaN(eddDate.getTime()) && ecdDate && !isNaN(ecdDate.getTime())) {
                            ndfDate = eddDate <= ecdDate ? eddDate : ecdDate;
                        } else if (eddDate && !isNaN(eddDate.getTime())) {
                            ndfDate = eddDate;
                        } else if (ecdDate && !isNaN(ecdDate.getTime())) {
                            ndfDate = ecdDate;
                        }
                        
                        if (ndfDate) {
                            const day = String(ndfDate.getDate()).padStart(2, '0');
                            const month = ndfDate.toLocaleDateString('en', { month: 'short' });
                            const year = String(ndfDate.getFullYear()).slice(-2);
                            return `${day}-${month}-${year}`;
                        }
                        
                        return status === 'AWP' ? 'EDDIW' : '';
                    };
                    
                    return getNDF(aircraft.edd, aircraft.ecd, aircraft.status);
                };

                // Helper function to get LFD (Last Fly Date) from aircraft status data
                const getLFDFromAircraftStatus = (buno) => {
                    if (!buno || !filteredAircraftStatusData) return '';
                    
                    const aircraft = filteredAircraftStatusData.find(item => 
                        item.buno === buno || item.modex === buno || item.effective_modex === buno || item.buno?.slice(-3) === buno
                    );
                    
                    if (!aircraft) return '';
                    
                    // If aircraft status is ACI, leave LFD blank (aircraft won't have LFD from Daily AC Time)
                    if (aircraft.status === 'ACI') return '';
                    
                    // Return the last fly date if available
                    const lfd = aircraft.lfd || aircraft.last_fly_date;
                    if (lfd) {
                        const lfdDate = new Date(lfd);
                        if (!isNaN(lfdDate.getTime())) {
                            const day = String(lfdDate.getDate()).padStart(2, '0');
                            const month = lfdDate.toLocaleDateString('en', { month: 'short' });
                            const year = String(lfdDate.getFullYear()).slice(-2);
                            return `${day}-${month}-${year}`;
                        }
                    }
                    
                    return '';
                };
                
                // Get the maximum length to handle both arrays
                const maxLength = Math.max(phaseLt100.length, phaseGt100.length);

                for (let i = 0; i < maxLength; i++) {
                    const leftAircraft = phaseLt100[i];
                    const rightAircraft = phaseGt100[i];
                    
                    dsrData.push([
                        i + 1, // Row number for left side
                        leftAircraft?.buno || '',
                        leftAircraft?.time || '',
                        getLFDFromAircraftStatus(leftAircraft?.buno) || '',
                        getNDFFromAircraftStatus(leftAircraft?.buno) || '',
                        i + 1, // Row number for right side
                        rightAircraft?.buno || '',
                        rightAircraft?.time || '',
                        getLFDFromAircraftStatus(rightAircraft?.buno) || '',
                        getNDFFromAircraftStatus(rightAircraft?.buno) || ''
                    ]);
                }
            } else {
                // Add empty row if no data
                dsrData.push(['', '', '', '', '', '', '', '', '', '']);
            }
            
            dsrData.push(['CORRECTIONS/REMARKS']);

            dsrData.push([]);
            // Add remarks rows
            dsrData.push(['', '', '', '', '', '', '', '', '', '']); // remarks1
            dsrData.push(['', '', '', '', '', '', '', '', '', '']); // remarks2  
            dsrData.push(['', '', '', '', '', '', '', '', '', '']); // remarks3
            dsrData.push(['','','Site Manager','','','','','Government Representative','','']); // remarks4
            
            // Note: merging for remarks rows is handled during sheet row creation
            
            return dsrData;
    };

    const exportToExcel = async (exportMonth = null, exportYear = null) => {
        try {
            setLoading(true);
            
            // Ensure fiscal summary data exists before export
            const ensureFiscalSummaryExists = async () => {
                try {
                    const siteName = selectedSite ? sites.find(s => s.id === parseInt(selectedSite))?.name : null;
                    if (!siteName) return;
                    
                    const exportMonthValue = exportMonth || fltDataMonth;
                    const exportYearValue = exportYear || fltDataYear;
                    
                    // Check if there's a fiscal summary for the current month/year
                    const response = await api.get('/monthly-fiscal-summary/', {
                        params: {
                            site_name: siteName,
                            month: exportMonthValue,
                            year: exportYearValue
                        }
                    });
                    
                    // If no data exists, create a default entry
                    if (!response.data || response.data.length === 0) {
                        const defaultData = {
                            site_name: siteName,
                            month: exportMonthValue,
                            year: exportYearValue,
                            previous_months_fiscal_total: 0,
                            previous_quarterly_fiscal_total: 0,
                            weekly_hours: null,
                            accident_hours: 620009.2,
                            quarterly_weekly_hours: null,
                            quarterly_accident_hours: 0,
                            previous_months_scheduled: 0,
                            previous_month_flown: 0,
                            previous_quarterly_scheduled: 0,
                            previous_quarterly_flown: 0
                        };
                        await api.post('/monthly-fiscal-summary/', defaultData);
                        console.log('Created default fiscal summary for export');
                    }
                } catch (error) {
                    console.error('Error ensuring fiscal summary exists before export:', error);
                }
            };
            
            await ensureFiscalSummaryExists();
            setAlert({ show: true, message: 'Preparing Excel export...', type: 'info' });
            
            // Fetch all data needed for Excel export
            const siteName = selectedSite ? sites.find(s => s.id === parseInt(selectedSite))?.name : null;
            const [dailyACTimeResponse, flightDataEntriesResponse, fiscalSummaryResponse, nmcsPmcsResponse] = await Promise.all([
                fetchDailyACTimeData(),
                api.get('/flight-data-entries/'),
                api.get('/monthly-fiscal-summary/', {
                    params: {
                        site_name: siteName,
                        month: exportMonth || fltDataMonth,
                        year: exportYear || fltDataYear
                    }
                }),
                api.get('/nmcs-pmcs/', {
                    params: {
                        page_size: 10000, // Get all records
                        ...(siteName ? { site_name: siteName } : {})
                    }
                })
            ]);
            
            // Extract flight data entries, fiscal summary, and filter by selected site
            const allFlightDataEntries = flightDataEntriesResponse.data || [];
            const fiscalSummaryData = fiscalSummaryResponse.data && fiscalSummaryResponse.data.length > 0 ? fiscalSummaryResponse.data[0] : null;
            const flightDataEntries = siteName ? allFlightDataEntries.filter(entry => entry.site_name === siteName) : allFlightDataEntries;
            
            // Extract NMCS PMCS data
            const nmcsPmcsData = nmcsPmcsResponse.data?.results || nmcsPmcsResponse.data || [];
            
            // Helper function to normalize modex (convert "11" to "011" and vice versa)
            const normalizeModex = (modex) => {
                if (!modex || modex === 'N/A') return null;
                const modexStr = String(modex).trim();
                // Convert to number and back to string with zero-padding to 3 digits
                const num = parseInt(modexStr, 10);
                if (isNaN(num)) return null;
                return num.toString().padStart(3, '0');
            };
            
            // Create a Set of normalized modex values that have AK0 records
            const ak0ModexSet = new Set();
            nmcsPmcsData.forEach(record => {
                if (record.proj === 'AK0' && record.modex) {
                    const normalized = normalizeModex(record.modex);
                    if (normalized) {
                        ak0ModexSet.add(normalized);
                    }
                }
            });

            // Recalculate phase rows using freshly fetched dailyACTimeData
            const freshDailyACTimeData = dailyACTimeResponse || [];
            const filteredFreshDailyACTimeData = siteName ? freshDailyACTimeData.filter(item => item.site_name === siteName) : freshDailyACTimeData;

            // Rebuild phase rows with fresh data
            const freshPhaseLt100Rows = buildPhaseRows(filteredFreshDailyACTimeData, (h) => h <= 40 && h >= 0);
            const freshPhaseGt100Rows = buildPhaseRows(filteredFreshDailyACTimeData, (h) => h < 0);

            setAlert({ show: true, message: 'Generating Excel file...', type: 'info' });
            const workbook = new ExcelJS.Workbook();
            
            // Helper function to apply borders and centering to a sheet
            const applyStyling = (sheet, fontOptions = null) => {
                sheet.eachRow((row, rowNumber) => {
                    row.eachCell((cell, colNumber) => {
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                        // Apply font if specified
                        if (fontOptions) {
                            cell.font = { ...cell.font, ...fontOptions };
                        }
                        // Center headers and important rows (but not Date/Time rows)
                        if (rowNumber <= 3) {
                            cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        }
                    });
                });
            };

            // Cover Page Sheet — match CDRL A001 N00421-24-F-0543 layout
            const selectedSiteCode = selectedSite
                ? (sites.find((s) => s.id === parseInt(selectedSite, 10))?.name || '')
                : '';
            const coverInfo = getCoverInfoForSite(selectedSiteCode);
            await addDsrCoverPage(workbook, {
                siteLocation: coverInfo.location,
                contractorAddress: coverInfo.address,
            });

            // DSR Sheet — capture MATCON snapshot for trend history (08:30 DSR)
            try {
                await api.post('/matcon-snapshots/capture/', {
                    source: 'dsr_export',
                    site: selectedSite ? parseInt(selectedSite, 10) : null,
                });
            } catch (snapshotErr) {
                console.warn('MATCON snapshot capture failed:', snapshotErr);
            }

            const dsrSheet = workbook.addWorksheet('DSR');
            const dsrData = prepareDSRData(freshPhaseLt100Rows, freshPhaseGt100Rows);

            // Example usage with the date 10/8/2025
            const myDate = new Date(DSR_DATA.date);
            const jdate = convertDateToSerialNumber(myDate);
            
            // Add DSR header (dynamic by site)
            const wingTitle = selectedSiteCode
                ? `${selectedSiteCode} DAILY STATUS REPORT`
                : 'DAILY STATUS REPORT';
            const TMS = aircraftType || '';
            
            dsrSheet.addRow([wingTitle]);
            dsrSheet.addRow([`Contract Number: ${DSR_DATA.contract_number}`]);
            dsrSheet.addRow([`CDRL Number: ${DSR_DATA.cdrl_number}`]);

            // Add Date row - set values in merged cells
            dsrSheet.addRow(['Date:', '', jdate, DSR_DATA.date, '','','Location:', DSR_DATA.location]);
            // Set values in the merged cells           
            // Add Time row - set values in merged cells  
            const timeRow = dsrSheet.addRow(['Time:', '', '0830', '', '','','Aircraft:', TMS]);
            // Set values in the merged cells
            //timeRow.getCell(1).value = `Time: 0700`;
            //timeRow.getCell(4).value = `Aircraft: ${DSR_DATA.aircraft}`;
            if (typeof timeRow.commit === 'function') timeRow.commit();
            dsrSheet.addRow([]);
            dsrSheet.addRow(['COMPILED BY:','','','','','','SUBMITTED BY:']); 
            dsrSheet.addRow([]);
            dsrSheet.addRow(['SECTION "A" - AIRCRAFT “MISSION CAPABLE” (MC) DATA']);
            dsrSheet.addRow(['1.','Number Assigned (Aircraft in Inventory) "AC Status":','','','','','','','', DSR_DATA.num_assigned]);
            dsrSheet.addRow(['2.','Number NMCRS (Out Of Reporting) Section "C":','','','','','','','', DSR_DATA.num_nmcrs]);
            dsrSheet.addRow(['3.','In Mission Capable Reporting Status (MCRS) "AC Status":','','','','','','','', DSR_DATA.num_mcrs]);
            dsrSheet.addRow(['4','Number Not Mission Capable, Maintenance (NMCM) Section "D":','','','','','','','',DSR_DATA.num_nmcm]);
            dsrSheet.addRow(['5','Number In Planned Schedule Maintenance (PM) Section "E":','','','','','','','',DSR_DATA.num_pm]);
            dsrSheet.addRow(['6','Number Not Mission Capable, Supply (NMCS) Section "F":','','','','','','','',DSR_DATA.num_nmcs]);
            dsrSheet.addRow(['7','Number Partial Mission Capable (Awaiting FCF) Section "G":','','','','','','','',DSR_DATA.num_pmc_fcf]);
            
            const dsrrow17 = dsrSheet.addRow(['8','Number Mission Capable (FMC/PMC Minus FCF):','','','','','','','','']);
            dsrrow17.getCell(10).value = { formula: '=J12-SUM(J13:J16)' };
            dsrSheet.addRow(['9A','Number Cross Country (XC /PREPOS) Section "H":','','','','','','','',DSR_DATA.num_xc]);
            dsrSheet.addRow(['9B','Number Detachment (DET) Section "H":','','','','','','','',DSR_DATA.num_det]);
            
            const dsrrow20 = dsrSheet.addRow(['10','Number of Ready For Production Aircraft at MOB','','','','','','','','']);
            dsrrow20.getCell(10).value = { formula: '=J17-J18' };
            dsrrow20.getCell(10).fill = { type: 'pattern', pattern: 'solid',fgColor: { argb: 'FFFFCC' }};
            
            dsrSheet.addRow(['11','Number of Aircraft in the Phase Window (< 100%) Section "I":','','','','','','','',freshPhaseLt100Rows.length]);
            dsrSheet.addRow(['12','Number of Aircraft in the Phase Window (> 100%) Section "I":','','','','','','','',freshPhaseGt100Rows.length]);  
            dsrSheet.addRow([]);                             
            dsrSheet.addRow(['SECTION "B"  - FLIGHT OPERATIONS DATA']);									
            dsrSheet.addRow(['PREVIOUS DAY’S DATA']);									
            const dsrrow26 = dsrSheet.addRow(['','DATE:','','','','','','','','']);	
            dsrrow26.getCell(3).value = { formula: `=VALUE(RIGHT(YEAR(D26),1)&"001")+(D26-DATE(YEAR(D26),1,1))` };
            dsrrow26.getCell(4).value = { formula: `='SCR-RFPA '!D2` };
            dsrrow26.getCell(4).numFmt = 'mm/dd/yyyy';
            dsrSheet.addRow(['','','','','','','','Scheduled','','Flown']);
            
            const dsrrow28 = dsrSheet.addRow(['1','Sorties','','','','','','','','']);
            dsrrow28.getCell(8).value = { formula: "='SCR-RFPA '!D12" };
            dsrrow28.getCell(10).value = { formula: "='SCR-RFPA '!D13" };
            
            const dsrrow29= dsrSheet.addRow(['2','Hours','','','','','','','','']);
            dsrrow29.getCell(8).value = { formula: "='SCR-RFPA '!D15" };
            dsrrow29.getCell(10).value = { formula: `=IFERROR(VLOOKUP(D26,'FLT DATA'!$A$5:$F$35,2,FALSE),0)` };
            
            const dsrrow30 = dsrSheet.addRow(['3','Sorties Added On','','','','','','','','8']);
            dsrrow30.getCell(10).value = { formula: "='SCR-RFPA '!G14" };
                        
            const dsrrow31 = dsrSheet.addRow(['4','Total Sorties Scheduled','','','','','','','','153']);
            dsrrow31.getCell(10).value = { formula: "=H28+J30" };
            
            dsrSheet.addRow(['FLIGHT DATA SUMMARY']);									
            
            const dsrrow32 = dsrSheet.addRow(['1','Total Hours Weekly:','','','','','','','','']);
            dsrrow32.getCell(10).value = { formula: `=SUMIFS('FLT DATA'!$K$5:$K$35,'FLT DATA'!$A$5:$A$35,"<=" &$D$26-WEEKDAY($D$26,2)+7,'FLT DATA'!$A$5:$A$35,">"& $D$26-WEEKDAY($D$26,2))` };
            dsrrow32.getCell(10).numFmt = '#,##0.0';
            const dsrrow33 = dsrSheet.addRow(['2','Total Hours Monthly:','','','','','','','','']);
            dsrrow33.getCell(10).value = { formula: `=SUM('FLT DATA'!$B$36)` };
            dsrrow33.getCell(10).numFmt = '#,##0.0';
            const dsrrow35 = dsrSheet.addRow(['3','Total Flights Flown Weekly:','','','','','','','','']);
            dsrrow35.getCell(10).value = { formula: `=SUMIFS('FLT DATA'!$L$5:$L$35,'FLT DATA'!$A$5:$A$35,"<=" &$D$26-WEEKDAY($D$26,2)+7,'FLT DATA'!$A$5:$A$35,">"& $D$26-WEEKDAY($D$26,2))` };
            dsrrow35.getCell(10).numFmt = '#,##0.0';
            const dsrrow36 = dsrSheet.addRow(['4','Total Flights Flown Monthly:','','','','','','','','']);
            dsrrow36.getCell(10).value = { formula: `=SUM('FLT DATA'!$F$36)` };
            dsrrow36.getCell(10).numFmt = '#,##0.0';
            const dsrrow37 = dsrSheet.addRow(['5','ACCIDENT FREE HOURS','','','','','','','','']);
            dsrrow37.getCell(10).value = { formula: `='FLT DATA'!$M$37` };
            dsrrow37.getCell(10).numFmt = '#,##0.0';
            const dsrrow38 = dsrSheet.addRow(['6','Det Hours Daily','','','','','','','','']);
            dsrrow38.getCell(10).value = { formula: `=SUMIFS('FLT DATA'!$I$5:$I$35,'FLT DATA'!$A$5:$A$35,"=" &$D$26)` };
            
            const dsrrow39 = dsrSheet.addRow(['7','Det Hours Weekly','','','','','','','','']);
            dsrrow39.getCell(10).value = { formula: `=SUMIFS('FLT DATA'!$I$5:$I$35,'FLT DATA'!$A$5:$A$35,"<=" &$D$26-WEEKDAY($D$26,2)+7,'FLT DATA'!$A$5:$A$35,">"& $D$26-WEEKDAY($D$26,2))` };
            
            const dsrrow40 = dsrSheet.addRow(['8','Det Hours Monthly','','','','','','','','']);
            dsrrow40.getCell(10).value = { formula: `=SUM('FLT DATA'!$I$36)` };
            
            const dsrrow41 = dsrSheet.addRow(['9','Det Flights Daily','','','','','','','','']);
            dsrrow41.getCell(10).value = { formula: `=SUMIFS('FLT DATA'!$H$5:$H$35,'FLT DATA'!$A$5:$A$35,"=" &$D$26)` };
            
            const dsrrow42 = dsrSheet.addRow(['10','Det Flights Weekly','','','','','','','','']);
            dsrrow42.getCell(10).value = { formula: `=SUMIFS('FLT DATA'!$H$5:$H$35,'FLT DATA'!$A$5:$A$35,"<=" &D$26-WEEKDAY(D$26,2)+7,'FLT DATA'!$A$5:$A$35,">"& D$26-WEEKDAY(D$26,2))` };
            
            const dsrrow43 = dsrSheet.addRow(['11','Det Flights Monthly','','','','','','','','']);
            dsrrow43.getCell(10).value = { formula: `=SUM('FLT DATA'!$H$36)` };
            
            dsrSheet.addRow(['','CANCELLATIONS','','','','','','','','']);								
        
            const dsrrow45 = dsrSheet.addRow(['12','Ops CNX','','','','','','','','']);
            dsrrow45.getCell(10).value = { formula: `='SCR-RFPA '!G19` };
            
            const dsrrow46 = dsrSheet.addRow(['13','Weather CNX','','','','','','','','']);
            dsrrow46.getCell(10).value = { formula: `='SCR-RFPA '!G18` };
            
            const dsrrow47 = dsrSheet.addRow(['14','ACNA/Maintenance CNX','','','','','','','','']);
            dsrrow47.getCell(10).value = { formula: `='SCR-RFPA '!G15` };
            
            const dsrrow48 = dsrSheet.addRow(['15','Aborts (A & C)','','','','','','','','']);
            dsrrow48.getCell(10).value = { formula: `='SCR-RFPA '!G16 + 'SCR-RFPA '!G17` };
            
            dsrSheet.addRow([]);
            

            // Add all DSR data with dynamic merging
            let currentRow = dsrSheet.lastRow.number;
            
            // Merge cells to match the desired format from the first image
            // Row 1: Title row - merge entire row
            dsrSheet.mergeCells('A1:J1');
            // Row 2: Contract number - merge entire row  
            dsrSheet.mergeCells('A2:J2');
            // Row 3: CDRL number - merge entire row
            dsrSheet.mergeCells('A3:J3');
            
            // Row 4: Date row - merge A4:B4, D4:F4, G4:H4, I4:J4
            dsrSheet.mergeCells('A4:B4'); // "Date:" and date_code
            dsrSheet.mergeCells('D4:F4'); // "Location:" and location
            dsrSheet.mergeCells('H4:J4'); // Site
            
            // Row 5: Time row - merge A5:B5, C5:F5, G5:H5, I5:J5
            dsrSheet.mergeCells('A5:B5'); // "Time:" and time
            dsrSheet.mergeCells('D5:F5'); // Empty cells between time and aircraft
            dsrSheet.mergeCells('H5:J5'); // "Aircraft:" and aircraft
            // Row 6: Empty row - no merge needed
            dsrSheet.mergeCells('A6:J6');
            // Row 7: COMPILED BY row - merge entire row
            dsrSheet.mergeCells('A7:F7');
            dsrSheet.mergeCells('G7:J7');
            // Row 8: Empty row - merge needed make it look clean
            dsrSheet.mergeCells('A8:J8');
            // Row 9: Section A header - merge entire row
            dsrSheet.mergeCells('A9:J9');
            dsrSheet.mergeCells('B10:I10');
            dsrSheet.mergeCells('B11:I11');
            dsrSheet.mergeCells('B12:I12');
            dsrSheet.mergeCells('B13:I13');
            dsrSheet.mergeCells('B14:I14');
            dsrSheet.mergeCells('B15:I15');
            dsrSheet.mergeCells('B16:I16');
            dsrSheet.mergeCells('B17:I17');
            dsrSheet.mergeCells('B18:I18');
            dsrSheet.mergeCells('B19:I19');
            dsrSheet.mergeCells('B20:I20');
            dsrSheet.mergeCells('B21:I21');
            dsrSheet.mergeCells('B22:I22');
            dsrSheet.mergeCells('A23:J23');
            dsrSheet.mergeCells('A24:J24');
            dsrSheet.mergeCells('A25:J25');
            dsrSheet.mergeCells('D26:G26'); dsrSheet.mergeCells('H26:I26');
            dsrSheet.mergeCells('B27:G27'); dsrSheet.mergeCells('H27:I27');
            dsrSheet.mergeCells('B28:G28'); dsrSheet.mergeCells('H28:I28');
            dsrSheet.mergeCells('B29:G29'); dsrSheet.mergeCells('H29:I29');
            dsrSheet.mergeCells('B30:G30'); dsrSheet.mergeCells('H30:I30');
            dsrSheet.mergeCells('B31:G31'); dsrSheet.mergeCells('H31:I31');
            dsrSheet.mergeCells('A32:J32');
            dsrSheet.mergeCells('B33:I33');
            dsrSheet.mergeCells('B34:I34');
            dsrSheet.mergeCells('B35:I35');
            dsrSheet.mergeCells('B36:I36');
            dsrSheet.mergeCells('B37:I37');
            dsrSheet.mergeCells('B38:I38');
            dsrSheet.mergeCells('B39:I39');
            dsrSheet.mergeCells('B40:I40');
            dsrSheet.mergeCells('B41:I41');
            dsrSheet.mergeCells('B42:I42');
            dsrSheet.mergeCells('B43:I43');
            dsrSheet.mergeCells('B44:I44');
            dsrSheet.mergeCells('B45:I45');
            dsrSheet.mergeCells('B46:I46');
            dsrSheet.mergeCells('B47:I47');
            dsrSheet.mergeCells('B48:I48');
            dsrSheet.mergeCells('A49:J49');

            // Process DSR data and merge cells dynamically (alignment only, fonts applied later)
            let remarksRowsToMerge = 1;
            dsrData.forEach((row, index) => {
                const addedRow = dsrSheet.addRow(row);
                currentRow = addedRow.number;

                // Check if this is a section header row (contains "Section" or "SECTION")
                if (row[0] && row[0].toString().toLowerCase().includes('section')) {
                    // Merge section header across columns A-J
                    dsrSheet.mergeCells(`A${currentRow}:J${currentRow}`);
                    addedRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
                }
                // Check if this is a data header row (contains "BUNO")
                else if (row[1] && row[1].toString().includes('BUNO')) {
                    // Apply alignment only
                    addedRow.eachCell(cell => {
                        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                    });
                }
                // Check if this is a data row (has BUNO data)
                else if (row[1] && row[1].toString().length > 0 && !row[1].toString().includes('BUNO')) {
                    // Apply alignment to STATUS CODE columns (B and G)
                    const statusCellLeft = addedRow.getCell(2); // Column B
                    const statusCellRight = addedRow.getCell(7); // Column G

                    if (statusCellLeft.value) {
                        statusCellLeft.alignment = { vertical: 'top', wrapText: true };
                    }
                    if (statusCellRight.value) {
                        statusCellRight.alignment = { vertical: 'top', wrapText: true };
                    }

                    // Center BUNO, LFD, and NDF columns
                    // Left side: IDX (column 1), BUNO (column 2), LFD (column 4), NDF (column 5)
                    const idxLeft = addedRow.getCell(1);
                    const bunoLeft = addedRow.getCell(2);
                    const lfdLeft = addedRow.getCell(4);
                    const ndfLeft = addedRow.getCell(5);

                    if (idxLeft.value) {
                        idxLeft.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    if (bunoLeft.value) {
                        bunoLeft.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    if (lfdLeft.value) {
                        lfdLeft.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    if (ndfLeft.value) {
                        ndfLeft.alignment = { horizontal: 'center', vertical: 'middle' };
                    }

                    // Right side: IDX (column 6), BUNO (column 7), LFD (column 9), NDF (column 10)
                    const idxRight = addedRow.getCell(6);
                    const bunoRight = addedRow.getCell(7);
                    const lfdRight = addedRow.getCell(9);
                    const ndfRight = addedRow.getCell(10);

                    if (idxRight.value) {
                        idxRight.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    if (bunoRight.value) {
                        bunoRight.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    if (lfdRight.value) {
                        lfdRight.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                    if (ndfRight.value) {
                        ndfRight.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                }
                // Merge three remarks rows after CORRECTIONS/REMARKS header
                else if (row[0] && row[0].toString().toLowerCase().includes('corrections/remarks')) {
                    // next three rows in dsrData are remarks1..remarks3
                    remarksRowsToMerge = 4;
                } else if (remarksRowsToMerge > 0) {
                    dsrSheet.mergeCells(`A${currentRow}:J${currentRow}`);
                    dsrSheet.getCell(`A${currentRow}`).fill = { type: 'pattern', pattern: 'solid',fgColor: { argb: 'FFFFCC' }};
                    remarksRowsToMerge -= 1;
                }
            });

            // Set column widths for DSR sheet
            dsrSheet.getColumn(1).width = 7; // BUNO
            dsrSheet.getColumn(2).width = 8; // STATUS CODE (left)
            dsrSheet.getColumn(3).width = 45; // LFD (left)
            dsrSheet.getColumn(4).width = 10; // NDF (left)
            dsrSheet.getColumn(5).width = 10; // Empty separator
            dsrSheet.getColumn(6).width = 7; // BUNO (right)
            dsrSheet.getColumn(7).width = 12; // STATUS CODE (right)
            dsrSheet.getColumn(8).width = 43; // LFD (right)
            dsrSheet.getColumn(9).width = 11; // NDF (right)
            dsrSheet.getColumn(10).width = 12; // Empty

            // CONSOLIDATED FONT STYLING - Single pass for all DSR sheet styling
            // This ensures consistent behavior across all environments
            dsrSheet.eachRow((row, rowNumber) => {
                // Default font for all rows
                const defaultFont = { name: 'Times New Roman', bold: true };

                // Rows 1-3: Title rows (size 14)
                if (rowNumber >= 1 && rowNumber <= 3) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 14 };
                    });
                }
                // Row 7: COMPILED BY/SUBMITTED BY (size 12)
                else if (rowNumber === 7) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12 };
                    });
                }
                // Row 9: Section A header (size 12, underline)
                else if (rowNumber === 9) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12, underline: true };
                    });
                }
                // Rows 10-23: Section A content (size 12)
                else if (rowNumber >= 10 && rowNumber <= 23) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12 };
                    });
                }
                // Row 24: Section B header (size 12, underline)
                else if (rowNumber === 24) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12, underline: true };
                    });
                }
                // Row 25: Previous day's data (size 12, underline)
                else if (rowNumber === 25) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12, underline: true };
                    });
                }
                // Rows 26-31: Flight operations data (size 12)
                else if (rowNumber >= 26 && rowNumber <= 31) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12 };
                    });
                }
                // Row 32: Flight data summary header (size 12)
                else if (rowNumber === 32) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12 };
                    });
                }
                // Rows 33-43: Flight data summary content (size 12)
                else if (rowNumber >= 33 && rowNumber <= 43) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12 };
                    });
                }
                // Row 44: Cancellations header (size 12)
                else if (rowNumber === 44) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12 };
                    });
                }
                // Rows 45-49: Cancellations content (size 12)
                else if (rowNumber >= 45 && rowNumber <= 49) {
                    row.eachCell(cell => {
                        cell.font = { ...defaultFont, size: 12 };
                    });
                }
                // Rows 50+: Dynamic DSR data (size 10)
                else if (rowNumber >= 50) {
                    const firstCellValue = row.getCell(1).value;
                    // Check if this is a section header (Sections C-I)
                    if (firstCellValue && firstCellValue.toString().toLowerCase().includes('section')) {
                        const sectionText = firstCellValue.toString();
                        // Sections C through I get size 12
                        if (sectionText.includes('"C"') || sectionText.includes('"D"') ||
                            sectionText.includes('"E"') || sectionText.includes('"F"') ||
                            sectionText.includes('"G"') || sectionText.includes('"H"') ||
                            sectionText.includes('"I"')) {
                            row.eachCell(cell => {
                                cell.font = { ...defaultFont, size: 12, underline: true };
                            });
                        } else {
                            // Other sections get size 10
                            row.eachCell(cell => {
                                cell.font = { ...defaultFont, size: 10, underline: true };
                            });
                        }
                    }
                    // Data header rows (contains "BUNO")
                    else if (row.getCell(1).value && row.getCell(1).value.toString().includes('BUNO')) {
                        row.eachCell(cell => {
                            cell.font = { ...defaultFont, size: 10 };
                        });
                    }
                    // Regular data rows
                    else {
                        row.eachCell(cell => {
                            cell.font = { ...defaultFont, size: 10 };
                        });
                    }
                }
            });

            // Apply specific cell fonts that need special formatting (italic)
            dsrSheet.getCell('D4').font = { name: 'Times New Roman', size: 12, italic: true, bold: true };
            dsrSheet.getCell('C4').font = { name: 'Times New Roman', size: 12, bold: true };
            dsrSheet.getCell('C5').font = { name: 'Times New Roman', size: 12, italic: true, bold: true };
            dsrSheet.getCell('G4').font = { name: 'Times New Roman', size: 12, bold: true };
            dsrSheet.getCell('G5').font = { name: 'Times New Roman', size: 12, bold: true };
            dsrSheet.getCell('H4').font = { name: 'Times New Roman', size: 12, bold: true };
            dsrSheet.getCell('H5').font = { name: 'Times New Roman', size: 12, bold: true };
            dsrSheet.getCell('C26').font = { name: 'Times New Roman', size: 12, bold: true };
            dsrSheet.getCell('D26').font = { name: 'Times New Roman', size: 12, italic: true, bold: true };

            // Apply alignments that weren't set during data insertion
            dsrSheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getRow(2).alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getRow(3).alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getRow(9).alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getRow(24).alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getRow(25).alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getRow(32).alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getRow(44).alignment = { horizontal: 'center', vertical: 'middle' };

            dsrSheet.getCell('C4').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('D4').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('C5').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('H4').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('H5').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('C26').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('D26').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('H27').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('H28').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('H29').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('J27').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('J28').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('J29').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('J30').alignment = { horizontal: 'center', vertical: 'middle' };
            dsrSheet.getCell('J31').alignment = { horizontal: 'center', vertical: 'middle' };

            // Center column A for specific row ranges
            // A10-A22: Section A content
            for (let row = 10; row <= 22; row++) {
                dsrSheet.getCell(`A${row}`).alignment = { horizontal: 'center', vertical: 'middle' };
            }
            // A28-A31: Flight operations data
            for (let row = 28; row <= 31; row++) {
                dsrSheet.getCell(`A${row}`).alignment = { horizontal: 'center', vertical: 'middle' };
            }
            // A33-A43: Flight data summary content
            for (let row = 33; row <= 43; row++) {
                dsrSheet.getCell(`A${row}`).alignment = { horizontal: 'center', vertical: 'middle' };
            }
            // A45-A48: Cancellations content
            for (let row = 45; row <= 48; row++) {
                dsrSheet.getCell(`A${row}`).alignment = { horizontal: 'center', vertical: 'middle' };
            }

            // Apply borders to all cells
            applyStyling(dsrSheet);
            
            

            // AC Status Sheet
            const acStatusSheet = workbook.addWorksheet('AC Status');
            const acStatusToday = new Date();
            const formattedDate = acStatusToday.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
            acStatusSheet.addRow([formattedDate]);
            acStatusSheet.addRow(['BUNO', 'Modex', 'Status 1', 'Status 2', 'Reason', 'Last Fly Date', '30 Day NO FLY','','Status 1','Status 2','Description']);
            
            // Add static status legend/key in rows 3-15, columns K-M
            const statusLegend = [           
                ['FMC', 'UP', 'FMC UP Aircraft at MOB'],
                ['FMC', 'XC', 'FMC Up Aircraft on Cross Country'],
                ['FMC', 'FCF', 'Aircraft Pending FCF'],
                ['FMC', 'PMC', 'PMC UP Aircraft at MOB'],
                ['DET', 'DET', 'Aircraft on Detachment'],
                ['NMCM', 'PM', 'Down in Unscheduled Maintenance'],
                ['NMCM', 'DN', 'Down Supply Awaiting Parts but Not Exempt'],
                ['NMCS', 'DS', 'Down Supply Awaiting Parts but Not Exempt'],
                ['ACO', 'AWP', 'Down Awaiting Parts with ACO Exemption'],
                ['ACO', 'OTH', 'ACO Exempt Aircraft but not AWP or on DET'],
                ['ACO', 'REI/TEI', 'Aircraft Awaiting REI/TEI FST'],
                ['ACO', 'ACI', 'Aircraft Conditional Inspection']
            ];
            
            statusLegend.forEach((row, index) => {
                const rowNumber = index + 3; // Start at row 3
                acStatusSheet.getCell(`K${rowNumber}`).value = row[0]; // Status 1
                acStatusSheet.getCell(`L${rowNumber}`).value = row[1]; // Status 2
                acStatusSheet.getCell(`M${rowNumber}`).value = row[2]; // Description
            });
            
            
            // Write aircraft rows starting at row 3 so legend (K-M) lines up without gaps
            let writeRowIndex = 3;
            const writeAircraftRow = (item) => {
                const buno = item.buno || 'N/A';
                const modex = buno !== 'N/A' ? (getAircraftModex(item) || 'N/A') : 'N/A';
                const lastFlyDateStr = item.lfd || item.last_fly_date || '';
                const lastFlyDate = lastFlyDateStr ? new Date(lastFlyDateStr) : null;
                const thirtyDayNoFly = lastFlyDate ? (() => {
                    const todayCalc = new Date();
                    const daysDiff = Math.floor((todayCalc - lastFlyDate) / (1000 * 60 * 60 * 24));
                    return daysDiff >= 30 ? 'X' : '';
                })() : '';

                // Check if this aircraft has an AK0 record
                const normalizedModex = normalizeModex(modex);
                const hasAK0 = normalizedModex && ak0ModexSet.has(normalizedModex);
                const ak0NmcsValue = hasAK0 ? 'X' : '';

                const row = acStatusSheet.getRow(writeRowIndex);
                row.getCell(1).value = buno;
                row.getCell(2).value = modex;
                row.getCell(3).value = getStatus1(item.status1 || item.status || 'N/A');
                // Convert REI to REI/TEI for Status 2
                const status2Value = item.status2 || item.status || 'N/A';
                row.getCell(4).value = status2Value === 'REI' ? 'REI/TEI' : status2Value;
                row.getCell(5).value = item.reason || '';
                row.getCell(6).value = lastFlyDateStr || 'DET/ACPT';
                row.getCell(7).value = thirtyDayNoFly;
                row.getCell(8).value = getNDF(item.edd, item.ecd, item.status);
                row.getCell(9).value = ak0NmcsValue; // AK0/NMCS column
                if (typeof row.commit === 'function') row.commit();
                writeRowIndex += 1;
            };

            if (filteredAircraftStatusData.length === 0) {
                if (aircraftStatusData.length === 0) {
                    acStatusSheet.getCell('A3').value = 'No aircraft data available';
                } else {
                    aircraftStatusData.forEach(writeAircraftRow);
                }
            } else {
                filteredAircraftStatusData.forEach(writeAircraftRow);
            }
            
            // Style AC Status sheet
            acStatusSheet.getRow(1).font = { bold: true, size: 14 };
            acStatusSheet.getRow(2).font = { bold: true, size: 12 };
            
            // Set column widths for AC Status sheet
            acStatusSheet.getColumn(1).width = 7.86; // BUNO
            acStatusSheet.getColumn(2).width = 7.86; // Modex
            acStatusSheet.getColumn(3).width = 7.86; // Status 1
            acStatusSheet.getColumn(4).width = 7.86; // Status 2
            acStatusSheet.getColumn(5).width = 60.71; // Reason
            acStatusSheet.getColumn(6).width = 15.41; // Last Fly Date
            acStatusSheet.getColumn(7).width = 7.71; // 30 Day NO FLY
            acStatusSheet.getColumn(8).width = 10; // NDF
            acStatusSheet.getColumn(9).width = 10; // AK0/NMCS
            acStatusSheet.getColumn(13).width = 44.86;
            
            // Set date format for Last Fly Date column (Column F)
            acStatusSheet.getColumn(6).numFmt = 'dd-mmm-yy';
            
            applyStyling(acStatusSheet);
            
            // Set word wrap for headers and Reason column, and rotate Status 1 and Status 2 headers
            acStatusSheet.getRow(2).eachCell((cell, colNumber) => {
                if (colNumber === 3 || colNumber === 4) {
                    // Rotate Status 1 and Status 2 headers 90 degrees
                    cell.alignment = { horizontal: 'center', vertical: 'middle', textRotation: 90 };
                } else {
                    // Other headers with word wrap
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                }
            });
            
            // Merge A1-H1 and set left alignment
            acStatusSheet.mergeCells('A1:H1');
            acStatusSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };
            
            // Add auto-filter to the header row
            acStatusSheet.autoFilter = {
                from: 'A2',
                to: 'I2'
            };
            
            // Freeze rows 1 and 2
            acStatusSheet.views = [
                { state: 'frozen', ySplit: 2 }
            ];
            
            // Style BUNO and Reason columns with light yellow background and center all except Reason
            acStatusSheet.eachRow((row, rowNumber) => {
                if (rowNumber > 2) { // Skip header rows
                    // Apply light yellow background to BUNO and Reason columns
                    const bunoCell = row.getCell(1); // BUNO is column A (1st column)
                    const status2Cell = row.getCell(4); // Status 2 is column D (4th column)
                    const reasonCell = row.getCell(5); // Reason is column E (5th column)
                    const keystatus1 = row.getCell(11); // Status 1 is column K (11th column)
                    const keystatus2 = row.getCell(12); // Status 2 is column L (12th column)

                    bunoCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFCC' }
                    };
                    
                    status2Cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFCC' }
                    };
                    
                    reasonCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFCC' }
                    };
                    
                    // Center all columns except Reason (column 5) and make specific columns bold
                    row.eachCell((cell, colNumber) => {
                        if (colNumber !== 5) { // Skip Reason column
                            cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        } else {
                            // Reason column: left align with word wrap
                            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                        }
                        
                        // Make specific columns bold: Modex(2), Status 1(3), Status 2(4), Last Fly Date(6), 30 Day NO FLY(7), NDF(8), AK0/NMCS(9)
                        if ([2, 3, 4, 6, 7, 8, 9, 11, 12].includes(colNumber)) {
                            cell.font = { bold: true };
                        }
                    });
                    
                    // Apply conditional colors to Status 1 and Status 2 columns
                    const status1Cell = row.getCell(3); // Status 1 is column C (3rd column)
                    
                    // Status 1 conditional text colors
                    if (status1Cell.value === 'ACO') {
                        status1Cell.font = { ...status1Cell.font, color: { argb: 'FFC000' } }; // Orange text
                    } else if (status1Cell.value === 'FMC') {
                        status1Cell.font = { ...status1Cell.font, color: { argb: '00B050' } }; // Green text
                    } else if (status1Cell.value === 'NMCM') {
                        status1Cell.font = { ...status1Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status1Cell.value === 'NMCS') {
                        status1Cell.font = { ...status1Cell.font, color: { argb: 'C00000' } }; // Dark red text
                    } else if (status1Cell.value === 'PM') {
                        status1Cell.font = { ...status1Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status1Cell.value === 'DET') {
                        status1Cell.font = { ...status1Cell.font, color: { argb: '161616' } }; // Dark gray text
                    }
                    
                    // Status 2 conditional text colors
                    if (status2Cell.value === 'UP') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: '00B050' } }; // Green text
                    } else if (status2Cell.value === 'XC') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: '538DD5' } }; // Blue text
                    } else if (status2Cell.value === 'FCF') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: '538DD5' } }; // Blue text
                    } else if (status2Cell.value === 'DET') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: '1D1B10' } }; // Dark brown text
                    } else if (status2Cell.value === 'PM') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status2Cell.value === 'DN') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status2Cell.value === 'DS') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status2Cell.value === 'AWP') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: '7030A0' } }; // Purple text
                    } else if (status2Cell.value === 'OTH') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status2Cell.value === '107') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status2Cell.value === 'REI' || status2Cell.value === 'REI/TEI') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status2Cell.value === 'AIMD') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (status2Cell.value === 'ACI') {
                        status2Cell.font = { ...status2Cell.font, color: { argb: 'FF0000' } }; // Red text
                    }

                    if (keystatus1.value === 'ACO') {
                        keystatus1.font = { ...keystatus1.font, color: { argb: 'FFC000' } }; // Orange text
                    } else if (keystatus1.value === 'FMC') {
                        keystatus1.font = { ...keystatus1.font, color: { argb: '00B050' } }; // Green text
                    } else if (keystatus1.value === 'NMCM') {
                        keystatus1.font = { ...keystatus1.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus1.value === 'NMCS') {
                        keystatus1.font = { ...keystatus1.font, color: { argb: 'C00000' } }; // Dark red text
                    } else if (keystatus1.value === 'PM') {
                        keystatus1.font = { ...keystatus1.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus1.value === 'DET') {
                        keystatus1.font = { ...keystatus1.font, color: { argb: '161616' } }; // Dark gray text
                    }

                    if (keystatus2.value === 'UP') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: '00B050' } }; // Green text
                    } else if (keystatus2.value === 'XC') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: '538DD5' } }; // Blue text
                    } else if (keystatus2.value === 'FCF') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: '538DD5' } }; // Blue text
                    } else if (keystatus2.value === 'DET') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: '1D1B10' } }; // Dark brown text
                    } else if (keystatus2.value === 'PM') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus2.value === 'DN') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus2.value === 'DS') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus2.value === 'AWP') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: '7030A0' } }; // Purple text
                    } else if (keystatus2.value === 'OTH') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus2.value === '107') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus2.value === 'REI' || keystatus2.value === 'REI/TEI') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus2.value === 'AIMD') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    } else if (keystatus2.value === 'ACI') {
                        keystatus2.font = { ...keystatus2.font, color: { argb: 'FF0000' } }; // Red text
                    }
                }
            });

            // FLT Data Sheet
            const fltDataSheet = workbook.addWorksheet('FLT Data');
            
            // Set column widths
            fltDataSheet.columns = [
                { width: 18 }, // A - Previous Months/Date
                { width: 12 }, // B - Daily Flight Hours
                { width: 16 }, // C - Events Scheduled (exclude FCF)
                { width: 16 }, // D - Weather aborts, ops cancels
                { width: 16 }, // E - Total Scheduled
                { width: 16 }, // F - Events Flown
                { width: 12 }, // G - Weekend Flt. Hrs.
                { width: 12 }, // H - Detachment Sorties Flwn
                { width: 12 }, // I - Detachment Hrs.
                { width: 18 }, // J - NOTES
                { width: 7}, // K - Weekly Hours
                { width: 7 }, // L - Weekly Sorties
                { width: 10 }  // M - Accident Free
            ];

            // Header rows (Rows 1-3)
            // Row 1: Previous Months Fiscal Total
            const row1 = fltDataSheet.addRow(['', '', '', '', '', '', '', '', '', '', 'Weekly', '', 'Accident Hours']);
            row1.font = { name: 'Times New Roman', size: 10, bold: true };
            row1.getCell(13).alignment = { vertical: 'top', wrapText: true };
        
            //Row 2: Previous Months Fiscal Total
            const row2 = fltDataSheet.addRow([
                'Previous Months Fiscal Total',
                fiscalSummaryData?.previous_months_fiscal_total || 0,
                '', '',
                fiscalSummaryData?.previous_months_scheduled || 0,
                fiscalSummaryData?.previous_month_flown || 0,
                '', '', '', '',
                fiscalSummaryData?.weekly_hours || '',
                '',
                fiscalSummaryData?.accident_hours || ''
            ]);
            row2.font = { name: 'Times New Roman', size: 10, bold: true };
            row2.getCell(1).alignment = { vertical: 'top', wrapText: true };

            row2.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
            row2.getCell(6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
            row2.getCell(11).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
            row2.getCell(12).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
            row2.getCell(13).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow

            // Row 3: Fiscal Total with values
            const row3 = fltDataSheet.addRow([
                'Previous Quaterly Fiscal Total',
                fiscalSummaryData?.previous_quarterly_fiscal_total || 0,
                '', '',  
                fiscalSummaryData?.previous_quarterly_scheduled || 0,
                fiscalSummaryData?.previous_quarterly_flown || 0,
                '', '', '', '',
                fiscalSummaryData?.quarterly_weekly_hours || '',
                '',
                fiscalSummaryData?.quarterly_accident_hours || 0
            ]);
            row3.font = { name: 'Times New Roman', size: 10, bold: true };
            row3.getCell(1).alignment = { vertical: 'top', wrapText: true };
            row3.getCell(11).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
            row3.getCell(12).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
            row3.getCell(13).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
            
            // Main table headers (Row 4)
            const headers = [
                'Date',
                'Daily Flight Hours',
                'Events Scheduled (exclude FCF)',
                'Weather aborts, ops cancels',
                'Total Scheduled',
                'Events Flown',
                'Weekend Flt. Hrs.',
                'Detachment Sorties Flwn',
                'Detachment Hrs.',
                'NOTES',
                'Weekly Hours',
                'Weekly Sorties',
                'Accident Free'
            ];
            const headerRow = fltDataSheet.addRow(headers);
            headerRow.font = { name: 'Times New Roman', size: 10, bold: true };
            headerRow.eachCell((cell) => {
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            });

            // Generate month days for specified month/year or current month
            const currentDate = new Date();
            const currentMonth = exportMonth || fltDataMonth || currentDate.getMonth() + 1; // Use export parameter or FLT Data tab month or current month
            const currentYear = exportYear || fltDataYear || currentDate.getFullYear(); // Use export parameter or FLT Data tab year or current year
            
            
            // Ensure month and year are valid numbers
            const validMonth = Math.max(1, Math.min(12, parseInt(currentMonth) || currentDate.getMonth() + 1));
            const validYear = parseInt(currentYear) || currentDate.getFullYear();
            

            const generateMonthDays = (year, month) => {
                const daysInMonth = new Date(year, month, 0).getDate();
                const days = [];
                for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(year, month - 1, day);
                    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
                    days.push({
                        date: date.toISOString().split('T')[0],
                        dayOfWeek: dayOfWeek,
                        dayNumber: day,
                        month: month,
                        year: year.toString().slice(-2),
                        isWeekend: date.getDay() === 0 || date.getDay() === 6,
                        isSunday: date.getDay() === 0
                    });
                }
                return days;
            };

            const monthDays = generateMonthDays(validYear, validMonth);

            // Calculate daily flight hours from flight hour monthly data
            const calculateDailyFlightHours = (date) => {
                // Get calculated value from Flight Hour Monthly data
                let calculatedValue = 0;
                if (filteredFlightHourData && filteredFlightHourData.length > 0) {
                    const dateStr = date.split('T')[0];
                    calculatedValue = filteredFlightHourData
                        .filter(item => {
                            if (item.create_dt) {
                                const itemDate = new Date(item.create_dt).toISOString().split('T')[0];
                                return itemDate === dateStr;
                            }
                            return false;
                        })
                        .reduce((sum, item) => sum + (parseFloat(item.tmr_hrs) || 0), 0);
                }
                
                // Get manual entry from FlightDataEntry
                const flightDataEntry = getFlightDataEntry(date);
                const manualValue = flightDataEntry?.daily_flight_hours;
                
                // Use hybrid logic: take the higher of calculated vs manual
                if (manualValue !== null && manualValue !== undefined) {
                    return Math.max(calculatedValue, parseFloat(manualValue) || 0);
                }
                
                // Otherwise use calculated value
                return calculatedValue;
            };

            // Get flight data entry for a specific date
            const getFlightDataEntry = (date) => {
                // Find flight data entry for the specific date
                return flightDataEntries.find(entry => entry.date === date);
            };

            // Calculate weekly totals
            const calculateWeeklyTotals = () => {
                const weeklyTotals = [];
                let weeklyHours = 0;
                let weeklySorties = 0;
                const lastDayIndex = monthDays.length - 1;

                monthDays.forEach((day, index) => {
                    // Add to weekly totals
                    weeklyHours += calculateDailyFlightHours(day.date);
                    const entry = getFlightDataEntry(day.date);
                    if (entry?.events_flown) {
                        weeklySorties += entry.events_flown;
                    }

                    // If it's a Sunday and not the last day, close the week
                    if (day.dayOfWeek === 'Sun' && index !== lastDayIndex) {
                        weeklyTotals.push({
                            weekEndDate: day.date,
                            weeklyHours: weeklyHours,
                            weeklySorties: weeklySorties
                        });
                        
                        // Reset for next week
                        weeklyHours = 0;
                        weeklySorties = 0;
                    }
                    // If it's the last day of the month
                    else if (index === lastDayIndex) {
                        // If the last day is not a Sunday, calculate from the Monday of that week
                        if (day.dayOfWeek !== 'Sun') {
                            // Find the Monday of that week by going back
                            const dateObj = new Date(day.date.split('T')[0] + 'T00:00:00');
                            const dayOfWeek = dateObj.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                            
                            // Calculate how many days to go back to reach Monday (the start of the work week)
                            // Monday = 1, so we need to calculate days back to Monday
                            // If Monday (1), go back 0 days
                            // If Tuesday (2), go back 1 day
                            // If Wednesday (3), go back 2 days
                            // If Thursday (4), go back 3 days
                            // If Friday (5), go back 4 days
                            // If Saturday (6), go back 5 days
                            // If Sunday (0), we don't reach here, but would need to go back 6 days
                            const daysBackToMonday = dayOfWeek === 0 ? 6 : (dayOfWeek - 1);
                            const mondayIndex = Math.max(0, index - daysBackToMonday);
                            
                            // Recalculate from Monday to last day (inclusive)
                            let lastWeekHours = 0;
                            let lastWeekSorties = 0;
                            
                            for (let i = mondayIndex; i <= index; i++) {
                                lastWeekHours += calculateDailyFlightHours(monthDays[i].date);
                                const lastWeekEntry = getFlightDataEntry(monthDays[i].date);
                                if (lastWeekEntry?.events_flown) {
                                    lastWeekSorties += lastWeekEntry.events_flown;
                                }
                            }
                            
                            weeklyTotals.push({
                                weekEndDate: day.date,
                                weeklyHours: lastWeekHours,
                                weeklySorties: lastWeekSorties
                            });
                        } else {
                            // Last day is a Sunday, use current totals
                            weeklyTotals.push({
                                weekEndDate: day.date,
                                weeklyHours: weeklyHours,
                                weeklySorties: weeklySorties
                            });
                        }
                    }
                });

                return weeklyTotals;
            };

            const weeklyTotals = calculateWeeklyTotals();

            // Calculate the actual number of days in the month (handles leap years automatically)
            const daysInMonth = new Date(validYear, validMonth, 0).getDate();

            // Add daily data rows - always generate 31 rows
            for (let dayNumber = 1; dayNumber <= 31; dayNumber++) {
                // Check if this day exists in the month
                if (dayNumber > daysInMonth) {
                    // Add a blank row for days that don't exist
                    fltDataSheet.addRow([
                        null, null, null, null, null, null, null, null, null, null, null, null, null
                    ]);
                    continue;
                }

                // Day exists, find it in monthDays array
                const day = monthDays.find(d => d.dayNumber === dayNumber);
                if (!day) {
                    // Should not happen, but add blank row just in case
                    fltDataSheet.addRow([
                        null, null, null, null, null, null, null, null, null, null, null, null, null
                    ]);
                    continue;
                }

                const entry = getFlightDataEntry(day.date);
                const dailyFlightHours = calculateDailyFlightHours(day.date);
                const eventsScheduled = entry?.events_scheduled || '';
                const weatherAborts = entry?.weather_aborts_ops_cancels || '';
                const totalScheduled = (eventsScheduled && weatherAborts) ? eventsScheduled - weatherAborts : (eventsScheduled || '');
                const eventsFlown = entry?.events_flown || '';

                // Calculate weekly totals for Sunday rows or last day of month
                const weeklyTotal = weeklyTotals.find(w => w.weekEndDate === day.date);
                const isLastDayOfMonth = dayNumber === daysInMonth;
                const showWeeklyTotals = (day.isSunday || isLastDayOfMonth) && weeklyTotal;

                // Create date at midnight UTC and convert to Excel serial date number (date-only, no time)
                // Excel serial date: days since January 1, 1900 (Excel counts from 1, not 0)
                const jsDate = new Date(Date.UTC(validYear, day.month - 1, day.dayNumber, 0, 0, 0, 0));
                const excelEpoch = new Date(Date.UTC(1900, 0, 1, 0, 0, 0, 0)); // January 1, 1900
                const millisecondsPerDay = 24 * 60 * 60 * 1000;
                const daysDiff = Math.floor((jsDate - excelEpoch) / millisecondsPerDay);
                // Excel serial date starts at 1 for Jan 1, 1900, and Excel incorrectly treats 1900 as a leap year
                // For dates after Feb 28, 1900, we need to add 1 extra day
                const excelSerialDate = daysDiff + 1 + (jsDate >= new Date(Date.UTC(1900, 2, 1)) ? 1 : 0);

                const detachmentHrsValue = entry?.detachment_hrs || 0;
                const accidentFreeValue = day.isWeekend
                    ? 0
                    : ((dailyFlightHours + detachmentHrsValue) > 0 ? (dailyFlightHours + detachmentHrsValue) : null);

                const rowData = [
                    excelSerialDate,
                    day.isWeekend ? 0 : (dailyFlightHours > 0 ? dailyFlightHours : null),
                    eventsScheduled || null,
                    weatherAborts || null,
                    totalScheduled !== '' && totalScheduled !== null ? totalScheduled : null,
                    eventsFlown !== '' && eventsFlown !== null ? eventsFlown : null,
                    day.isWeekend ? (dailyFlightHours > 0 ? dailyFlightHours : 0) : (entry?.weekend_flt_hrs || null), // Weekend Flt. Hrs - show actual hours for weekends
                    entry?.detachment_sorties || null,
                    entry?.detachment_hrs || null,
                    day.isWeekend ? 'WEEKEND' : (entry?.notes || ''),
                    showWeeklyTotals ? weeklyTotal.weeklyHours : null,
                    showWeeklyTotals ? weeklyTotal.weeklySorties : null,
                    accidentFreeValue
                ];

                const row = fltDataSheet.addRow(rowData);

                // Apply date format to column A (Date column) - set value explicitly as Excel serial date
                const dateCell = row.getCell(1);
                dateCell.value = excelSerialDate;
                dateCell.numFmt = 'ddd, dd-mmm-yy';

                // Apply background color to specified columns (B, C, D, F, G, H, I, J)
                const columnsToColor = [2, 3, 4, 6, 7, 8, 9, 10]; // B=2, C=3, D=4, F=6, G=7, H=8, I=9, J=10
                columnsToColor.forEach(colNum => {
                    row.getCell(colNum).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } };
                });

                // Apply yellow background to weekly total cells on Sunday or last day of month
                if (showWeeklyTotals) {
                    row.getCell(11).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
                    row.getCell(12).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFCC' } }; // Yellow
                }
            }

            // Calculate monthly totals
            const calculateMonthlyTotals = () => {
                let dailyFlightHours = 0;
                let eventsScheduled = 0;
                let weatherAborts = 0;
                let eventsFlown = 0;
                let weekendFltHrs = 0;
                let detachmentSorties = 0;
                let detachmentHrs = 0;

                monthDays.forEach(day => {
                    dailyFlightHours += calculateDailyFlightHours(day.date);
                    const entry = getFlightDataEntry(day.date);
                    if (entry) {
                        eventsScheduled += entry.events_scheduled || 0;
                        weatherAborts += entry.weather_aborts_ops_cancels || 0;
                        eventsFlown += entry.events_flown || 0;
                        weekendFltHrs += entry.weekend_flt_hrs || 0;
                        detachmentSorties += entry.detachment_sorties || 0;
                        detachmentHrs += entry.detachment_hrs || 0;
                    }
                });

                return {
                    dailyFlightHours,
                    eventsScheduled,
                    weatherAborts,
                    totalScheduled: eventsScheduled - weatherAborts,
                    eventsFlown,
                    weekendFltHrs,
                    detachmentSorties,
                    detachmentHrs
                };
            };

            const monthlyTotals = calculateMonthlyTotals();

            // Monthly Total row
            const monthlyTotalRow = [
                'Monthly Total',
                monthlyTotals.dailyFlightHours > 0 ? monthlyTotals.dailyFlightHours : null,
                monthlyTotals.eventsScheduled > 0 ? monthlyTotals.eventsScheduled : null,
                monthlyTotals.weatherAborts > 0 ? monthlyTotals.weatherAborts : null,
                monthlyTotals.totalScheduled > 0 ? monthlyTotals.totalScheduled : null,
                monthlyTotals.eventsFlown > 0 ? monthlyTotals.eventsFlown : null,
                monthlyTotals.weekendFltHrs > 0 ? monthlyTotals.weekendFltHrs : null,
                monthlyTotals.detachmentSorties > 0 ? monthlyTotals.detachmentSorties : null,
                monthlyTotals.detachmentHrs > 0 ? monthlyTotals.detachmentHrs : null,
                null,
                null,
                null,
                monthlyTotals.dailyFlightHours + monthlyTotals.detachmentHrs > 0 ? monthlyTotals.dailyFlightHours + monthlyTotals.detachmentHrs: null
            ];
            const monthlyRow = fltDataSheet.addRow(monthlyTotalRow);
            monthlyRow.font = { name: 'Times New Roman', size: 10, bold: true };
            

            // Fiscal Totals row
            const fiscalTotalRow2 = ['Fiscal Totals','','','','','','','','','','','',''];
            const fiscalRow2 = fltDataSheet.addRow(fiscalTotalRow2);
            fiscalRow2.font = { name: 'Times New Roman', size: 10, bold: true };
            fiscalRow2.getCell(2).value = { formula: `=B36+B3+B2` };
            fiscalRow2.getCell(5).value = { formula: `=E36+E3+E2` };
            fiscalRow2.getCell(6).value = { formula: `=F36+F3+F2` };
            fiscalRow2.getCell(13).value = { formula: `=M2+M36` };

            // Empty row
            fltDataSheet.addRow([]);

            // Previous 1st Month row
            fltDataSheet.addRow(['Previous 1st Month', '', '', '', '', '', '', '', '', '', '', '', '']);
            fltDataSheet.getRow(fltDataSheet.rowCount).font = { name: 'Times New Roman', size: 10, bold: true };

            // Previous 2nd Month row
            fltDataSheet.addRow(['Previous 2nd Month', '', '', '', '', '', '', '', '', '', '', '', '']);
            fltDataSheet.getRow(fltDataSheet.rowCount).font = { name: 'Times New Roman', size: 10, bold: true };

            // Quarterly row
            const quarterlyRow = ['Quarterly', '', '', '', '', '', '', '', '', '', '', '', ''];
            const quarterly = fltDataSheet.addRow(quarterlyRow);
            quarterly.font = { name: 'Times New Roman', size: 10, bold: true };
            quarterly.getCell(2).value = { formula: `=SUM(B36,B40,B39)` };
            quarterly.getCell(5).value = { formula: `=SUM(E39:E40,E36)` };
            quarterly.getCell(6).value = { formula: `=SUM(F39:F40,F36)` };
            
            // Style FLT Data sheet
            applyStyling(fltDataSheet, { name: 'Times New Roman', size: 10 });
            
            // Add borders and formatting to range A1:M41
            for (let row = 1; row <= 41; row++) {
                for (let col = 1; col <= 13; col++) { // A=1, M=13
                    const cell = fltDataSheet.getCell(row, col);
                    
                    // Add borders to all cells
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                    
                    // Preserve existing alignment for specific cells that need wrapText
                    if (row === 2 && col === 1) {
                        // Preserve wrapText for row 2, column A
                        cell.alignment = { vertical: 'top', wrapText: true };
                    } else if (row === 3 && col === 1) {
                        // Preserve wrapText for row 3, column A
                        cell.alignment = { vertical: 'top', wrapText: true };
                    } else if (row === 1 && col === 13) {
                        // Preserve wrapText for row 1, column M
                        cell.alignment = { vertical: 'top', wrapText: true };
                    }
                    // Center text and format as numbers in range B5:M41
                    else if (row >= 5 && col >= 2) {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        
                        // Format numeric columns as numbers with appropriate decimal places
                        if (col >= 2 && col <= 9) { // Columns B through I (numeric data columns)
                            if (cell.value !== null && cell.value !== undefined && cell.value !== '') {
                                cell.numFmt = '0.0'; // Format with 1 decimal place
                            }
                        } else if (col >= 11 && col <= 13) { // Columns K through M (weekly totals and accident free)
                            if (cell.value !== null && cell.value !== undefined && cell.value !== '') {
                                cell.numFmt = '0.0'; // Format with 1 decimal place
                            }
                        }
                    }
                }
            }
            row2.getCell(13).numFmt = '#,##0.0';
            monthlyRow.getCell(1).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(2).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(3).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(4).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(5).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(6).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(7).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(8).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(9).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(10).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(11).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(12).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};
            monthlyRow.getCell(13).border = {top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }};

            // SCR-RFPA Sheet
            const scrRfpaSheet = workbook.addWorksheet('SCR-RFPA ');

            scrRfpaSheet.protect('automatedDSR', {
                selectLockedCells: true,
                selectUnlockedCells: true,
                formatCells: false,
                formatColumns: false,
                formatRows: false,
                insertColumns: false,
                insertRows: false,
                insertHyperlinks: false,
                deleteColumns: false,
                deleteRows: false,
                sort: false,
                autoFilter: false,
                pivotTables: false,
                objects: false,
                scenarios: false
            });
            


            // Set column widths to match the image layout
            scrRfpaSheet.columns = [
                { width: 12 }, // A - Left section descriptions
                { width: 8 },  // B - Sr labels
                { width: 8 },  // C - Reconciled values
                { width: 8 },  // D - Actual values
                { width: 8 }, // E - Right section descriptions
                { width: 38 },  // F - Additional info
                { width: 8 },  // G - Values
                { width: 34 }, // H - RFPA descriptions
                { width: 8 }   // I - RFPA values
            ];

            // Get current SCR-RFPA entry for selected site/date for export
            const getCurrentScrRfpaEntry = async (exportDate = null) => {
                try {
                    const siteName = selectedSite ? sites.find(s => s.id === parseInt(selectedSite))?.name : null;
                    
                    // Get all entries since the site filter API is not working correctly
                    const response = await api.get('/scr-rfpa-entries/');
                    const allEntries = response.data || [];
                    
                    // Filter in JavaScript since the API site filter is not working
                    const entries = siteName ? allEntries.filter(e => e.site_name === siteName) : allEntries;
                    
                    // Use provided date or default to today's date
                    const reportedDate = exportDate || new Date().toISOString().split('T')[0];
                    const found = entries.find(e => e.date === reportedDate && (!siteName || e.site_name === siteName));
                    return found || null;
                } catch (e) {
                    return null;
                }
            };

            // Get the current date from SCR-RFPA component if available
            // Use the SCR-RFPA date from the main component state
            const exportDate = window.scrRfpaCurrentDate || scrRfpaCurrentDate;
            
            
            
            
            const currentScrRfpaEntry = await getCurrentScrRfpaEntry(exportDate);
            
            // Helper function to get value or placeholder
            const getValue = (value, placeholder = 0) => {
                // Handle null, undefined, empty string, and return 0 for these cases
                if (value === null || value === undefined || value === '') {
                    return placeholder;
                }
                // Return the actual value (including 0)
                return value;
            };

            const titleRow = scrRfpaSheet.addRow(['PMF TOTALS', '', '', '', '', '', '', '','']);
            scrRfpaSheet.mergeCells('A1:I1');
            titleRow.getCell(5).font = { bold: true, size: 14 };
            titleRow.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF99' } }; // Light yellow
            titleRow.getCell(5).alignment = { horizontal: 'center', vertical: 'middle' };

            // Row 2: Reported date
            const dateRow = scrRfpaSheet.addRow(['', 'Reported date:', '', '', '', '', '', '', '']);
            scrRfpaSheet.mergeCells('B2:C2');
            scrRfpaSheet.mergeCells('D2:E2');
            const dateCell = scrRfpaSheet.getCell('D2');
            dateCell.value = new Date(exportDate);
            dateCell.numFmt = 'dd-mmm-yy';
            dateRow.getCell(2).font = { bold: true };
            dateCell.alignment = { horizontal: 'left', vertical: 'middle' };

            // Row 3: Empty
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '', '']);

            // Row 4: Band information (E5: 0830 BAND, E6: 75%)
             const row4 = scrRfpaSheet.addRow(['', '', '', '', '', '', '0830\nBAND', '','']);
             row4.getCell(7).font = { name: 'Arial', size: 8, bold: true };
             row4.getCell(7).alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' };
             row4.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5D9F1' } };
             row4.getCell(7).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            
             // Row 5: 0830 BAND / 75%
            const row5 = scrRfpaSheet.addRow(['', '', '', '', '', '', '75%', '','']);
            row5.getCell(7).font = { name: 'Arial', size: 8, bold: true };
            row5.getCell(7).alignment = { horizontal: 'center', vertical: 'middle' };
            row5.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5D9F1' } };
            row5.getCell(7).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
 
            // Row 6: MOB TAR (Total A/C Required excl. DET)
            const row6 = scrRfpaSheet.addRow(['', '', '', '', '','MOB TAR (Total A/C Required excl. DET)', '', '', '']);
            row6.getCell(7).value = { formula: '=ROUNDDOWN(($D8)*0.75,0)' };
            row6.getCell(6).font = { name: 'Arial', size: 8, bold: true };
            row6.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5D9F1' } }; // Light blue
            row6.getCell(7).alignment = { horizontal: 'right' };
            row6.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row6.getCell(6).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row6.getCell(7).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};

            // Row 7: A/C on XC/Down out MOB
            const row7 = scrRfpaSheet.addRow(['', '', '', '', '','A/C on XC/Down out MOB', '', 'RFPA Totals', 'RFPA %']);
            row7.getCell(7).value = { formula: '=D9' };
            row7.getCell(6).font = { name: 'Arial', size: 8, bold: true };
            row7.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5D9F1' } };
            row7.getCell(7).alignment = { horizontal: 'right' };
            row7.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row7.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row7.getCell(8).alignment = { horizontal: 'right' };
            row7.getCell(8).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6B8B7' } };
            row7.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row7.getCell(9).alignment = { horizontal: 'right' }; 
            row7.getCell(9).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
            row7.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row7.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row7.getCell(8).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'none'},right: { style: 'medium'}};
            row7.getCell(9).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            
            // Row 8: Adj MOB TAR after Minus out MOB
            const row8 = scrRfpaSheet.addRow(['Line 3: # MCRS A/C on DSR', '', '', '', '', 'Adj MOB TAR after Minus out MOB', '', 'Req RFP TAR (Ready for Production)','']);
            scrRfpaSheet.mergeCells('A8:C8');
            row8.getCell(4).value = getValue(currentScrRfpaEntry?.mcrs_ac_on_dsr);
            row8.getCell(7).value = { formula: '=G6-G7' };
            row8.getCell(9).value = { formula: '=SUM(G8:G9)'};
            row8.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBE4BC' } };
            row8.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row8.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row8.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row8.getCell(4).alignment = { horizontal: 'right' };
            row8.getCell(6).font = { name: 'Arial', size: 8, bold: true };
            row8.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5D9F1' } };
            row8.getCell(7).alignment = { horizontal: 'right' };
            row8.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row8.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row8.getCell(8).alignment = { horizontal: 'right' };
            row8.getCell(8).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6B8B7' } };
            row8.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row8.getCell(9).alignment = { horizontal: 'right' };
            row8.getCell(1).border = { top: { style: 'medium' },left: { style: 'none' },bottom: { style: 'none'},right: { style: 'none'}};
            row8.getCell(4).border = { top: { style: 'medium' },left: { style: 'none' },bottom: { style: 'none'},right: { style: 'medium'}};
            row8.getCell(6).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row8.getCell(7).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row8.getCell(8).border = { top: { style: 'none' },left: { style: 'medium' },bottom: { style: 'none'},right: { style: 'medium'}};
            row8.getCell(9).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            // Row 9: Adj DET TAR at 80%
            const row9 = scrRfpaSheet.addRow(['Line 9A:  XC A/Cs/Band', '', '', '','', 'Adj DET TAR at 80%', '','RFPA (Ready for Production Avail)', '']);
            scrRfpaSheet.mergeCells('A9:C9');
            row9.getCell(4).value = getValue(currentScrRfpaEntry?.xc_acs_band);
            row9.getCell(7).value = { formula: '=ROUNDDOWN($D$10*0.8,0)' };
            row9.getCell(9).value = { formula: '=IF(I8<SUM(G10:G11),I8,SUM(G10:G11))' };
            row9.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBE4BC' } };
            row9.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row9.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row9.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row9.getCell(4).alignment = { horizontal: 'right' };
            row9.getCell(6).font = { name: 'Arial', size: 8, bold: true };
            row9.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC5D9F1' } };
            row9.getCell(7).alignment = { horizontal: 'right' };
            row9.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row9.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row9.getCell(8).alignment = { horizontal: 'right' };
            row9.getCell(8).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6B8B7' } };
            row9.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row9.getCell(9).alignment = { horizontal: 'right' };

            row9.getCell(4).border = { top: { style: 'none' },left: { style: 'none' },bottom: { style: 'none'},right: { style: 'medium'}};
            row9.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row9.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row9.getCell(8).border = { top: { style: 'none' },left: { style: 'none' },bottom: { style: 'none'},right: { style: 'medium'}};
            row9.getCell(9).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};

            // Row 10: RFP MOB A/C per Det Count
            const row10 = scrRfpaSheet.addRow(['Line 9B: DET A/Cs/Band', '', '', '', '','RFP MOB A/C per Det Count (Cannot exceed TAR)', '','RFPA % (Availability)','' ]);
            scrRfpaSheet.mergeCells('A10:C10');
            row10.getCell(4).value = getValue(currentScrRfpaEntry?.det_acs_band);
            row10.getCell(7).value = getValue(currentScrRfpaEntry?.rfp_mob_ac_per_det_count);
            row10.getCell(9).style = { numFmt: '0.00%' };
            row10.getCell(9).value = { formula: '=IF(OR(I8=0,I9>I8),1,(I9/(I8)))' };
            row10.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBE4BC' } };
            row10.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row10.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row10.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row10.getCell(4).alignment = { horizontal: 'right' };
            row10.getCell(6).font = { name: 'Arial', size: 8, bold: true };
            row10.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row10.getCell(7).alignment = { horizontal: 'right' };
            row10.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row10.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row10.getCell(8).alignment = { horizontal: 'right' };
            row10.getCell(8).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6B8B7' } };
            row10.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row10.getCell(9).alignment = { horizontal: 'right' };
            row10.getCell(3).border = { top: { style: 'none' },left: { style: 'none' },bottom: { style: 'medium'},right: { style: 'none'}};
            row10.getCell(4).border = { top: { style: 'none' },left: { style: 'none' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row10.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row10.getCell(7).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row10.getCell(8).border = { top: { style: 'none' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row10.getCell(9).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};

            // Row 11 RFP DET A/C per Det Count
            const row11 = scrRfpaSheet.addRow(['*NTE 72 hours (PWS 5.2.10.13)', '', '', '', '', 'RFP DET A/C per Det Count (Can exceed TAR)', '', '', '']);
            scrRfpaSheet.mergeCells('A11:C11');
            row11.getCell(7).value = getValue(currentScrRfpaEntry?.rfp_det_ac_per_det_count);
            row11.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row11.getCell(6).font = { name: 'Arial', size: 8, bold: true };
            row11.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row11.getCell(7).alignment = { horizontal: 'right' };
            row11.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row11.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row11.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            
            // Row 12 Total Sorties Sched
            const row12 = scrRfpaSheet.addRow(['Total Sorties Sched', '', 'Sr', '', '', 'Sched Sorties', '', '', '']);
            scrRfpaSheet.mergeCells('A12:B12');
            row12.getCell(4).value = { formula: '=G12' };
            row12.getCell(7).value = getValue(currentScrRfpaEntry?.sched_sorties);
            row12.getCell(1).font = { name: 'Arial', size: 10 };
            row12.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row12.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row12.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row12.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row12.getCell(7).alignment = { horizontal: 'right' };
            row12.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row12.getCell(4).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row12.getCell(6).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row12.getCell(7).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};




            // Row 13 Total Sorties Flown
            const row13 = scrRfpaSheet.addRow(['Total Sorties Flown', '','Sr', '', '', 'Completed Sorties (incl. Add-Ons)', '', '', '']);
            scrRfpaSheet.mergeCells('A13:B13');
            row13.getCell(4).value = { formula: '=G13' };
            row13.getCell(7).value = getValue(currentScrRfpaEntry?.completed_sorties_incl_add_ons);
            row13.getCell(1).font = { name: 'Arial', size: 10 };
            row13.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row13.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row13.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row13.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row13.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row13.getCell(7).alignment = { horizontal: 'right' };
            row13.getCell(4).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row13.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row13.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            
            // Row 14 Scheduled FLT HRS
            const row14 = scrRfpaSheet.addRow(['', '', '', '', '', 'Added Sorties', '', 'Ssked (Flts Req Flt Sched)', '']);
            row14.getCell(7).value = getValue(currentScrRfpaEntry?.added_sorties);
            row14.getCell(9).value = { formula: '=$D$12' };
            row14.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row14.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row14.getCell(7).alignment = { horizontal: 'right' };
            row14.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row14.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row14.getCell(8).alignment = { horizontal: 'right' };
            row14.getCell(8).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF339933' } };
            row14.getCell(9).alignment = { horizontal: 'right' };
            row14.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row14.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row14.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row14.getCell(8).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'none'},right: { style: 'medium'}};
            row14.getCell(9).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};

            // Row 15 Ssked Schduled FLT HRS
            const row15 = scrRfpaSheet.addRow(['Schduled FLT HRS', '', '', '', '', 'ACNA\'s', '', 'FCF (Functional Check Flight)', '']);
            scrRfpaSheet.mergeCells('A15:C15');
            row15.getCell(4).value = getValue(currentScrRfpaEntry?.scheduled_flt_hrs);
            row15.getCell(7).value = getValue(currentScrRfpaEntry?.acnas);
            row15.getCell(9).value = getValue(currentScrRfpaEntry?.fcf_functional_check_flight);
            row15.getCell(1).font = { name: 'Arial', size: 10, bold: true };
            row15.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row15.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row15.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row15.getCell(7).alignment = { horizontal: 'right' };
            row15.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row15.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row15.getCell(8).alignment = { horizontal: 'right' };
            row15.getCell(9).alignment = { horizontal: 'right' };
            row15.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row15.getCell(9).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCFFCC' } };
            row15.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row15.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row15.getCell(8).border = { top: { style: 'none' },left: { style: 'medium' },bottom: { style: 'none'},right: { style: 'medium'}};
            row15.getCell(9).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            
            // Row 16 
            const row16 = scrRfpaSheet.addRow(['', '', '', '', '', 'A Aborts', '', 'ACNAs (A/Cs not Available, A/C aborts)', '']);
            row16.getCell(7).value = getValue(currentScrRfpaEntry?.a_aborts);
            row16.getCell(9).value = { formula: '=SUM(C18,C19)' };
            row16.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row16.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row16.getCell(7).alignment = { horizontal: 'right' };
            row16.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row16.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row16.getCell(8).alignment = { horizontal: 'right' };
            row16.getCell(9).alignment = { horizontal: 'right' };
            row16.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row16.getCell(9).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF0000' } };
            row16.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row16.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row16.getCell(8).border = { top: { style: 'none' },left: { style: 'medium' },bottom: { style: 'none'},right: { style: 'medium'}};
            row16.getCell(9).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            
            // Row 17: Reconciled/Actual
            const row17 = scrRfpaSheet.addRow(['', '', 'Reconciled', 'Actual', '', 'C Aborts', '', 'Ops/WX Cancellations', '']);
            row17.getCell(7).value = getValue(currentScrRfpaEntry?.c_aborts);
            row17.getCell(9).value = { formula: '=SUM(G18:G19)' };
            row17.getCell(3).font = { name: 'Arial', size: 8, bold: true };
            row17.getCell(3).alignment = { horizontal: 'center' };
            row17.getCell(4).font = { name: 'Arial', size: 8, bold: true };
            row17.getCell(4).alignment = { horizontal: 'center' };
            row17.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row17.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } }; //00CCFF
            row17.getCell(7).alignment = { horizontal: 'right' };
            row17.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row17.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row17.getCell(8).alignment = { horizontal: 'right' };
            row17.getCell(9).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00CCFF' } };
            row17.getCell(9).alignment = { horizontal: 'right' };
            row17.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row17.getCell(3).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            row17.getCell(4).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            row17.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row17.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row17.getCell(8).border = { top: { style: 'none' },left: { style: 'medium' },bottom: { style: 'none'},right: { style: 'medium'}};
            row17.getCell(9).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            
            // Row 18: ACNA's
            const row18 = scrRfpaSheet.addRow(['ACNA\'s', '', '', '', '', 'Weather Cnx\'s', '', 'MC Sorties Flown  Sfl','']);
            scrRfpaSheet.mergeCells('A18:B18');
            row18.getCell(3).value = { formula: '=$D$18' };
            row18.getCell(4).value = { formula: '=$G$15' };
            row18.getCell(7).value = getValue(currentScrRfpaEntry?.weather_cnxs);
            row18.getCell(9).value = { formula: '=$D$13' };
            row18.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row18.getCell(3).alignment = { horizontal: 'right' };
            row18.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row18.getCell(4).alignment = { horizontal: 'right' };
            row18.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row18.getCell(7).alignment = { horizontal: 'right' };
            row18.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row18.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row18.getCell(8).alignment = { horizontal: 'right' };
            row18.getCell(9).alignment = { horizontal: 'right' };
            row18.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row18.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF00000' } }; // Light red
            row18.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF99CCFF' } }; // Light blue
            row18.getCell(6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6B8B7' } };
            row18.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row18.getCell(8).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF339933' } };
            row18.getCell(3).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            row18.getCell(4).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            row18.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row18.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'thin'},right: { style: 'medium'}};
            row18.getCell(8).border = { top: { style: 'none' },left: { style: 'medium' },bottom: { style: 'none'},right: { style: 'medium'}};
            row18.getCell(9).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            
            // Row 19: "A" & "C" Aborts
            const row19 = scrRfpaSheet.addRow(['"A" & "C" Aborts', '', '', '', '', 'OPS Cnx\'s', '', 'SCR (Sortie Completion Rate)','']);
            scrRfpaSheet.mergeCells('A19:B19');
            row19.getCell(3).value = { formula: '=D19' };
            row19.getCell(4).value = { formula: '=SUM(G16:G17)' };
            row19.getCell(7).value = getValue(currentScrRfpaEntry?.ops_cnxs);
            row19.getCell(9).style = { numFmt: '0.00%' };
            row19.getCell(9).value = { formula: '=IF(I14-(I17)=0,1,IF((I18)/(I14-(I17))>1,1,(I18)/(I14-(I17))))' };
            row19.getCell(3).font = { name: 'Arial', size: 10, bold: true };
            row19.getCell(3).alignment = { horizontal: 'right' };
            row19.getCell(4).font = { name: 'Arial', size: 10, bold: true };
            row19.getCell(4).alignment = { horizontal: 'right' };
            row19.getCell(6).font = { name: 'Arial', size: 9, bold: true };
            row19.getCell(7).alignment = { horizontal: 'right' };
            row19.getCell(7).font = { name: 'Arial', size: 10, bold: true };
            row19.getCell(8).font = { name: 'Arial', size: 9, bold: true };
            row19.getCell(8).alignment = { horizontal: 'right' };
            row19.getCell(9).alignment = { horizontal: 'right' };
            row19.getCell(9).font = { name: 'Arial', size: 10, bold: true };
            row19.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF00000' } }; // Light red
            row19.getCell(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF99CCFF' } }; // Light blue
            row19.getCell(6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6B8B7' } };
            row19.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBDBDB' } };
            row19.getCell(8).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF339933' } };
            row19.getCell(3).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            row19.getCell(4).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            row19.getCell(6).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row19.getCell(7).border = { top: { style: 'thin' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row19.getCell(8).border = { top: { style: 'none' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            row19.getCell(9).border = { top: { style: 'medium' },left: { style: 'medium' },bottom: { style: 'medium'},right: { style: 'medium'}};
            
            //20
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '']);
            //21
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '']);

            //Row 22-27
            const FlightBandsText = "5.2.10.5 Flight Bands:  The Contractor shall provide aircraft to meet the daily flight schedule (DFS) commencing at 0700 at all MOBs.  The DET will record the number of RFP aircraft delivered at 0830.  The aircraft count presented at 0830 must be able to support the requirements of the DFS to be counted for the RFPA metric.  The DET will count RFP aircraft delivered via Aircraft Discrepancy Book (ADB).  The Contractor shall assign aircraft to the flight schedule at least one hour prior to scheduled takeoff. The number of aircraft required will be a percentage of the Mission Capable Reporting Status (MCRS) (A30) aircraft at each MOB."
            const row22 = scrRfpaSheet.addRow([FlightBandsText, '', '', '', '', '', '', '']);
            scrRfpaSheet.mergeCells('A22:I27');
            row22.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };   
            row22.getCell(1).alignment = { wrapText: true, vertical: 'top' };

            // Row 28: Empty
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '']);
 
            // Row 29: Legend
            const row29 = scrRfpaSheet.addRow(['Legend:', '%',' = Made 96.0% or above SCR/RFPA goals.', '', '', '', '','','']);
            scrRfpaSheet.mergeCells('C29:F29');
            row29.getCell(2).alignment = { horizontal: 'center' };
            row29.getCell(2).font = { name: 'Arial', size: 10, bold: true };
            row29.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00B050' } }; 
            row29.getCell(2).font = {color: { argb: 'FFFFFF00' }};
            row29.getCell(2).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            // Row 30: Empty
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '']);

            // Row 31: % = Made 95.9% or below
            const row31 = scrRfpaSheet.addRow(['', '% ', '= Made 95.9% or below SCR/RFPA goals.', '', '', '', '','','']);
            scrRfpaSheet.mergeCells('C31:F31');
            row31.getCell(2).alignment = { horizontal: 'center' };
            row31.getCell(2).font = { name: 'Arial', size: 10, bold: true };
            row31.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } }; 
            row31.getCell(2).font = {color: { argb: 'FFFF0000' }};
            row31.getCell(2).border = { top: { style: 'thin' },left: { style: 'thin' },bottom: { style: 'thin'},right: { style: 'thin'}};
            // Row 32: Empty
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '']);

            // Row 33: Comments
            const commentsText = 'Comments:  Requesting the combined RFPA/SCR adjusted by the Government to be no lower than 96.0% for the performance period.';
            scrRfpaSheet.addRow([commentsText, '', '', '', '', '', '', '']);
            scrRfpaSheet.mergeCells('A33:I33');
            scrRfpaSheet.getRow(33).getCell(1).font = { bold: true, name: 'Times New Roman' };

            // Row 34: Empty
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '']);

            // Row 35: Comments: 'A' ABORT -
            scrRfpaSheet.addRow(['Comments:','A ABORT -', '', '', '', '', '', '', '']);
            scrRfpaSheet.getRow(35).getCell(1).font = { bold: true };
            scrRfpaSheet.getRow(35).getCell(2).font = { bold: true };
            

            // Row 36: 'C' ABORT-
            scrRfpaSheet.addRow(['', 'C ABORT-', '', '', '', '', '', '']);
            scrRfpaSheet.getRow(36).getCell(2).font = { bold: true };
            
            // Row 37: Empty
            scrRfpaSheet.addRow(['', '', '', '', '', '', '', '']);

            // Row 38: Notes:
            scrRfpaSheet.addRow(['Notes:', '', '', '', '', '', '', '']);
            scrRfpaSheet.getRow(38).getCell(1).font = { bold: true };
            
            scrRfpaSheet.addConditionalFormatting({
                ref: 'I10', // The range to apply the formatting to
                rules: [
                  {
                    type: 'expression',
                    formulae: ['I10>=0.961'], // Rule for >= 96.1%detr
                    style: {
                      fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        bgColor: { argb: 'FF00B050' } // Green
                      },
                      font: {
                        color: { argb: 'FFFFFF00' }
                      },
                    },
                  },
                  {
                    type: 'expression',
                    formulae: ['I10<0.961'], // Rule for < 96.1%
                    style: {
                      fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        bgColor: { argb: 'FFFFFF00' } // Light Yellow
                      },
                      font: {
                        color: { argb: 'FFFF0000' }
                      },
                    },
                  },
                ],
              });
              scrRfpaSheet.addConditionalFormatting({
                ref: 'I19', // The range to apply the formatting to
                rules: [
                  {
                    type: 'expression',
                    formulae: ['I19>=0.961'], // Rule for >= 96.1%
                    style: {
                      fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        bgColor: { argb: 'FF00B050' } // Light Green
                      },
                      font: {
                        color: { argb: 'FFFFFF00' }
                      },
                    },
                  },
                  {
                    type: 'expression',
                    formulae: ['I19<0.961'], // Rule for < 96.1%
                    style: {
                      fill: {
                        type: 'pattern',
                        pattern: 'solid',
                        bgColor: { argb: 'FFFFFF00' } // Light Yellow
                      },
                      font: {
                        color: { argb: 'FFFF0000' }
                      },
                    },
                  },
                ],
              });
            
            scrRfpaSheet.getCell('D8').protection = {locked : false};
            scrRfpaSheet.getCell('D9').protection = {locked : false};
            scrRfpaSheet.getCell('D10').protection = {locked : false};
            scrRfpaSheet.getCell('G10').protection = {locked : false};
            scrRfpaSheet.getCell('G11').protection = {locked : false};
            scrRfpaSheet.getCell('G12').protection = {locked : false};
            scrRfpaSheet.getCell('G13').protection = {locked : false};
            scrRfpaSheet.getCell('G14').protection = {locked : false};
            scrRfpaSheet.getCell('G15').protection = {locked : false};
            scrRfpaSheet.getCell('G16').protection = {locked : false};
            scrRfpaSheet.getCell('G17').protection = {locked : false};
            scrRfpaSheet.getCell('G18').protection = {locked : false};
            scrRfpaSheet.getCell('G19').protection = {locked : false};
            scrRfpaSheet.getCell('I15').protection = {locked : false};
            scrRfpaSheet.getCell('B38').protection = {locked : false};
            scrRfpaSheet.getCell('B36').protection = {locked : false};
            scrRfpaSheet.getCell('B35').protection = {locked : false};
            // Generate filename and save
            const today = new Date();
            const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const dateString = `${String(today.getDate()).padStart(2, '0')} ${MONTHS[today.getMonth()]} ${today.getFullYear()}`;
            
            // Get site code for filename
            let siteCode = selectedSite
                ? (sites.find((s) => s.id === parseInt(selectedSite, 10))?.name || 'FLEET')
                : 'FLEET';
            const tmsLabel = (aircraftType || 'TMS').replace(/\s+/g, '');
            const filename = `CDRL ${DSR_COVER.cdrlNumber} ${DSR_COVER.taskOrder} DSR ${siteCode} ${tmsLabel} ${dateString}.xlsx`;
            
            // Save the file - use File System Access API if available, otherwise fall back to download
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            
            let fileSaved = false;
            
            // Check if File System Access API is available (Chrome, Edge, etc.)
            if ('showSaveFilePicker' in window) {
                try {
                    // Show save file picker dialog
                    const fileHandle = await window.showSaveFilePicker({
                        suggestedName: filename,
                        types: [{
                            description: 'Excel files',
                            accept: {
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
                            }
                        }]
                    });
                    
                    // Write the file to the selected location
                    const writable = await fileHandle.createWritable();
                    await writable.write(blob);
                    await writable.close();
                    
                    fileSaved = true;
                    setAlert({
                        show: true,
                        message: `Excel file "${filename}" has been saved successfully!`,
                        type: 'success'
                    });
                } catch (error) {
                    // User cancelled the dialog or error occurred
                    if (error.name !== 'AbortError') {
                        console.error('Error saving file:', error);
                        setAlert({
                            show: true,
                            message: 'Error saving file. Falling back to download...',
                            type: 'warning'
                        });
                        // Fall through to download method
                    } else {
                        // User cancelled - don't show error, just return
                        setLoading(false);
                        return;
                    }
                }
            }
            
            // Fallback: Use traditional download method (defaults to Downloads folder)
            if (!fileSaved) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            window.URL.revokeObjectURL(url);
            
                if (!('showSaveFilePicker' in window)) {
            setAlert({
                show: true,
                        message: `Excel file "${filename}" has been downloaded to your Downloads folder!`,
                type: 'success'
            });
                }
            }
            
        } catch (error) {
            setAlert({
                show: true,
                message: 'Error exporting to Excel. Please try again.',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // --- START OF STATIC CONTENT COMPONENTS ---

    const ACStatus = ({ styles }) => (
    <div style={{ ...styles.card, margin: '20px', padding: '20px', fontSize: '10pt', color: styles.text }}>
        <h3 style={{
            textAlign: 'left',
            marginBottom: '10px',
            fontSize: '14pt',
            fontWeight: 'bold',
            borderBottom: `1px solid ${styles.border}`
        }}>
            AIRCRAFT STATUS
        </h3>
        <p style={{marginBottom: '10px'}}>
            {filteredAircraftStatusData.length} Aircraft
            {selectedSite && sites.find(s => s.id === parseInt(selectedSite)) &&
                ` at ${sites.find(s => s.id === parseInt(selectedSite)).name}`}
            {selectedTms ? ` (${selectedTms})` : ''}
        </p>
        <div style={{ overflowX: 'auto' }}>
            <table style={{...styles.table, fontSize: '9px', tableLayout: 'fixed', width: '100%'}}>
                <thead>
                    <tr style={styles.tableHeader}>
                        <th style={{ ...styles.tableHeaderCell, width: '40px' }}>BUNO</th>
                        <th style={{ ...styles.tableHeaderCell, width: '35px' }}>Modex</th>
                        <th style={{ ...styles.tableHeaderCell, width: '40px' }}>Status 1</th>
                        <th style={{ ...styles.tableHeaderCell, width: '60px' }}>Status 2</th>
                        <th style={{...styles.tableHeaderCell, width: '450px'}}>Reason</th>
                        <th style={{ ...styles.tableHeaderCell, width: '80px' }}>Last Fly Date</th>
                        <th style={{ ...styles.tableHeaderCell, width: '80px' }}>30 Day NO FLY</th>
                        <th style={{ ...styles.tableHeaderCell, width: '80px' }}>NDF</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredAircraftStatusData.length === 0 ? (
                        <tr style={styles.tableRow}>
                            <td style={styles.tableCell} colSpan="8">No aircraft data available</td>
                        </tr>
                    ) : (
                        filteredAircraftStatusData.map((item, index) => {
                            const buno = item.buno || 'N/A';
                            const modex = buno !== 'N/A' ? (getAircraftModex(item) || 'N/A') : 'N/A';
                            const lastFlyDateStr = item.last_fly_date || '';
                            const lastFlyDate = lastFlyDateStr ? new Date(lastFlyDateStr) : null;
                            const thirtyDayNoFly = lastFlyDate ? (() => {
                                const todayCalc = new Date();
                                const daysDiff = Math.floor((todayCalc - lastFlyDate) / (1000 * 60 * 60 * 24));
                                return daysDiff >= 30 ? 'X' : '';
                            })() : '';
                            
                            return (
                            <tr key={index} style={styles.tableRow}>
                                    <td style={{...styles.tableCell, width: '40px'}}>{buno}</td>
                                    <td style={{...styles.tableCell, width: '35px'}}>{modex}</td>
                                    <td style={{...styles.tableCell, width: '40px'}}>{getStatus1(item.status || 'N/A')}</td>
                                    <td style={{...styles.tableCell, width: '60px'}}>{item.status || 'N/A'}</td>
                                    <td style={{...styles.tableCell, width: '450px'}}>{item.reason || ''}</td>
                                    <td style={{...styles.tableCell, width: '80px'}}>{lastFlyDateStr || 'DET/ACPT'}</td>
                                    <td style={{...styles.tableCell, width: '80px'}}>{thirtyDayNoFly}</td>
                                <td style={{...styles.tableCell, width: '80px'}}>{getNDF(item.edd, item.ecd, item.status)}</td>
                            </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    </div>
    );

    const MonthlyFiscalSummaryTable = ({ styles, currentMonth, currentYear, siteName }) => {
        const [summaryData, setSummaryData] = useState(null);
        const [isEditing, setIsEditing] = useState(false);
        const [editData, setEditData] = useState({});
        const [alertMsg, setAlertMsg] = useState({ show: false, message: '', type: '' });

        // Fetch summary data
        useEffect(() => {
            const fetchSummaryData = async () => {
                if (!siteName) return;

                setLoading(true);
                try {
                    const response = await api.get('/monthly-fiscal-summary/', {
                        params: {
                            site_name: siteName,
                            month: currentMonth,
                            year: currentYear
                        }
                    });

                    if (response.data && response.data.length > 0) {
                        setSummaryData(response.data[0]);
                        setEditData(response.data[0]);
                    } else {
                        // No data found, set defaults
                        setSummaryData({
                            site_name: siteName,
                            month: currentMonth,
                            year: currentYear,
                            previous_months_fiscal_total: 0,
                            previous_quarterly_fiscal_total: 0,
                            weekly_hours: null,
                            accident_hours: 620009.2,
                            quarterly_weekly_hours: null,
                            quarterly_accident_hours: 0,
                            previous_months_scheduled: 0,
                            previous_month_flown: 0,
                            previous_quarterly_scheduled: 0,
                            previous_quarterly_flown: 0
                        });
                        setEditData({
                            site_name: siteName,
                            month: currentMonth,
                            year: currentYear,
                            previous_months_fiscal_total: 0,
                            previous_quarterly_fiscal_total: 0,
                            weekly_hours: null,
                            accident_hours: 620009.2,
                            quarterly_weekly_hours: null,
                            quarterly_accident_hours: 0,
                            previous_months_scheduled: 0,
                            previous_month_flown: 0,
                            previous_quarterly_scheduled: 0,
                            previous_quarterly_flown: 0
                        });
                    }
                } catch (error) {
                    console.error('Error fetching fiscal summary:', error);
                }
            };

            fetchSummaryData();
        }, [siteName, currentMonth, currentYear]);

        const handleSave = async () => {
            try {
                let savedData;
                if (summaryData && summaryData.id) {
                    // Update existing - ensure all required fields are included (don't include id in body)
                    const dataToSave = {
                        ...editData,
                        site_name: summaryData.site_name || siteName,
                        month: summaryData.month || currentMonth,
                        year: summaryData.year || currentYear
                    };
                    const response = await api.put(`/monthly-fiscal-summary/${summaryData.id}/`, dataToSave);
                    // Use response data if available, otherwise use the data we just saved with id
                    savedData = response.data || { ...dataToSave, id: summaryData.id };
                } else {
                    // Create new - ensure required fields are included
                    const dataToSave = {
                        ...editData,
                        site_name: siteName,
                        month: currentMonth,
                        year: currentYear
                    };
                    const response = await api.post('/monthly-fiscal-summary/', dataToSave);
                    savedData = response.data;
                }
                
                // Update summaryData with the saved data
                setSummaryData(savedData);
                setEditData(savedData);
                setIsEditing(false);
                setAlertMsg({ show: true, message: 'Fiscal summary saved successfully', type: 'success' });
                setTimeout(() => setAlertMsg({ show: false, message: '', type: '' }), 3000);
            } catch (error) {
                console.error('Error saving fiscal summary:', error);
                setAlertMsg({ show: true, message: 'Failed to save fiscal summary data', type: 'error' });
                setTimeout(() => setAlertMsg({ show: false, message: '', type: '' }), 3000);
            }
        };

        const handleFieldChange = (field, value) => {
            setEditData(prev => ({
                ...prev,
                [field]: value === '' ? null : parseFloat(value)
            }));
        };

        if (!siteName) {
            return null;
        }

        return (
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '12pt' }}>Monthly Fiscal Summary</h4>
                    {!isEditing ? (
                        <button onClick={() => setIsEditing(true)} style={{ ...styles.button, padding: '5px 10px', fontSize: '10pt' }}>
                            Edit
                        </button>
                    ) : (
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <button onClick={handleSave} style={{ ...styles.button, backgroundColor: '#28a745', color: 'white', padding: '5px 10px', fontSize: '10pt' }}>
                                Save
                            </button>
                            <button onClick={() => { setIsEditing(false); setEditData(summaryData || {}); }} style={{ ...styles.button, backgroundColor: '#6c757d', color: 'white', padding: '5px 10px', fontSize: '10pt' }}>
                                Cancel
                            </button>
                        </div>
                    )}
                </div>

                <table style={{...styles.table, fontSize: '10pt', marginBottom: '20px'}}>
                    <thead>
                        <tr style={{...styles.tableHeader, backgroundColor: styles.bg}}>
                            <th style={{...styles.tableHeaderCell, width: '25%'}}>Description</th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}>0</th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}></th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}></th>
                            
                            <th style={{...styles.tableHeaderCell, width: '8%'}}>Previous Scheduled</th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}>Previous Flown</th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}></th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}></th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}></th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}>Weekly</th>
                            <th style={{...styles.tableHeaderCell, width: '8%'}}>Accident Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={styles.tableCell}>Previous Months Fiscal Total</td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.previous_months_fiscal_total || 0}
                                        onChange={(e) => handleFieldChange('previous_months_fiscal_total', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.previous_months_fiscal_total) || 0).toFixed(1)
                                )}
                            </td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.previous_months_scheduled || 0}
                                        onChange={(e) => handleFieldChange('previous_months_scheduled', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.previous_months_scheduled) || 0).toFixed(1)
                                )}
                            </td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.previous_month_flown || 0}
                                        onChange={(e) => handleFieldChange('previous_month_flown', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.previous_month_flown) || 0).toFixed(1)
                                )}
                            </td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.weekly_hours || ''}
                                        onChange={(e) => handleFieldChange('weekly_hours', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    summaryData?.weekly_hours ? parseFloat(summaryData.weekly_hours).toFixed(1) : ''
                                )}
                            </td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.accident_hours || 0}
                                        onChange={(e) => handleFieldChange('accident_hours', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.accident_hours) || 0).toFixed(1)
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tableCell}>Previous Quarterly Fiscal Total</td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.previous_quarterly_fiscal_total || 0}
                                        onChange={(e) => handleFieldChange('previous_quarterly_fiscal_total', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.previous_quarterly_fiscal_total) || 0).toFixed(1)
                                )}
                            </td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.previous_quarterly_scheduled || 0}
                                        onChange={(e) => handleFieldChange('previous_quarterly_scheduled', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.previous_quarterly_scheduled) || 0).toFixed(1)
                                )}
                            </td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.previous_quarterly_flown || 0}
                                        onChange={(e) => handleFieldChange('previous_quarterly_flown', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.previous_quarterly_flown) || 0).toFixed(1)
                                )}
                            </td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}></td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.quarterly_weekly_hours || ''}
                                        onChange={(e) => handleFieldChange('quarterly_weekly_hours', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    summaryData?.quarterly_weekly_hours ? parseFloat(summaryData.quarterly_weekly_hours).toFixed(1) : ''
                                )}
                            </td>
                            <td style={styles.tableCell}>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={editData.quarterly_accident_hours || 0}
                                        onChange={(e) => handleFieldChange('quarterly_accident_hours', e.target.value)}
                                        style={{ width: '100%', padding: '2px' }}
                                        step="0.1"
                                    />
                                ) : (
                                    (parseFloat(summaryData?.quarterly_accident_hours) || 0).toFixed(1)
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
                {alertMsg.show && (
                    <div style={{
                        padding: '10px',
                        marginTop: '10px',
                        borderRadius: '4px',
                        backgroundColor: alertMsg.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: alertMsg.type === 'success' ? '#155724' : '#721c24',
                        border: `1px solid ${alertMsg.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                        fontSize: '10pt'
                    }}>
                        {alertMsg.message}
                    </div>
                )}

            </div>
        );
    };

    const FLTData = ({ styles, onMonthYearChange, currentMonth = new Date().getMonth() + 1, currentYear = new Date().getFullYear(), setCurrentMonth, setCurrentYear, selectedSite, sites }) => {
        const [flightDataEntries, setFlightDataEntries] = useState([]);
        const [editingEntry, setEditingEntry] = useState(null);
        const [alert, setAlert] = useState({ show: false, message: '', type: '' });
        const selectedSiteName = useMemo(() => {
            if (!selectedSite) return null;
            return sites.find(s => s.id === parseInt(selectedSite))?.name || null;
        }, [selectedSite, sites]);
        const defaultSiteId = sites[0]?.id;
        const defaultSiteName = sites[0]?.name;
        const resolvedSiteId = selectedSite ? parseInt(selectedSite) : defaultSiteId;
        const resolvedSiteName = selectedSiteName || defaultSiteName;

        // Generate days for current month
        const generateMonthDays = (year, month) => {
            const daysInMonth = new Date(year, month, 0).getDate();
            const days = [];
            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, month - 1, day);
                days.push({
                    date: date.toISOString().split('T')[0],
                    dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'short' }),
                    dayNumber: day,
                    isWeekend: date.getDay() === 0 || date.getDay() === 6
                });
            }
            return days;
        };

        const monthDays = generateMonthDays(currentYear, currentMonth);

        // Calculate daily flight hours with hybrid logic (manual + auto-validation)
        const calculateDailyFlightHours = (date, manualValue = null) => {
            // Get calculated value from Flight Hour Monthly data
            let calculatedValue = 0;
            if (filteredFlightHourData && filteredFlightHourData.length > 0) {
                const dateStr = date.split('T')[0];
                calculatedValue = filteredFlightHourData
                    .filter(item => {
                        // Check if create_dt matches the date (handle both date and datetime formats)
                        if (item.create_dt) {
                            const itemDate = new Date(item.create_dt).toISOString().split('T')[0];
                            return itemDate === dateStr;
                        }
                        return false;
                    })
                    .reduce((sum, item) => sum + (parseFloat(item.tmr_hrs) || 0), 0);
            }
            
            // If user provided manual value, compare and use higher value
            if (manualValue !== null && manualValue !== undefined && manualValue !== '') {
                const manualNum = parseFloat(manualValue) || 0;
                return Math.max(calculatedValue, manualNum);
            }
            
            // Otherwise use calculated value
            return calculatedValue;
        };

        // Calculate weekend flight hours with hybrid logic (manual + auto-validation)
        const calculateWeekendFlightHours = (date, manualValue = null) => {
            // Get calculated value from Flight Hour Monthly data for weekends
            let calculatedValue = 0;
            if (filteredFlightHourData && filteredFlightHourData.length > 0) {
                const dateStr = date.split('T')[0];
                const dayOfWeek = new Date(dateStr).getDay();
                
                // Only calculate for weekends (Saturday = 6, Sunday = 0)
                if (dayOfWeek === 0 || dayOfWeek === 6) {
                    calculatedValue = filteredFlightHourData
                        .filter(item => {
                            // Check if create_dt matches the date (handle both date and datetime formats)
                            if (item.create_dt) {
                                const itemDate = new Date(item.create_dt).toISOString().split('T')[0];
                                return itemDate === dateStr;
                            }
                            return false;
                        })
                        .reduce((sum, item) => sum + (parseFloat(item.tmr_hrs) || 0), 0);
                }
            }
            
            // If user provided manual value, compare and use higher value
            if (manualValue !== null && manualValue !== undefined && manualValue !== '') {
                const manualNum = parseFloat(manualValue) || 0;
                return Math.round(Math.max(calculatedValue, manualNum) * 10) / 10;
            }
            
            // Otherwise use calculated value, rounded to nearest tenth
            return Math.round(calculatedValue * 10) / 10;
        };

        // Get flight data entry for a specific date
        const getFlightDataEntry = (date) => {
            return flightDataEntries.find(entry => entry.date === date);
        };

        // Fetch flight data entries
        const fetchFlightDataEntries = useCallback(async () => {
            try {
                const response = await api.get('/flight-data-entries/');
                const allEntries = response.data || [];
                // Filter by selected site
                const filteredEntries = selectedSiteName ? allEntries.filter(entry => entry.site_name === selectedSiteName) : allEntries;
                setFlightDataEntries(filteredEntries);
            } catch (error) {
                setAlert({ show: true, message: 'Failed to fetch flight data entries', type: 'error' });
            }
        }, [selectedSiteName]);

        // Save flight data entry
        const saveFlightDataEntry = async (entryData) => {
            try {
                console.log('Saving flight data entry:', entryData);
                if (editingEntry && editingEntry.id) {
                    // Update existing entry
                    console.log('Updating existing entry with ID:', editingEntry.id);
                    await api.put(`/flight-data-entries/${editingEntry.id}/`, entryData);
                } else {
                    // Create new entry
                    console.log('Creating new entry');
                    await api.post('/flight-data-entries/', entryData);
                }
                await fetchFlightDataEntries();
                setEditingEntry(null);
                setAlert({ show: true, message: 'Flight data saved successfully', type: 'success' });
            } catch (error) {
                console.error('Error saving flight data:', error);
                console.error('Error response:', error.response?.data);
                setAlert({ show: true, message: 'Failed to save flight data', type: 'error' });
            }
        };


        useEffect(() => {
            fetchFlightDataEntries();
        }, [currentMonth, currentYear, fetchFlightDataEntries]);

        // Notify parent component when month/year changes
        useEffect(() => {
            if (onMonthYearChange) {
                onMonthYearChange(currentMonth, currentYear);
            }
        }, [currentMonth, currentYear, onMonthYearChange]);

        // Safety check for props after hooks
        if (!currentMonth || !currentYear || !setCurrentMonth || !setCurrentYear) {
            console.error('FLTData missing required props:', { currentMonth, currentYear, setCurrentMonth, setCurrentYear });
            return <div>Loading...</div>;
        }




        const EditableCell = ({ value, field, date, type = 'text' }) => {
            const entry = getFlightDataEntry(date);
            const isEditing = editingEntry && editingEntry.date === date && editingEntry.field === field;
            const [localValue, setLocalValue] = useState('');
            const inputRef = useRef(null);

            // Initialize local value when entering edit mode
            useEffect(() => {
                if (isEditing) {
                    // For daily_flight_hours, use hybrid logic
                    if (field === 'daily_flight_hours') {
                        const manualValue = entry ? entry[field] : null;
                        const calculatedValue = calculateDailyFlightHours(date, manualValue);
                        setLocalValue(calculatedValue.toFixed(1));
                    } else if (field === 'weekend_flt_hrs') {
                        // For weekend_flt_hrs, use hybrid logic
                        const manualValue = entry ? entry[field] : null;
                        const calculatedValue = calculateWeekendFlightHours(date, manualValue);
                        setLocalValue(calculatedValue.toFixed(1));
                    } else {
                        setLocalValue(entry ? (entry[field] ?? '') : '');
                    }
                }
            }, [isEditing, entry, field, date]);

            const handleChange = (e) => {
                setLocalValue(e.target.value);
            };

            const handleBlur = async () => {
                // Save the value when user clicks away
                let newValue = type === 'number' ? (localValue === '' ? null : parseFloat(localValue)) : localValue;
                let dataSource = 'auto';
                
                // Check if this is a weekend day (Sunday = 0, Saturday = 6)
                const dateStr = date.split('T')[0];
                const dateObj = new Date(dateStr + 'T00:00:00');
                const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                
                // For daily_flight_hours, implement hybrid logic
                if (field === 'daily_flight_hours') {
                    const manualValue = parseFloat(localValue) || 0;
                    const calculatedValue = calculateDailyFlightHours(date);
                    const finalValue = Math.max(calculatedValue, manualValue);
                    
                    // Determine data source
                    if (manualValue > calculatedValue) {
                        dataSource = 'manual';
                    } else if (calculatedValue > manualValue) {
                        dataSource = 'hybrid';
                    } else {
                        dataSource = 'auto';
                    }
                    
                    newValue = finalValue;
                } else if (field === 'weekend_flt_hrs') {
                    // For weekend_flt_hrs, implement hybrid logic
                    const manualValue = parseFloat(localValue) || 0;
                    const calculatedValue = calculateWeekendFlightHours(date);
                    const finalValue = Math.max(calculatedValue, manualValue);
                    
                    // Determine data source
                    if (manualValue > calculatedValue) {
                        dataSource = 'manual';
                    } else if (calculatedValue > manualValue) {
                        dataSource = 'hybrid';
                    } else {
                        dataSource = 'auto';
                    }
                    
                    newValue = finalValue;
                }
                
                const updatedEntry = {
                    id: editingEntry?.id || null,
                    date,
                    site: resolvedSiteId,
                    site_name: resolvedSiteName,
                    [field]: newValue,
                    ...(field === 'daily_flight_hours' && { daily_flight_hours_source: dataSource }),
                    ...(field === 'weekend_flt_hrs' && { weekend_flt_hrs_source: dataSource })
                };
                
                // If updating daily_flight_hours, also update accident_hour_free and weekend_flt_hrs (if weekend)
                if (field === 'daily_flight_hours') {
                    updatedEntry.accident_hour_free = newValue;
                    
                    // If updating daily_flight_hours for a weekend day, also update weekend_flt_hrs
                    if (isWeekend) {
                        updatedEntry.weekend_flt_hrs = newValue;
                        updatedEntry.weekend_flt_hrs_source = dataSource;
                    }
                }
                
                setEditingEntry(updatedEntry);
                
                // Save to backend
                try {
                    await saveFlightDataEntry(updatedEntry);
                } catch (error) {
                    console.error('Error saving flight data:', error);
                }
            };

            const handleKeyDown = async (e) => {
                if (e.key === 'Enter') {
                    await handleBlur();
                    setEditingEntry(null);
                } else if (e.key === 'Escape') {
                    setEditingEntry(null);
                } else if (e.key === 'Tab') {
                    e.preventDefault();
                    // Save current field first
                    await handleBlur();
                    setEditingEntry(null);
                    
                    // Find the next input field and focus it
                    const allInputs = document.querySelectorAll('input[type="number"], input[type="text"]');
                    const currentIndex = Array.from(allInputs).indexOf(e.target);
                    const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;
                    
                    if (nextIndex >= 0 && nextIndex < allInputs.length) {
                        // Small delay to ensure the current field is saved and the next field is rendered
                        setTimeout(() => {
                            allInputs[nextIndex].focus();
                            allInputs[nextIndex].click(); // Trigger the click to enter edit mode
                        }, 50);
                    }
                }
            };

            const handleClick = () => {
                if (!isEditing) {
                    const existingEntry = getFlightDataEntry(date);
                    setEditingEntry({
                        id: existingEntry?.id,
                        date,
                        field,
                        site: resolvedSiteId,
                        site_name: resolvedSiteName,
                        events_scheduled: existingEntry?.events_scheduled || null,
                        weather_aborts_ops_cancels: existingEntry?.weather_aborts_ops_cancels || null,
                        events_flown: existingEntry?.events_flown || null,
                        weekend_flt_hrs: existingEntry?.weekend_flt_hrs || null,
                        weekend_flt_hrs_source: existingEntry?.weekend_flt_hrs_source || null,
                        detachment_sorties: existingEntry?.detachment_sorties || null,
                        detachment_hrs: existingEntry?.detachment_hrs || null,
                        daily_flight_hours: existingEntry?.daily_flight_hours || null,
                        daily_flight_hours_source: existingEntry?.daily_flight_hours_source || null,
                        notes: existingEntry?.notes || '',
                        accident_hour_free: existingEntry?.accident_hour_free || null
                    });
                }
            };

            // Focus the input when it becomes editable
            useEffect(() => {
                if (isEditing && inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, [isEditing]);

            if (isEditing) {
                return (
                    <input
                        ref={inputRef}
                        key={`${date}-${field}`}
                        type={type}
                        value={localValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        style={{
                            width: '100%',
                            border: '1px solid #007bff',
                            padding: '2px',
                            fontSize: '10pt',
                            backgroundColor: '#f8f9fa'
                        }}
                    />
                );
            }

            return (
                <div
                    onClick={handleClick}
                    style={{
                        cursor: 'pointer',
                        minHeight: '20px',
                        padding: '2px',
                        border: '1px solid transparent',
                        borderRadius: '2px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    {(() => {
                        // For daily_flight_hours, show calculated value if no manual value exists
                        if (field === 'daily_flight_hours') {
                            if (entry && entry[field] !== undefined && entry[field] !== null) {
                                return entry[field];
                            } else {
                                return calculateDailyFlightHours(date).toFixed(1);
                            }
                        } else if (field === 'weekend_flt_hrs') {
                            // For weekend_flt_hrs, show calculated value if no manual value exists
                            if (entry && entry[field] !== undefined && entry[field] !== null) {
                                return entry[field];
                            } else {
                                return calculateWeekendFlightHours(date).toFixed(1);
                            }
                        }
                        return entry ? (entry[field] ?? '') : '';
                    })()}
                </div>
            );
        };

        return (
            <div style={{ ...styles.card, margin: '20px', padding: '20px', fontSize: '10pt', color: styles.text }}>
                <h3 style={{ borderBottom: `1px solid ${styles.border}`, paddingBottom: '5px', marginBottom: '15px' }}>
                    Flight Data - {new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                
                {/* Month/Year Navigation */}
                <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        onClick={() => {
                            if (currentMonth > 1) {
                                setCurrentMonth(currentMonth - 1);
                            } else {
                                setCurrentMonth(12);
                                setCurrentYear(currentYear - 1);
                            }
                        }}
                        style={styles.button}
                    >
                        Previous Month
                    </button>
                    <span style={{ fontWeight: 'bold' }}>
                        {new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                        onClick={() => {
                            if (currentMonth < 12) {
                                setCurrentMonth(currentMonth + 1);
                            } else {
                                setCurrentMonth(1);
                                setCurrentYear(currentYear + 1);
                            }
                        }}
                        style={styles.button}
                    >
                        Next Month
                    </button>
                </div>

                {/* Action Buttons */}
                {editingEntry && (
                    <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => saveFlightDataEntry(editingEntry)}
                            style={{ ...styles.button, backgroundColor: '#28a745', color: 'white' }}
                        >
                            Save
                        </button>
                        <button
                            onClick={() => setEditingEntry(null)}
                            style={{ ...styles.button, backgroundColor: '#6c757d', color: 'white' }}
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {/* Monthly Fiscal Summary Table */}
                <MonthlyFiscalSummaryTable
                    styles={styles}
                    currentMonth={currentMonth}
                    currentYear={currentYear}
                    siteName={selectedSite ? sites.find(s => s.id === parseInt(selectedSite))?.name : null}
                />

                <div style={{ overflowX: 'auto' }}>
                    <table style={{...styles.table, fontSize: '10pt'}}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th style={styles.tableHeaderCell}>Date</th>
                                <th style={styles.tableHeaderCell}>Daily Flight Hours</th>
                                <th style={styles.tableHeaderCell}>Events Scheduled</th>
                                <th style={styles.tableHeaderCell}>WNX/Ops Cnx</th>
                                <th style={styles.tableHeaderCell}>Total Scheduled</th>
                                <th style={styles.tableHeaderCell}>Events Flown</th>
                                <th style={styles.tableHeaderCell}>Weekend Flt. Hrs.</th>
                                <th style={styles.tableHeaderCell}>Detachment Sorties</th>
                                <th style={styles.tableHeaderCell}>Detachment Hrs.</th>
                                <th style={styles.tableHeaderCell}>NOTES</th>
                                <th style={styles.tableHeaderCell}>Accident Hour Free</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monthDays.map((day, index) => {
                                const entry = getFlightDataEntry(day.date);
                                const totalScheduled = entry ? 
                                    (entry.events_scheduled && entry.weather_aborts_ops_cancels ? 
                                        entry.events_scheduled - entry.weather_aborts_ops_cancels : null) : null;

                                return (
                                    <tr key={index} style={{
                                        ...styles.tableRow,
                                        backgroundColor: day.isWeekend ? '#f8f9fa' : 'transparent'
                                    }}>
                                        <td style={{...styles.tableCell, width: '40%'}}>
                                            {day.dayOfWeek}, {day.dayNumber.toString().padStart(2, '0')}-{new Date(currentYear, currentMonth - 1, day.dayNumber).toLocaleDateString('en-US', { month: 'short' })}-{currentYear.toString().slice(-2)}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'center', backgroundColor: '#e3f2fd', color: '#000'}}>
                                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'}}>
                                                <EditableCell 
                                                    value={calculateDailyFlightHours(day.date, entry?.daily_flight_hours)} 
                                                    field="daily_flight_hours" 
                                                    date={day.date} 
                                                    type="number" 
                                                />
                                                {entry?.daily_flight_hours_source && (
                                                    <span style={{
                                                        fontSize: '10px',
                                                        padding: '1px 4px',
                                                        borderRadius: '3px',
                                                        backgroundColor: entry.daily_flight_hours_source === 'manual' ? '#ffeb3b' : 
                                                                      entry.daily_flight_hours_source === 'hybrid' ? '#4caf50' : '#2196f3',
                                                        color: entry.daily_flight_hours_source === 'manual' ? '#000' : '#fff'
                                                    }}>
                                                        {entry.daily_flight_hours_source === 'manual' ? 'M' : 
                                                         entry.daily_flight_hours_source === 'hybrid' ? 'H' : 'A'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{...styles.tableCell, width: '20%'}}>
                                            <EditableCell value={entry?.events_scheduled} field="events_scheduled" date={day.date} type="number" />
                                        </td>
                                        <td style={{...styles.tableCell, width: '15%'}}>
                                            <EditableCell value={entry?.weather_aborts_ops_cancels} field="weather_aborts_ops_cancels" date={day.date} type="number" />
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'center', backgroundColor: '#e8f5e8', color: '#000'}}>
                                            {totalScheduled !== null ? totalScheduled.toFixed(0) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, width: '40%'}}>
                                            <EditableCell value={entry?.events_flown} field="events_flown" date={day.date} type="number" />
                                        </td>
                                        <td style={{...styles.tableCell, width: '40%'}}>
                                            {day.isWeekend ? (
                                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'}}>
                                                    <span style={{
                                                        textAlign: 'center',
                                                        display: 'block',
                                                        padding: '4px',
                                                        backgroundColor: '#e3f2fd',
                                                        color: '#000',
                                                        minWidth: '60px'
                                                    }}>
                                                        {calculateDailyFlightHours(day.date, entry?.daily_flight_hours).toFixed(1)}
                                                    </span>
                                                    {entry?.daily_flight_hours_source && (
                                                        <span style={{
                                                            fontSize: '8px',
                                                            padding: '1px 3px',
                                                            borderRadius: '2px',
                                                            backgroundColor: entry.daily_flight_hours_source === 'manual' ? '#ffc107' : 
                                                                           entry.daily_flight_hours_source === 'hybrid' ? '#17a2b8' : '#28a745',
                                                            color: 'white',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {entry.daily_flight_hours_source === 'manual' ? 'M' : 
                                                             entry.daily_flight_hours_source === 'hybrid' ? 'H' : 'A'}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{textAlign: 'center', display: 'block', color: '#999', padding: '4px'}}>
                                                    -
                                                </span>
                                            )}
                                        </td>
                                        <td style={{...styles.tableCell, width: '40%'}}>
                                            <EditableCell value={entry?.detachment_sorties} field="detachment_sorties" date={day.date} type="number" />
                                        </td>
                                        <td style={{...styles.tableCell, width: '40%'}}>
                                            <EditableCell value={entry?.detachment_hrs} field="detachment_hrs" date={day.date} type="number" />
                                        </td>
                                        <td style={{...styles.tableCell, width: '60%'}}>
                                            <EditableCell value={entry?.notes} field="notes" date={day.date} type="text" />
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'center', backgroundColor: '#e3f2fd', color: '#000', width: '40%'}}>
                                            {entry?.accident_hour_free !== null && entry?.accident_hour_free !== undefined 
                                                ? entry.accident_hour_free.toFixed(1) 
                                                : calculateDailyFlightHours(day.date, entry?.daily_flight_hours).toFixed(1)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Totals Table */}
                {(() => {
                    // Calculate monthly totals for a specific month/year
                    const calculateMonthlyTotalsForMonth = (year, month, entriesForMonth, flightHourDataForMonth) => {
                        const days = generateMonthDays(year, month);
                        let dailyFlightHours = 0;
                        let eventsScheduled = 0;
                        let weatherAborts = 0;
                        let eventsFlown = 0;
                        let weekendFltHrs = 0;
                        let detachmentSorties = 0;
                        let detachmentHrs = 0;
                        let totalScheduled = 0;
                        let accidentHourFree = 0;

                        days.forEach(day => {
                            const entry = entriesForMonth.find(e => e.date === day.date);
                            
                            // Calculate daily hours using flight hour data for that month
                            let dailyHrs = 0;
                            if (flightHourDataForMonth && flightHourDataForMonth.length > 0) {
                                const dateStr = day.date.split('T')[0];
                                dailyHrs = flightHourDataForMonth
                                    .filter(item => {
                                        if (item.create_dt) {
                                            const itemDate = new Date(item.create_dt).toISOString().split('T')[0];
                                            return itemDate === dateStr;
                                        }
                                        return false;
                                    })
                                    .reduce((sum, item) => sum + (parseFloat(item.tmr_hrs) || 0), 0);
                            }
                            
                            // Use manual value if provided and higher
                            if (entry?.daily_flight_hours !== null && entry?.daily_flight_hours !== undefined) {
                                dailyHrs = Math.max(dailyHrs, parseFloat(entry.daily_flight_hours) || 0);
                            }
                            
                            dailyFlightHours += dailyHrs;
                            accidentHourFree += dailyHrs;
                            
                            if (entry) {
                                eventsScheduled += entry.events_scheduled || 0;
                                weatherAborts += entry.weather_aborts_ops_cancels || 0;
                                eventsFlown += entry.events_flown || 0;
                                
                                if (day.isWeekend) {
                                    weekendFltHrs += dailyHrs;
                                } else {
                                    weekendFltHrs += entry.weekend_flt_hrs || 0;
                                }
                                
                                detachmentSorties += entry.detachment_sorties || 0;
                                detachmentHrs += entry.detachment_hrs || 0;
                                
                                if (entry.events_scheduled && entry.weather_aborts_ops_cancels) {
                                    totalScheduled += entry.events_scheduled - entry.weather_aborts_ops_cancels;
                                }
                            }
                        });

                        return {
                            dailyFlightHours: parseFloat(dailyFlightHours.toFixed(1)),
                            eventsScheduled,
                            weatherAborts,
                            totalScheduled,
                            eventsFlown,
                            weekendFltHrs: parseFloat(weekendFltHrs.toFixed(1)),
                            detachmentSorties,
                            detachmentHrs,
                            accidentHourFree: parseFloat(accidentHourFree.toFixed(1))
                        };
                    };

                    // Calculate monthly totals for current month
                    const calculateMonthlyTotals = () => {
                        let dailyFlightHours = 0;
                        let eventsScheduled = 0;
                        let weatherAborts = 0;
                        let eventsFlown = 0;
                        let weekendFltHrs = 0;
                        let detachmentSorties = 0;
                        let detachmentHrs = 0;
                        let totalScheduled = 0;
                        let accidentHourFree = 0;

                        monthDays.forEach(day => {
                            const entry = getFlightDataEntry(day.date);
                            const dailyHrs = calculateDailyFlightHours(day.date, entry?.daily_flight_hours);
                            dailyFlightHours += dailyHrs;
                            accidentHourFree += dailyHrs;
                            
                            if (entry) {
                                eventsScheduled += entry.events_scheduled || 0;
                                weatherAborts += entry.weather_aborts_ops_cancels || 0;
                                eventsFlown += entry.events_flown || 0;
                                
                                if (day.isWeekend) {
                                    weekendFltHrs += dailyHrs;
                                } else {
                                    weekendFltHrs += entry.weekend_flt_hrs || 0;
                                }
                                
                                detachmentSorties += entry.detachment_sorties || 0;
                                detachmentHrs += entry.detachment_hrs || 0;
                                
                                if (entry.events_scheduled && entry.weather_aborts_ops_cancels) {
                                    totalScheduled += entry.events_scheduled - entry.weather_aborts_ops_cancels;
                                }
                            }
                        });

                        return {
                            dailyFlightHours: parseFloat(dailyFlightHours.toFixed(1)),
                            eventsScheduled,
                            weatherAborts,
                            totalScheduled,
                            eventsFlown,
                            weekendFltHrs: parseFloat(weekendFltHrs.toFixed(1)),
                            detachmentSorties,
                            detachmentHrs,
                            accidentHourFree: parseFloat(accidentHourFree.toFixed(1))
                        };
                    };

                    const monthlyTotals = calculateMonthlyTotals();
                    
                    // Calculate previous month totals
                    let prevMonth = currentMonth - 1;
                    let prevYear = currentYear;
                    if (prevMonth < 1) {
                        prevMonth = 12;
                        prevYear = currentYear - 1;
                    }
                    
                    const prevMonthEntries = flightDataEntries.filter(entry => {
                        const entryDate = new Date(entry.date);
                        return entryDate.getMonth() + 1 === prevMonth && entryDate.getFullYear() === prevYear;
                    });
                    
                    const prevMonthFlightHourData = filteredFlightHourData ? filteredFlightHourData.filter(item => {
                        if (item.create_dt) {
                            const itemDate = new Date(item.create_dt);
                            return itemDate.getMonth() + 1 === prevMonth && itemDate.getFullYear() === prevYear;
                        }
                        return false;
                    }) : [];
                    
                    const prevMonthTotals = calculateMonthlyTotalsForMonth(prevYear, prevMonth, prevMonthEntries, prevMonthFlightHourData);
                    
                    // Calculate previous quarter (3 months ago) totals
                    let prevQuarterMonth = currentMonth - 3;
                    let prevQuarterYear = currentYear;
                    if (prevQuarterMonth < 1) {
                        prevQuarterMonth += 12;
                        prevQuarterYear = currentYear - 1;
                    }
                    
                    const prevQuarterEntries = flightDataEntries.filter(entry => {
                        const entryDate = new Date(entry.date);
                        return entryDate.getMonth() + 1 === prevQuarterMonth && entryDate.getFullYear() === prevQuarterYear;
                    });
                    
                    const prevQuarterFlightHourData = filteredFlightHourData ? filteredFlightHourData.filter(item => {
                        if (item.create_dt) {
                            const itemDate = new Date(item.create_dt);
                            return itemDate.getMonth() + 1 === prevQuarterMonth && itemDate.getFullYear() === prevQuarterYear;
                        }
                        return false;
                    }) : [];
                    
                    const prevQuarterTotals = calculateMonthlyTotalsForMonth(prevQuarterYear, prevQuarterMonth, prevQuarterEntries, prevQuarterFlightHourData);
                    
                    // Calculate Fiscal Totals = Monthly Total + Previous Month Fiscal Total + Previous Quarter Fiscal Total
                    const fiscalTotals = {
                        dailyFlightHours: parseFloat((monthlyTotals.dailyFlightHours + prevMonthTotals.dailyFlightHours + prevQuarterTotals.dailyFlightHours).toFixed(1)),
                        totalScheduled: monthlyTotals.totalScheduled + prevMonthTotals.totalScheduled + prevQuarterTotals.totalScheduled,
                        eventsFlown: monthlyTotals.eventsFlown + prevMonthTotals.eventsFlown + prevQuarterTotals.eventsFlown,
                        accidentHourFree: parseFloat((monthlyTotals.accidentHourFree + prevMonthTotals.accidentHourFree + prevQuarterTotals.accidentHourFree).toFixed(1))
                    };

                    return (
                        <div style={{ marginTop: '30px', overflowX: 'auto' }}>
                            <table style={{...styles.table, fontSize: '10pt', borderCollapse: 'collapse'}}>
                                <thead>
                                    <tr style={styles.tableHeader}>
                                        <th style={{...styles.tableHeaderCell, minWidth: '120px'}}>Label</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Daily Flight Hours</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Events Scheduled</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>WNX/Ops Cnx</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Total Scheduled</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Events Flown</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Weekend Flt. Hrs.</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Detachment Sorties</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Detachment Hrs.</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>NOTES</th>
                                        <th style={{...styles.tableHeaderCell, minWidth: '80px'}}>Accident Hour Free</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Monthly Total Row */}
                                    <tr style={styles.tableRow}>
                                        <td style={{...styles.tableCell, fontWeight: 'bold'}}>Monthly Total</td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.dailyFlightHours > 0 ? monthlyTotals.dailyFlightHours.toFixed(1) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.eventsScheduled > 0 ? monthlyTotals.eventsScheduled : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.weatherAborts > 0 ? monthlyTotals.weatherAborts : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.totalScheduled > 0 ? monthlyTotals.totalScheduled : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.eventsFlown > 0 ? monthlyTotals.eventsFlown : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.weekendFltHrs > 0 ? monthlyTotals.weekendFltHrs.toFixed(1) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.detachmentSorties > 0 ? monthlyTotals.detachmentSorties : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.detachmentHrs > 0 ? monthlyTotals.detachmentHrs : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {monthlyTotals.accidentHourFree > 0 ? monthlyTotals.accidentHourFree.toFixed(1) : ''}
                                        </td>
                                    </tr>
                                    {/* Fiscal Totals Row */}
                                    <tr style={styles.tableRow}>
                                        <td style={{...styles.tableCell, fontWeight: 'bold'}}>Fiscal Totals</td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {fiscalTotals.dailyFlightHours > 0 ? fiscalTotals.dailyFlightHours.toFixed(1) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {fiscalTotals.totalScheduled > 0 ? fiscalTotals.totalScheduled : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {fiscalTotals.eventsFlown > 0 ? fiscalTotals.eventsFlown : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {fiscalTotals.accidentHourFree > 0 ? fiscalTotals.accidentHourFree.toFixed(1) : ''}
                                        </td>
                                    </tr>
                                    {/* Empty Row */}
                                    <tr style={styles.tableRow}>
                                        <td colSpan="11" style={styles.tableCell}></td>
                                    </tr>
                                    {/* Previous 1st Month Row */}
                                    <tr style={styles.tableRow}>
                                        <td style={{...styles.tableCell, fontWeight: 'bold'}}>Previous 1st Month</td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.dailyFlightHours > 0 ? prevMonthTotals.dailyFlightHours.toFixed(1) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.eventsScheduled > 0 ? prevMonthTotals.eventsScheduled : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.weatherAborts > 0 ? prevMonthTotals.weatherAborts : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.totalScheduled > 0 ? prevMonthTotals.totalScheduled : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.eventsFlown > 0 ? prevMonthTotals.eventsFlown : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.weekendFltHrs > 0 ? prevMonthTotals.weekendFltHrs.toFixed(1) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.detachmentSorties > 0 ? prevMonthTotals.detachmentSorties : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.detachmentHrs > 0 ? prevMonthTotals.detachmentHrs : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevMonthTotals.accidentHourFree > 0 ? prevMonthTotals.accidentHourFree.toFixed(1) : ''}
                                        </td>
                                    </tr>
                                    {/* Previous 2nd Month Row */}
                                    <tr style={styles.tableRow}>
                                        <td style={{...styles.tableCell, fontWeight: 'bold'}}>Previous 2nd Month</td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.dailyFlightHours > 0 ? prevQuarterTotals.dailyFlightHours.toFixed(1) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.eventsScheduled > 0 ? prevQuarterTotals.eventsScheduled : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.weatherAborts > 0 ? prevQuarterTotals.weatherAborts : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.totalScheduled > 0 ? prevQuarterTotals.totalScheduled : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.eventsFlown > 0 ? prevQuarterTotals.eventsFlown : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.weekendFltHrs > 0 ? prevQuarterTotals.weekendFltHrs.toFixed(1) : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.detachmentSorties > 0 ? prevQuarterTotals.detachmentSorties : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.detachmentHrs > 0 ? prevQuarterTotals.detachmentHrs : ''}
                                        </td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                        <td style={{...styles.tableCell, textAlign: 'right'}}>
                                            {prevQuarterTotals.accidentHourFree > 0 ? prevQuarterTotals.accidentHourFree.toFixed(1) : ''}
                                        </td>
                                    </tr>
                                    {/* Quarterly Row */}
                                    {(() => {
                                        const quarterlyTotals = {
                                            dailyFlightHours: parseFloat((monthlyTotals.dailyFlightHours + prevMonthTotals.dailyFlightHours + prevQuarterTotals.dailyFlightHours).toFixed(1)),
                                            totalScheduled: monthlyTotals.totalScheduled + prevMonthTotals.totalScheduled + prevQuarterTotals.totalScheduled,
                                            eventsFlown: monthlyTotals.eventsFlown + prevMonthTotals.eventsFlown + prevQuarterTotals.eventsFlown
                                        };
                                        
                                        return (
                                            <tr style={styles.tableRow}>
                                                <td style={{...styles.tableCell, fontWeight: 'bold'}}>Quarterly</td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}>
                                                    {quarterlyTotals.dailyFlightHours > 0 ? quarterlyTotals.dailyFlightHours.toFixed(1) : ''}
                                                </td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}>
                                                    {quarterlyTotals.totalScheduled > 0 ? quarterlyTotals.totalScheduled : ''}
                                                </td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}>
                                                    {quarterlyTotals.eventsFlown > 0 ? quarterlyTotals.eventsFlown : ''}
                                                </td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                                <td style={{...styles.tableCell, textAlign: 'right'}}></td>
                                            </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}

                {alert.show && (
                    <div style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        padding: '10px 20px',
                        backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: alert.type === 'success' ? '#155724' : '#721c24',
                        border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                        borderRadius: '4px',
                        zIndex: 1000
                    }}>
                        {alert.message}
                        <button
                            onClick={() => setAlert({ show: false, message: '', type: '' })}
                            style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            ×
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const SCRRFPA = ({ styles, currentDate, setCurrentDate, selectedSite, sites }) => {
        const [scrRfpaEntries, setScrRfpaEntries] = useState([]);
        const [editingEntry, setEditingEntry] = useState(null);
        const [alert, setAlert] = useState({ show: false, message: '', type: '' });
        // Use the parent's setCurrentDate function
        const [saving, setSaving] = useState(false);
        const currentScrRfpaId = useRef(null);
        const [draftEntry, setDraftEntry] = useState(null);
        const selectedSiteName = useMemo(() => {
            if (!selectedSite) return null;
            return sites.find(s => s.id === parseInt(selectedSite))?.name || null;
        }, [selectedSite, sites]);
        const defaultSiteId = sites[0]?.id;
        const defaultSiteName = sites[0]?.name;
        const resolvedSiteId = selectedSite ? parseInt(selectedSite) : defaultSiteId;
        const resolvedSiteName = selectedSiteName || defaultSiteName;

        // Store current date in a global variable for Excel export access
        useEffect(() => {
            window.scrRfpaCurrentDate = currentDate;
        }, [currentDate]);

        // Store the last saved date to preserve it for Excel export
        const lastSavedDateRef = useRef(null);

        // Load existing data when date changes
        useEffect(() => {
            if (scrRfpaEntries.length > 0) {
                const existing = scrRfpaEntries.find(e => e.date === currentDate && (!selectedSiteName || e.site_name === selectedSiteName));
                
                if (existing) {
                    currentScrRfpaId.current = existing.id;
                    setDraftEntry(existing);
                } else {
                    currentScrRfpaId.current = null;
                    setDraftEntry({
                        id: null,
                        date: currentDate,
                        site: resolvedSiteId,
                        site_name: resolvedSiteName
                    });
                }
            }
        }, [currentDate, scrRfpaEntries, selectedSiteName, resolvedSiteId, resolvedSiteName]);

        // Get SCR-RFPA entry for current date and selected site
        const getScrRfpaEntry = useCallback((date) => {
            return scrRfpaEntries.find(entry => 
                entry.date === date && 
                (!selectedSiteName || entry.site_name === selectedSiteName)
            );
        }, [scrRfpaEntries, selectedSiteName]);

        // Fetch SCR-RFPA entries
        const fetchScrRfpaEntries = useCallback(async (dateToUse = null) => {
            const dateForFetch = dateToUse || currentDate;
            try {
                // Fetch all entries without site filtering - we'll filter on the frontend
                const url = '/scr-rfpa-entries/';
                const response = await api.get(url);
                const allEntries = Array.isArray(response.data) ? response.data : (response.data?.results || []);
                
                // Filter by selected site on the frontend
                let filteredEntries = allEntries;
                if (selectedSiteName) {
                    filteredEntries = allEntries.filter(entry => entry.site_name === selectedSiteName);
                }
                
                setScrRfpaEntries(filteredEntries);
                // initialize/refresh draft for current date/site without losing unsaved local fields
                const existing = filteredEntries.find(e => e.date === dateForFetch && (!selectedSiteName || e.site_name === selectedSiteName));
                if (existing) {
                    currentScrRfpaId.current = existing.id;
                    setDraftEntry(prev => {
                        if (prev && prev.date === dateForFetch && ((selectedSiteName && prev.site_name === selectedSiteName) || (!selectedSiteName))) {
                            // Merge server values, but keep any locally typed values
                            return { ...existing, ...prev, id: existing.id };
                        }
                        return { ...existing };
                    });
                } else {
                    setDraftEntry({
                        id: null,
                        date: dateForFetch,
                        site: resolvedSiteId,
                        site_name: resolvedSiteName
                    });
                }
            } catch (error) {
                setAlert({ show: true, message: 'Failed to fetch SCR-RFPA entries', type: 'error' });
            }
        }, [currentDate, selectedSiteName, resolvedSiteId, resolvedSiteName]); // useCallback dependencies

        // Save SCR-RFPA entry
        const saveScrRfpaEntry = async (entryData) => {
            if (saving) {
                return;
            }
            
            try {
                setSaving(true);
                
                // Clean the data - remove null/undefined values and ensure proper types
                const cleanedData = {
                    date: entryData.date,
                    site: parseInt(entryData.site, 10)
                };

                // Add other fields with proper type conversion
                Object.entries(entryData).forEach(([key, value]) => {
                    if (key !== 'id' && 
                        key !== 'date' && 
                        key !== 'site' && 
                        key !== 'site_name' && 
                        value !== null && 
                        value !== undefined && 
                        value !== '') {
                        
                        // Convert numeric fields to numbers
                        if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                                cleanedData[key] = numValue;
                            } else {
                                cleanedData[key] = value;
                            }
                        } else {
                            cleanedData[key] = value;
                        }
                    }
                });


                // Check if an entry already exists for this date and site
                // Helper to fetch list (handles paginated and array responses)
                const fetchServerList = async () => {
                    const resp = await api.get('/scr-rfpa-entries/');
                    return Array.isArray(resp.data) ? resp.data : (resp.data?.results || []);
                };

                // Server-authoritative existence check (prevents stale client list)
                let serverExistingId = null;
                try {
                    const list = await fetchServerList();
                    const siteId = cleanedData.site;
                    const matched = list.find(e => e.date === cleanedData.date && e.site === siteId);
                    serverExistingId = matched?.id || null;
                } catch (e) {
                    // Proceed with local fallback
                }

                const existingEntry = getScrRfpaEntry(entryData.date);
                const targetId = serverExistingId || entryData.id || currentScrRfpaId.current || existingEntry?.id;
                if (targetId) {
                    // Update existing entry
                    await api.put(`/scr-rfpa-entries/${targetId}/`, cleanedData);
                } else {
                    // Create new entry
                    try {
                        const resp = await api.post('/scr-rfpa-entries/', cleanedData);
                        const createdId = resp?.data?.id;
                        if (createdId) {
                            currentScrRfpaId.current = createdId;
                            setDraftEntry(prev => prev ? { ...prev, id: createdId } : prev);
                        }
                    } catch (postErr) {
                        const serverData = postErr?.response?.data;
                        const isUniqueErr = Array.isArray(serverData?.non_field_errors) && serverData.non_field_errors.join(' ').includes('unique set');
                        if (isUniqueErr) {
                            // Find existing again and update instead
                            const list = await fetchServerList();
                            const siteId = cleanedData.site;
                            const matched = list.find(e => e.date === cleanedData.date && e.site === siteId);
                            if (matched?.id) {
                                await api.put(`/scr-rfpa-entries/${matched.id}/`, cleanedData);
                                currentScrRfpaId.current = matched.id;
                                setDraftEntry(prev => prev ? { ...prev, id: matched.id } : prev);
                            } else {
                                throw postErr;
                            }
                        } else {
                            throw postErr;
                        }
                    }
                }
                
                // Store the date that was used for saving
                lastSavedDateRef.current = cleanedData.date;
                window.scrRfpaLastSavedDate = cleanedData.date;
                
                await fetchScrRfpaEntries();
                setAlert({ show: true, message: 'SCR-RFPA data saved successfully', type: 'success' });
            } catch (error) {
                setAlert({ 
                    show: true, 
                    message: `Failed to save SCR-RFPA data: ${error.response?.data?.detail || error.message}`, 
                    type: 'error' 
                });
            } finally {
                setSaving(false);
            }
        };

        // Delete SCR-RFPA entry
        const deleteScrRfpaEntry = async (entryId) => {
            try {
                await api.delete(`/scr-rfpa-entries/${entryId}/`);
                await fetchScrRfpaEntries();
                setAlert({ show: true, message: 'SCR-RFPA data deleted successfully', type: 'success' });
            } catch (error) {
                setAlert({ show: true, message: 'Failed to delete SCR-RFPA data', type: 'error' });
            }
        };

        // Cleanup old SCR-RFPA entries
        const cleanupOldScrRfpaEntries = async () => {
            try {
                const monthsToKeep = 3; // Keep last 3 months of data
                await api.post('/scr-rfpa-entries/cleanup_old_data/', { months_to_keep: monthsToKeep });
                await fetchScrRfpaEntries();
                setAlert({ show: true, message: `Cleaned up SCR-RFPA entries older than ${monthsToKeep} months`, type: 'success' });
            } catch (error) {
                setAlert({ show: true, message: 'Failed to cleanup SCR-RFPA data', type: 'error' });
            }
        };

        // Fetch SCR-RFPA entries when component loads or site changes
        useEffect(() => {
            fetchScrRfpaEntries();
        }, [fetchScrRfpaEntries]);

        // Render this directly instead of defining a nested React component.
        // A nested component gets a new identity on every SCR-RFPA state update,
        // which unmounts and recreates the input after each keystroke.
        const renderEditableCell = ({ field, date, type = 'text', placeholder = '' }) => {
            const entry = getScrRfpaEntry(date);
            const isEditing = !!(editingEntry && editingEntry.date === date && editingEntry.field === field);
            const currentValue = isEditing
                ? (draftEntry ? draftEntry[field] : '')
                : (draftEntry?.[field] ?? (entry ? entry[field] : ''));

            const handleChange = (e) => {
                const rawValue = e.target.value;
                const parsedValue = type === 'number' ? parseFloat(rawValue) : null;
                const newValue = type === 'number'
                    ? (rawValue === '' || Number.isNaN(parsedValue) ? null : parsedValue)
                    : rawValue;
                setDraftEntry(prev => ({
                    ...(prev || {}),
                    id: prev?.id ?? currentScrRfpaId.current ?? getScrRfpaEntry(date)?.id ?? null,
                    date,
                    site: resolvedSiteId,
                    site_name: resolvedSiteName,
                    [field]: newValue
                }));
            };

            const handleClick = () => {
                if (!isEditing && selectedSite) {
                    const existingEntry = getScrRfpaEntry(date);
                    setEditingEntry({ date, field });
                    // initialize or sync draft with existing entry
                    setDraftEntry(prev => ({
                        id: (prev?.id ?? existingEntry?.id ?? currentScrRfpaId.current ?? null),
                        date,
                        site: resolvedSiteId,
                        site_name: resolvedSiteName,
                        ...existingEntry,
                        ...(prev || {}),
                    }));
                }
            };

            const handleBlur = () => {
                // End edit mode only; saving happens via explicit Save button
                if (editingEntry) {
                    setEditingEntry(null);
                }
            };

            const handleKeyDown = (e) => {
                if (e.key === 'Tab') {
                    e.preventDefault();
                    // End current edit mode
                    if (editingEntry) {
                        setEditingEntry(null);
                    }
                    // Find the next input field and focus it
                    const allInputs = document.querySelectorAll('input[type="number"], input[type="text"]');
                    const currentIndex = Array.from(allInputs).indexOf(e.target);
                    const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;
                    
                    if (nextIndex >= 0 && nextIndex < allInputs.length) {
                        allInputs[nextIndex].focus();
                    }
                }
            };

            if (isEditing) {
                return (
                    <input
                        type={type}
                        value={currentValue ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        autoFocus
                        style={{
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px',
                            border: '1px solid #007bff',
                            padding: '4px 6px',
                            fontSize: '10pt',
                            backgroundColor: '#f8f9fa',
                            boxSizing: 'border-box'
                        }}
                    />
                );
            }

            return (
                <div
                    onClick={selectedSite ? handleClick : undefined}
                    onKeyDown={selectedSite ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleClick();
                        }
                    } : undefined}
                    role="button"
                    tabIndex={selectedSite ? 0 : -1}
                    aria-label={`Edit ${field}`}
                    aria-disabled={!selectedSite}
                    style={{
                        cursor: selectedSite ? 'pointer' : 'not-allowed',
                        width: '120px',
                        minWidth: '120px',
                        maxWidth: '120px',
                        minHeight: '20px',
                        padding: '4px 6px',
                        border: '1px solid transparent',
                        borderRadius: '2px',
                        opacity: selectedSite ? 1 : 0.6,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {currentValue || ''}
                </div>
            );
        };

        const entry = useMemo(() => getScrRfpaEntry(currentDate), [currentDate, getScrRfpaEntry]);
        

        return (
            <div style={{ ...styles.card, margin: '20px', padding: '20px', fontSize: '10pt', color: styles.text }}>
                <h3 style={{ 
                    textAlign: 'center', 
                    marginBottom: '20px',
                    fontSize: '14pt',
                    fontWeight: 'bold',
                    backgroundColor: '#FFFF99',
                    padding: '5px',
                    border: `1px solid ${styles.border}`
                }}>
                    PMF TOTALS
                    {selectedSite && (
                        <div style={{ fontSize: '12pt', marginTop: '5px', color: '#333' }}>
                            Site: {sites.find(s => s.id === parseInt(selectedSite))?.name || 'Unknown'}
                        </div>
                    )}
                </h3>
                
                {/* Site Selection Notice */}
                {!selectedSite && (
                    <div style={{ 
                        marginBottom: '15px', 
                        padding: '10px', 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7',
                        borderRadius: '4px',
                        color: '#856404'
                    }}>
                        <strong>Please select a site from the dropdown above to enter SCR-RFPA data.</strong>
                    </div>
                )}

                {/* Date Selection */}
                <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <label style={{ fontWeight: 'bold' }}>Reported Date:</label>
                    <input
                        type="date"
                        value={currentDate}
                        onChange={(e) => setCurrentDate(e.target.value)}
                        style={{
                            padding: '5px',
                            border: `1px solid ${styles.border}`,
                            borderRadius: '4px',
                            fontSize: '10pt'
                        }}
                    />
                </div>

                {/* Save Controls */}
                <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => draftEntry && saveScrRfpaEntry(draftEntry)}
                        disabled={!selectedSite || !draftEntry || saving}
                        style={{ ...styles.button, backgroundColor: '#28a745', color: 'white', opacity: (!selectedSite || saving) ? 0.6 : 1 }}
                    >
                        {saving ? 'Saving…' : 'Save SCR-RFPA'}
                    </button>
                    <button
                        onClick={() => setDraftEntry(prev => prev ? { ...prev, id: null } : prev)}
                        disabled={!draftEntry}
                        style={{ ...styles.button, backgroundColor: '#6c757d', color: 'white', opacity: !draftEntry ? 0.6 : 1 }}
                    >
                        Start New For Date
                    </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{...styles.table, fontSize: '10pt', tableLayout: 'fixed', width: '100%'}}>
                        <tbody>
                            {/* Only the specified data entry fields */}
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Line 3: # MCRS A/C on DSR</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'mcrs_ac_on_dsr', date: currentDate, type: 'number', placeholder: '67' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Line 9A: XC A/Cs/Band</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'xc_acs_band', date: currentDate, type: 'number', placeholder: '2' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Line 9B: DET A/Cs/Band</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'det_acs_band', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Scheduled FLT HRS</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'scheduled_flt_hrs', date: currentDate, type: 'number', placeholder: '265.8' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>RFP MOB A/C per Det Count (Cannot exceed TAR)</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'rfp_mob_ac_per_det_count', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>RFP DET A/C per Det Count (Can exceed TAR)</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'rfp_det_ac_per_det_count', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Sched Sorties</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'sched_sorties', date: currentDate, type: 'number', placeholder: '145' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Completed Sorties (incl. Add-Ons)</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'completed_sorties_incl_add_ons', date: currentDate, type: 'number', placeholder: '108' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Added Sorties</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'added_sorties', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>ACNA's</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'acnas', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>A Aborts</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'a_aborts', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>C Aborts</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'c_aborts', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>Weather Cnx's</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'weather_cnxs', date: currentDate, type: 'number', placeholder: '28' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>OPS Cnx's</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'ops_cnxs', date: currentDate, type: 'number', placeholder: '17' })}
                                </td>
                            </tr>
                            <tr>
                                <td style={{...styles.tableCell, fontWeight: 'bold', width: '60%'}}>FCF (Functional Check Flight)</td>
                                <td style={{...styles.tableCell, width: '40%'}}>
                                    {renderEditableCell({ field: 'fcf_functional_check_flight', date: currentDate, type: 'number', placeholder: '0' })}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Action Buttons */}
                <div style={{ marginTop: '15px', textAlign: 'center', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    {entry && (
                        <button
                            onClick={() => deleteScrRfpaEntry(entry.id)}
                            style={{
                                ...styles.button,
                                backgroundColor: '#dc3545',
                                color: 'white',
                                padding: '8px 16px'
                            }}
                        >
                            Delete Entry
                        </button>
                    )}
                    <button
                        onClick={cleanupOldScrRfpaEntries}
                        style={{
                            ...styles.button,
                            backgroundColor: '#6c757d',
                            color: 'white',
                            padding: '8px 16px'
                        }}
                    >
                        Cleanup Old Data
                    </button>
                </div>

                {alert.show && (
                    <div style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        padding: '10px 20px',
                        backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: alert.type === 'success' ? '#155724' : '#721c24',
                        border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                        borderRadius: '4px',
                        zIndex: 1000
                    }}>
                        {alert.message}
                        <button
                            onClick={() => setAlert({ show: false, message: '', type: '' })}
                            style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            ×
                        </button>
                    </div>
                )}
            </div>
        );
    };


    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
                
                <div style={styles.pageHeader}>
                    {/* Assuming HamburgerMenu is defined elsewhere */}
                    <HamburgerMenu theme={theme}/> 
                    <h1 style={styles.pageTitle}>Daily Aircraft Status Report</h1>
                    <div style={{ width: '45px' }}></div>
                </div>

                {/* Export Button */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                <button onClick={() => exportToExcel(fltDataMonth, fltDataYear)} style={styles.exportButton}>
                    📊 Export to Excel
                </button>
                    <small style={{ color: styles.text, fontSize: '10px', opacity: 0.7 }}>
                        FLT Data: {new Date(fltDataYear, fltDataMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </small>
                </div>


                {/* Filter Controls (Kept for UI structure) */}
                <div style={styles.controlsContainer}>
                    <select
                        value={selectedSite}
                        onChange={(e) => setSelectedSite(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All Sites</option>
                        {Array.isArray(filteredSites) && filteredSites.length > 0 ? (
                            filteredSites.map(site => (
                                <option key={site.id} value={site.id}>{site.name}</option>
                            ))
                        ) : (
                            <option value="" disabled>No sites available</option>
                        )}
                    </select>
                    <select
                        value={selectedTms}
                        onChange={(e) => setSelectedTms(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="">All TMS</option>
                        {Array.isArray(filteredTms) && filteredTms.length > 0 ? (
                            filteredTms.map((tms) => (
                                <option key={tms.id || tms.name} value={tms.name}>{tms.name}</option>
                            ))
                        ) : (
                            <option value="" disabled>No TMS available</option>
                        )}
                    </select>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                </div>

                {/* Tab Navigation */}
                <div style={styles.tabContainer}>
                    {['ac-status', 'flt-data', 'scr-rfpa'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === tab ? styles.activeTab : {})
                            }}
                        >
                            {tab === 'ac-status' ? 'AC Status' :
                             tab === 'flt-data' ? 'FLT Data' :
                             tab === 'scr-rfpa' ? 'SCR-RFPA ' : tab}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div style={styles.tabContent}>
                    {activeTab === 'ac-status' && (
                        <ACStatus styles={styles} />
                    )}
                    {activeTab === 'flt-data' && (
                        <FLTData 
                            styles={styles} 
                            onMonthYearChange={handleFltDataMonthYearChange}
                            currentMonth={fltDataMonth}
                            currentYear={fltDataYear}
                            setCurrentMonth={setFltDataMonth}
                            setCurrentYear={setFltDataYear}
                            selectedSite={selectedSite}
                            sites={sites}
                        />
                    )}
        {activeTab === 'scr-rfpa' && (
            <SCRRFPA 
                styles={styles} 
                currentDate={scrRfpaDate} 
                setCurrentDate={setScrRfpaDate}
                selectedSite={selectedSite}
                sites={sites}
            />
        )}
                    {!['ac-status', 'flt-data', 'scr-rfpa'].includes(activeTab) && (
                        <ACStatus styles={styles} />
                    )}
                </div>
            </div>
        </div>
    );
}

export default DailyAircraftStatusReport;
